import {
  Bath,
  Bed,
  Car,
  FileText,
  Image as ImageIcon,
  List as ListIcon,
  Square,
  User,
  X,
  Upload,
  Calendar,
  Building2,
  Building,
  Home,
  Map,
  Trees,
  Layers,
  MapPin,
  DollarSign,
  Video,
  Link,
  Percent,
  ChevronDown,
  ChevronUp,
  Plus,
  ClipboardList,
} from "lucide-react";

import acreageIcon from "../../../assets/property-listing/acreage.png";
import apartmentIcon from "../../../assets/property-listing/apartment.png";
import blockUnitsIcon from "../../../assets/property-listing/block-units.png";
import houseIcon from "../../../assets/property-listing/house.png";
import landIcon from "../../../assets/property-listing/land.png";
import retirementIcon from "../../../assets/property-listing/retirement.png";
import ruralIcon from "../../../assets/property-listing/rural.png";
import townhouseIcon from "../../../assets/property-listing/townhouse.png";
import villaIcon from "../../../assets/property-listing/villa.png";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import toast from "react-hot-toast";
import {
  SUPPORTED_UPLOAD_ACCEPT,
  splitSupportedUploads,
  rejectedUploadsMessage,
} from "../../../utils/imageUploadGuard";
import { getAgents, registerProperty, uploadPropertyDocuments, getProperty, updateProperty, generateAIDescription, checkDuplicateProperty, mergeIntoProperty } from "../../../api/agent";
import "react-phone-number-input/style.css";
import { toE164Value, isCompletePhoneNumber } from "../../../utils/phoneNumber";
import PhoneField from "../../../components/common/PhoneField";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui";
import { appendPriceReason, isPriceReasonRequired, usePriceChangeReason } from "../../compliance/components/PriceChangeReasonModal";
import { invalidateCache } from "../../../lib/cache";
import { getContacts } from "../../../api/contact";
import { sendPropertyClaimInvite } from "../../../api/propertyManagement";
import { convertProspect } from "../../../api/database";
import { setSellerLeadAction } from "../../../api/lead";
import InfiniteScrollSelect from "../../../components/common/InfiniteScrollSelect";
import PropertyTypeModal from "../../../components/properties/PropertyTypeModal";
import AddressAutocomplete from "../../../components/common/AddressAutocomplete";
import ConfirmModal from "../../../components/common/ConfirmModal";
import AppraisalReminderSeries from "../../leads/components/AppraisalReminderSeries";
import { DuplicateAddressNotice, DuplicateAddressDialog, SameBuildingNotice, SameBuildingDialog } from "../components/DuplicateAddressWarning";
import {
  formatMelbourneDatePart,
  formatMelbourneTimePart,
  datetimeLocalToUtcISOString,
  formatMelbourneDateTime,
} from "../../../utils/melbourneTime";
import { hasAdminAccess } from "../../../utils/roleAccess";
import { calculateBond } from "../utils/rentBond";

// Visibility options for On-Market only (Agent Point, Development Ready, Inspect Real Estate, RateMyAgent, RealTimeAgent removed; Collings.com.au added)
const VISIBILITY_OPTIONS = [
  { key: "internal_marketing", label: "Internal Marketing", implication: "Property will be included in internal marketing database and campaigns." },
  { key: "published_on_website", label: "Collings Portal", implication: "Property will be visible to all registered buyers in the Off-Market Portal." },
  { key: "published_on_rea", label: "realestate.com.au", implication: "Property will be listed publicly on realestate.com.au." },
  { key: "published_on_realcommercial", label: "realcommercial.com.au", implication: "Commercial listing will be uploaded to realcommercial.com.au." },
  { key: "published_on_domain", label: "domain.com.au", implication: "Property will be listed publicly on domain.com.au." },
  { key: "published_on_collings", label: "Collings.com.au", implication: "Property will be listed on Collings.com.au." },
];

// Valid Buyer Portal tab values for portal_category (must match the <select> options below
// and Property.PORTAL_CATEGORY_CHOICES on the backend). Legacy rows created before the
// 2026-05-27 schema change still hold market-status values like "off_market"/"on_market";
// those are no longer valid choices, so coerce any non-tab value to "" on load to avoid
// "portal_category: 'off_market' is not a valid choice" on save.
const PORTAL_CATEGORY_VALUES = [
  "apartment_unit_villa",
  "house",
  "townhouse",
  "blocks_of_units",
  "land",
  "house_and_land",
  "off_the_plan",
  "off_the_plan_townhouse",
  "commercial",
];

const initialState = {
  title: "",
  address: "",
  street: "",
  suburb: "",
  city: "",
  state: "",
  postal_code: "",
  // 'full' = Show Full Address (default — keeps prior behavior for existing properties)
  address_publish_mode: "full",
  price: "",
  weekly_rent: "",
  bond: "",           // Rental bond amount (lease properties) — mandatory for lease
  available_date: "", // Date the property is available from (lease properties)
  rental_application_url: "", // Ignite tenancy application link (lease properties)
  furnished: false, // realestate.com.au "Furnished" flag (residential lease properties)
  type: "Apartment",
  beds: "",
  baths: "",
  status: "", // Set dynamically based on listing type
  images: [],
  description: "",
  features: "",
  featuresList: [],
  internalFeatures: "",
  internalFeaturesList: [],
  externalFeatures: "",
  externalFeaturesList: [],
  internalFeatureInput: "",
  externalFeatureInput: "",
  agent: "",
  owner_preferred_name: "", // Name the owner goes by, when it differs from their legal name
  contact: "",
  email: "",
  contact_secondary: "", // optional second mobile for the same owner (CRM-2026-00044)
  email_secondary: "",   // optional second email for the same owner
  propertyTypes: "",
  publicShare: false,
  agentId: "",
  secondaryAgent: "",
  makeSecondaryAgentOnListing: false, // When true, secondary agent is sent as co_agent (shown on marketing)
  tertiaryAgent: "", // Third agent: access only, not shown on marketing
  // New fields
  transaction_type: "sale",
  land_size: "",
  building_size: "",
  year_built: "",
  price_display: "exact",
  price_to: "",
  price_display_text: "",
  auction_date: "",
  auction_venue: "",
  commission_type: "percentage",
  commission_rate: "",
  commission_amount: "",
  // Letting Fee (lease listings) — mirrors commission structure
  letting_fee_type: "percentage",
  letting_fee_rate: "",
  letting_fee_amount: "",
  virtual_tour_link: "",
  video_url: "",
  published_on_rea: false,
  published_on_realcommercial: false,
  published_on_domain: false,
  published_on_website: false,  // Default false — Off-Market must explicitly tick Buyer Portal; On-Market never goes to Buyer Portal
  portal_category: "",  // Which tab in Buyer Portal (apartment_unit_villa, house, townhouse, blocks_of_units, land, house_and_land, off_the_plan, commercial)
  internal_marketing: false,
  published_on_agentpoint: false,
  published_on_development_ready: false,
  published_on_inspect_real_estate: false,
  published_on_ratemyagent: false,
  published_on_realtimeagent: false,
  published_on_collings: false,
  listing_launch_at: "",  // Shared scheduled launch date/time (Buyer Portal filter; future: REA/Domain push)
  appraisal_market_type: "",
  appraisal_temperature: "warm",
  // How a from-scratch appraisal was generated (CRM-2026 Phase 2). Sets the
  // property's Origin behind the scenes; only asked when NOT created from a lead.
  origin_reason: "",
  appraisal_reminder_date: "",
  appraisal_reminder_reason: "",
  // Call-back series stacked on the appraisal: [{ key, label, date: Date, reason }].
  appraisal_reminders: [],
  appraisal_note: "",
  // Yield & Financial Details
  est_gross_yield: "",
  est_outgoings: "",
  est_net_yield: "",
  // Annual Outgoings Breakdown
  body_corporate: "",
  council_rates: "",
  water_rates: "",
  opex: "", // Commercial only: legacy single-value OpEx (replaced by breakdown fields below)
  insurance: "",         // Commercial OpEx: annual insurance
  land_tax: "",          // Commercial OpEx: annual land tax
  other_opex: "",        // Commercial OpEx: other annual operating expenses
  tenant_pays_opex: true, // Commercial: tenant pays OpEx (typical net lease)
  current_annual_property_management_fee: "",
  include_management_fee: false, // Agent opt-in for the standard 6.6% management fee (off by default)
  has_body_corporate: true,
  // Additional fields
  method_of_sale: "Private Sale",
  floor_area: "",
  commercial_category: "", // REA commercialCategory for realcommercial.com.au
  commercial_category_2: "",
  commercial_category_3: "",
  land_area: "",
  parking_type: "",
  parking_spaces: "",
  parking_list: [{ type: "", spaces: "" }],
  additionalOwners: [],
  property_class: "",
  commission_gst: "inclusive",
  letting_fee_gst: "inclusive",
  featureInput: "", // For the input field - DEPRECATED
  // Agent-curated Q&A pairs surfaced to the Buyer Portal AI assistant as per-property context.
  // Seeded with common buyer questions; agents fill answers and can add custom Q&A.
  ai_questionnaire: [],
};

// Default questions seeded into the AI Assistant Questionnaire for new listings.
// Agents fill the `answer` and can add their own Q&A pairs.
const DEFAULT_AI_QUESTIONS = [
  "What schools are in the catchment area?",
  "What's the neighbourhood like (vibe, demographics, noise)?",
  "What public transport and amenities are nearby?",
  "Are pets allowed? Any restrictions?",
  "What renovations or upgrades have been done recently?",
  "Why is the owner selling / leasing?",
  "What are the standard inspection times?",
  "Are there any known issues, easements, or restrictions?",
  "What's included in the sale (appliances, fixtures, etc.)?",
  "What are the body corporate / strata fees and what do they cover?",
];

const buildDefaultQuestionnaire = () =>
  DEFAULT_AI_QUESTIONS.map((q) => ({ question: q, answer: "" }));

// Property Types with Icons
// Property Types with Icons - Matched to design image
const propertyTypeOptions = [
  { id: "Apartment", label: "Apartment", icon: Building2 },
  { id: "Unit", label: "Unit", icon: Building },
  { id: "House", label: "House", icon: Home },
  { id: "Townhouse", label: "Townhouse", icon: Building2 },
  { id: "Villa", label: "Villa", icon: Home },
  { id: "House and Land", label: "House and Land", icon: Map },
  { id: "Land", label: "Land", icon: Trees },
  { id: "Block of Units", label: "Block of Units", icon: Layers },
  { id: "Commercial", label: "Commercial", icon: Building },
  { id: "Off the Plan Apartments", label: "Off the Plan Apartments", icon: Building2 },
];

const transactionTypes = [
  { id: "sale", label: "For Sale" },
  { id: "lease", label: "For Lease" },
];

// REA's fixed commercialCategory enum (partner.realestate.com.au). For a
// commercial listing these are stored in property_class so realcommercial.com.au
// gets the listing categorised correctly (the backend maps property_class to
// this enum, defaulting to "Other").
const commercialCategoryOptions = [
  "Retail", "Offices", "Industrial/Warehouse", "Medical/Consulting",
  "Land/Development", "Hotel/Leisure", "Showrooms/Bulky Goods",
  "Commercial Farming", "Other",
];


// Method of Sale options (replacing status)
const methodOfSaleOptions = [
  { label: "Private Sale", value: "Private Sale" },
  { label: "Auction", value: "Auction" },
  { label: "Commercial", value: "Commercial" },
  { label: "Off the Plan Apartments", value: "Off the Plan Apartments" },
];

// Keep status options for backwards compatibility
// Status options - Matched to design image
const statusOptions = [
  "All",
  "Prospect / Not Currently Listed",
  "Appraisal",
  "On Market Listing",
  "Off Market Listing"
];

// "How was this appraisal generated?" — asked when an agent adds an appraisal
// from scratch (not from a Collings lead), CRM-2026 Phase 2. The value maps to
// the property's immutable Origin on the backend (referral -> Referral,
// database/past_client -> Nurture, social_media_ads -> Collings Generated,
// everything else -> Agent Generated).
//
// Christian Gravias, Monday 13225687633 (7 Oct 2026): paid social ("Social Media
// ads") is a Collings-generated lead; split out organic social and letterbox
// drops as agent-generated sources, and drop "Prospecting" (too broad).
const APPRAISAL_ORIGIN_REASONS = [
  { value: "database", label: "Database" },
  { value: "referral", label: "Referral" },
  { value: "past_client", label: "Past client" },
  { value: "door_knock", label: "Door knock" },
  { value: "cold_call", label: "Cold call" },
  { value: "letter_box_drops", label: "Letter box drops" },
  { value: "social_media_ads", label: "Social Media ads" },
  { value: "social_media_organic", label: "Social Media organic" },
  { value: "open_home", label: "Open home" },
  { value: "other", label: "Other" },
];
// const propertyTypeOptions = ["Apartment", "House", "Townhouse", "Unit", "Villa", "Land", "Other"];

// Reverse mapping: Convert backend property_type back to frontend propertyTypes
const mapBackendPropertyTypeToFrontend = (backendType) => {
  if (!backendType) return "Apartment"; // Default

  const backendTypeLower = String(backendType).toLowerCase();

  // Map backend types to frontend display names
  const reverseMapping = {
    'apartment': 'Apartment',
    'house': 'House',
    'townhouse': 'Townhouse',
    'land': 'Land',
    'residential': 'House', // Default for residential
    'commercial': 'House',
    'business': 'House',
    'rural': 'Land',
    'holidayrental': 'House'
  };

  return reverseMapping[backendTypeLower] || 'Apartment';
};

// Prefill key -> form field. Used when a NEW listing arrives pre-populated, e.g.
// the Database's "Convert to appraisal" handing over the owner's property.
const PREFILL_FIELD_MAP = {
  address: "address",
  street: "street",
  suburb: "suburb",
  state: "state",
  postal_code: "postal_code",
  beds: "beds",
  baths: "baths",
  parking_spaces: "parking_spaces",
  land_size: "land_size",
  owner_name: "agent",     // "agent" is the owner-name field on this form
  owner_preferred_name: "owner_preferred_name",
  owner_contact: "contact",
  owner_email: "email",
  appraisal_temperature: "appraisal_temperature",
  // Seller Leads "Add as Appraisal" carries the lead's Off/On Market bucket
  // through so the new appraisal lands in the matching pipeline instead of
  // stopping on the required-field error (Monday 12817261831, 2026-08-17).
  appraisal_market_type: "appraisal_market_type",
};

export function AddPropertyForm({
  isModal = false,
  onClose,
  onSubmit,
  initialValues,
  isEdit = false,
  isUpdating = false,
  isUploading = false,
  isSuccess,
  isAdmin = false,
  isCrossIconPress,
  lead_id = null,
  externalPropertyId = null, // External property ID (e.g., from Collings upload)
  listingType = null, // 'appraisal' | 'offmarket' | 'onmarket' - from initial pop-up or derived from status when editing
  prefill = null, // Seed values for a NEW listing (e.g. Database "Convert to appraisal")
  sellerLeadId = null, // Seller Leads "Add as Appraisal": linked to the profile a merge lands on
  hideFurnished = false, // Collings/Public pages save to a model with no furnished field
}) {
  const [form, setForm] = useState(initialState);
  const [imagePreviews, setImagePreviews] = useState([]);
  // Map an already-saved image's URL -> its original filename, so edit mode can
  // show the agent's chosen name (e.g. "01 Front.jpg") on each tile. New uploads
  // read File.name directly; this covers images already on the property. Keyed by
  // URL (stable across reorder), so there's no 4th parallel array to keep aligned.
  const existingImageNames = useMemo(() => {
    const map = {};
    (initialValues?.images || []).forEach((img) => {
      const url = img?.file || img;
      if (typeof url === "string" && img?.original_name) {
        map[url.split("?")[0]] = img.original_name;
      }
    });
    return map;
  }, [initialValues]);
  const [lightboxImage, setLightboxImage] = useState(null);
  // const [videoPreviews, setVideoPreviews] = useState([]); // <-- REMOVE video previews state
  const [errors, setErrors] = useState({});
  const [agents, setAgents] = useState([]);
  // Logged-in user (the person creating/editing). Used to decide whether the AI
  // Assistant Questionnaire is shown here or emailed to the listing's primary agent.
  const { user: currentUser } = useSelector((state) => state.auth);

  // Fetch function for InfiniteScrollSelect. role: "agent" excludes admin/
  // super_admin/dev accounts from the Agent Assignment pickers (Monday
  // 13077094187) — only real sales agents can be assigned to a listing.
  const fetchAgentOptions = useCallback(async (page, pageSize, search = "") => {
    const response = await getAgents({ page, page_size: pageSize, search, role: "agent" });
    const data = response?.data ?? {};
    return {
      results: data.results ?? (Array.isArray(data) ? data : []),
      next: Boolean(data.next),
    };
  }, []);
  const [deleteImages, setDeleteImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Opt-in: after the listing/appraisal is saved, email the owner(s) a tokenised
  // link to claim this property into their My Property Portfolio (fired in
  // handleSubmit once the property id exists). Off by default — it sends real email.
  const [sendClaimInvite, setSendClaimInvite] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [ownerSuggestions, setOwnerSuggestions] = useState([]);
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [ownerSearchLoading, setOwnerSearchLoading] = useState(false);
  const ownerSearchTimer = React.useRef(null);
  const ownerDropdownRef = React.useRef(null);
  const [step, setStep] = useState(1); // 1: Add Property, 2: Statement of Info, 3: Contract
  const [createdPropertyId, setCreatedPropertyId] = useState(null);
  const [showVisibilityConfirm, setShowVisibilityConfirm] = useState(false);
  const [pendingVisibilityChanges, setPendingVisibilityChanges] = useState(null);
  // Guards the one-shot `prefill` seeding below (Database -> Add Appraisal).
  const prefillAppliedRef = React.useRef(false);

  // "This address is already in the CRM" (Michael Piccoli, CRM-2026-00081).
  // 2/29 Elizabeth Street was added three times as separate profiles and
  // nothing warned. While the agent types a NEW address we look for a live
  // profile already there and offer to open it; Save asks once more.
  // One profile per property (Christian Gravias, 1 Oct 2026: "The info should
  // just merge"): when that profile is the same kind (sale or lease), Save
  // becomes "Add to this profile" and merges into it (mergeTargetRef). Only a
  // lease beside a sale can still "Save anyway", which also tells the
  // backend's publish guard not to ask the same question a second time.
  const navigate = useNavigate();
  const [addressTwins, setAddressTwins] = useState([]);
  const [showTwinConfirm, setShowTwinConfirm] = useState(false);
  const twinLookupSeq = React.useRef(0);
  const twinsAcceptedForRef = React.useRef(null);
  const mergeTargetRef = React.useRef(null);
  // Same building, one side with no unit number ("11-17 Park St" vs
  // "14/11-17 Park Street", CRM-2026-00119): warned about, never merged.
  const [buildingTwins, setBuildingTwins] = useState([]);
  const [showBuildingConfirm, setShowBuildingConfirm] = useState(false);
  const buildingTwinsRef = React.useRef([]);
  const buildingAcceptedForRef = React.useRef(null);
  const twinKey = (address, suburb) =>
    `${(address || "").trim().toLowerCase()}|${(suburb || "").trim().toLowerCase()}`;

  const lookupAddressTwins = useCallback(async (address, suburb) => {
    const seq = ++twinLookupSeq.current;
    const a = (address || "").trim();
    const s = (suburb || "").trim();
    if (isEdit || a.length < 5 || !s) {
      setAddressTwins([]);
      setBuildingTwins([]);
      buildingTwinsRef.current = [];
      return [];
    }
    try {
      const res = await checkDuplicateProperty({ address: a, suburb: s });
      const matches = Array.isArray(res?.data?.matches) ? res.data.matches : [];
      const sameBuilding = Array.isArray(res?.data?.same_building) ? res.data.same_building : [];
      // A slower answer for an address the agent has since changed must not
      // overwrite the answer for the one on screen.
      if (seq === twinLookupSeq.current) {
        setAddressTwins(matches);
        setBuildingTwins(sameBuilding);
        buildingTwinsRef.current = sameBuilding;
      }
      return matches;
    } catch (err) {
      // A failed check must never stop a save.
      buildingTwinsRef.current = [];
      return [];
    }
  }, [isEdit]);

  useEffect(() => {
    if (isEdit) return undefined;
    const timer = setTimeout(async () => {
      const matches = await lookupAddressTwins(form.address, form.suburb);
      // The address is already in the CRM, so borrow the postcode (and state)
      // the existing profile already holds instead of making the agent re-key it
      // (CRM-2026-00094). Only fills blanks so nothing the agent typed is lost.
      const twin = matches[0];
      if (!twin) return;
      setForm((prev) => {
        const next = { ...prev };
        if (!prev.postal_code?.trim() && twin.postal_code) next.postal_code = twin.postal_code;
        if (!prev.state?.trim() && twin.state) next.state = twin.state;
        return next;
      });
      if (twin.postal_code) setErrors((prev) => ({ ...prev, postal_code: undefined }));
      if (twin.state) setErrors((prev) => ({ ...prev, state: undefined }));
    }, 600);
    return () => clearTimeout(timer);
  }, [isEdit, form.address, form.suburb, lookupAddressTwins]);

  const openAddressTwin = (match) => {
    setShowTwinConfirm(false);
    // A host page's own onClose navigates elsewhere; only a modal needs shutting.
    if (isModal && onClose) onClose();
    navigate(`/properties/${match.id}`);
  };

  // Handle external property ID (from Collings property upload success)
  useEffect(() => {
    if (externalPropertyId && !isEdit) {
      // Appraisals never collect documents — finish immediately, skip the documents step.
      const isAppraisalListing =
        listingType === "appraisal" ||
        (initialValues?.status?.name || initialValues?.status) === "Appraisal";
      if (isAppraisalListing) {
        toast.success("Appraisal added successfully!");
        if (isSuccess) isSuccess(externalPropertyId);
        if (onClose) onClose();
        return;
      }
      // Set property ID and move to step 2 (documents)
      setCreatedPropertyId(externalPropertyId);
      setStep((prev) => (prev === 1 ? 2 : prev));
    }
  }, [externalPropertyId, isEdit, listingType, initialValues]);
  const [statementFiles, setStatementFiles] = useState([]);
  const [contractFiles, setContractFiles] = useState([]);
  const [legalDocsFiles, setLegalDocsFiles] = useState([]);
  const [documentNames, setDocumentNames] = useState({}); // Store custom names for documents
  const [isUploadingDocs, setIsUploadingDocs] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [showAnnualOutgoings, setShowAnnualOutgoings] = useState(false);
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  // Parallel array to imagePreviews/form.images: marks which images are floorplans
  const [floorplanFlags, setFloorplanFlags] = useState([]);

  // Listing type from initial pop-up or derived from status when editing
  const effectiveListingType = listingType ?? (() => {
    const s = form.status || initialValues?.status?.name || initialValues?.status || "";
    if (s === "Appraisal") return "appraisal";
    if (s === "Off Market Listing") return "offmarket";
    return "onmarket";
  })();

  /*
   * THE SHORT APPRAISAL FORM.
   *
   * Simon Abbott, 3 Sep 2026: "When adding an appraisal, please just have the
   * below information required to submit an appraisal into the system,
   * everything else can be hidden behind an 'Add Extra Information' button at
   * the bottom" - address, expected commission rate (optional), property class,
   * price (with a "No Price Yet" button), beds/baths/car parks (not required),
   * primary and secondary agent, owner details, and the pipeline/follow-up.
   *
   * `showExtra` is true for every other listing type and for edits, so this only
   * ever shortens the NEW-appraisal form. Nothing is removed - the button below
   * the form reveals the rest, and the hidden fields keep whatever they were
   * seeded with (e.g. from a Database or Seller Lead handoff).
   */
  const isShortAppraisalForm = !isEdit && effectiveListingType === "appraisal";
  const [showExtraInfo, setShowExtraInfo] = useState(false);
  const showExtra = !isShortAppraisalForm || showExtraInfo;

  // "No Price Yet" - an appraisal often has no number attached yet, so this
  // clears the price fields and folds the price inputs away rather than leaving
  // an empty box the agent wonders whether they must fill.
  const [noPriceYet, setNoPriceYet] = useState(false);

  // The AI Assistant Questionnaire is only filled in-form by an agent working on
  // their own listing. When someone (e.g. a VA/admin) creates a listing for other
  // agents — i.e. the creator is NOT one of the assigned agents — the questionnaire
  // is hidden and the questions are emailed to the primary agent instead. Always
  // shown in edit mode so an assigned agent can open the listing and answer.
  const assignedAgentIds = [form.agentId, form.secondaryAgent, form.tertiaryAgent]
    .map((v) => String(v || "").trim())
    .filter(Boolean);
  const isCreatorAssignedAgent =
    !!currentUser?.id && assignedAgentIds.includes(String(currentUser.id));
  const showQuestionnaire = isEdit || isCreatorAssignedAgent;

  const extractCreatedPropertyId = (response) => {
    if (!response) return null;
    const d = response.data;
    const h = response.headers;
    const candidates = [
      d?.id,
      d?.property?.id,
      d?.created_id,
      d?.property_id,
      d?.data?.id,
      d?.result?.id,
    ].filter((v) => v !== undefined && v !== null);
    if (candidates.length > 0) {
      return candidates[0];
    }
    const location = h?.location || h?.Location;
    if (typeof location === "string") {
      const match = location.match(/\/(\d+)(?:\/?$|\b)/);
      if (match && match[1]) {
        return Number(match[1]);
      }
    }
    return null;
  };

  // Pre-fetch agents for all users (or modify as needed) - The "Admin" check was blocking it in the JSX
  useEffect(() => {
    fetchAgents(false); // Initial fetch without missing agents
  }, []);

  // Re-fetch agents with missing agents when initialValues are available
  useEffect(() => {
    if (isEdit && initialValues) {
      fetchAgents(true); // Include missing agents from initialValues
    }
  }, [isEdit, initialValues]);
  // console.log(initialValues);

  const fetchAgents = async (includeMissingAgents = false) => {
    try {
      // role: "agent" keeps this list (used for the InfiniteScrollSelect's
      // initialOptions) in sync with fetchAgentOptions above. A listing
      // already assigned to a non-agent (from before this fix) still
      // resolves via the individual getAgent(id) fallback below.
      const res = await getAgents({ role: "agent" });
      // console.log({ res });
      // Handle both paginated and non-paginated responses
      const agentsData = res?.data?.results || res?.data || [];
      let finalAgents = agentsData;

      // If we need to include missing agents (from initialValues), fetch them
      if (includeMissingAgents && isEdit && initialValues) {
        const primaryAgentId = initialValues?.agentId ||
          (initialValues?.agent?.id ? String(initialValues.agent.id) : "");
        const secondaryAgentId = initialValues?.secondaryAgent ||
          (initialValues?.co_agent?.id ? String(initialValues.co_agent.id) : "");

        const agentIdsToFetch = [];
        if (primaryAgentId && !agentsData.find(a => String(a.id) === String(primaryAgentId))) {
          agentIdsToFetch.push(primaryAgentId);
        }
        if (secondaryAgentId && !agentsData.find(a => String(a.id) === String(secondaryAgentId))) {
          agentIdsToFetch.push(secondaryAgentId);
        }

        // Fetch missing agents
        if (agentIdsToFetch.length > 0) {
          const { getAgent } = require("../../../api/agent");
          const missingAgents = await Promise.all(
            agentIdsToFetch.map(id =>
              getAgent(id).catch(() => null) // Return null if agent doesn't exist
            )
          );

          const validMissingAgents = missingAgents
            .filter(agent => agent && agent.data)
            .map(agent => agent.data);

          if (validMissingAgents.length > 0) {
            finalAgents = [...agentsData, ...validMissingAgents];
          }
        }
      }

      setAgents(finalAgents);
    } catch (error) {
      console.error("Failed to fetch agents:", error);
      toast.error("Failed to fetch agents.");
    }
  };

  useEffect(() => {
    // console.log(isAdmin);
    if (isAdmin) fetchAgents();
  }, [isAdmin]);
  useEffect(() => {
    // Only update form if we're in edit mode AND have initial values
    // Also ensure agents are loaded so dropdowns can show selected values
    if (isEdit && initialValues) {
      // Get agent IDs - check agent objects first (like public properties), then direct agentId field
      const primaryAgentId = (initialValues?.agent?.id ? String(initialValues.agent.id) : "") ||
        initialValues?.agentId || "";
      const secondaryAgentId = (initialValues?.co_agent?.id ? String(initialValues.co_agent.id) : "") ||
        initialValues?.secondaryAgent || "";

      // Ensure agent IDs are strings for proper dropdown matching
      const primaryAgentIdStr = primaryAgentId ? String(primaryAgentId).trim() : "";
      const secondaryAgentIdStr = secondaryAgentId ? String(secondaryAgentId).trim() : "";

      console.log("=== Setting Form with Agents ===");
      console.log("InitialValues agentId:", initialValues?.agentId);
      console.log("InitialValues secondaryAgent:", initialValues?.secondaryAgent);
      console.log("Primary Agent ID (string):", primaryAgentIdStr);
      console.log("Secondary Agent ID (string):", secondaryAgentIdStr);
      console.log("Available agents count:", agents?.length);
      console.log("Agent IDs in dropdown:", agents?.map(a => String(a.id)));
      console.log("Will match?", agents?.some(a => String(a.id) === primaryAgentIdStr));

      // Format price and weekly_rent with dollar sign and commas
      const formatCurrency = (value) => {
        if (!value || value === "0" || value === 0) return "";
        // Handle both string and number inputs
        const strValue = String(value);
        // Remove all non-numeric characters except decimal point
        const numericValue = strValue.replace(/[^\d.]/g, "");
        if (!numericValue) return "";
        // Parse as float to preserve decimals, then format
        const num = parseFloat(numericValue);
        if (isNaN(num)) return "";
        // Format with commas, no decimals for display
        return `$${Math.round(num).toLocaleString()}`;
      };

      setForm({
        title: initialValues?.title || "",
        address: initialValues?.address || "",
        street: initialValues?.street || initialValues?.street_name || "",
        suburb: initialValues?.suburb || "",
        city: initialValues?.city || "",
        state: initialValues?.state || "",
        postal_code: initialValues?.postal_code || "",
        address_publish_mode: initialValues?.address_publish_mode || "full",
        price: formatCurrency(initialValues?.price),
        // For commercial: display annual rent (weekly_rent × 52) since agents enter/view it as annual
        weekly_rent: (() => {
          const isCommercialEdit = (initialValues?.extra_info?.property_class || initialValues?.property_class) === "Commercial";
          const raw = initialValues?.weekly_rent || initialValues?.extra_info?.weekly_rent;
          if (isCommercialEdit && raw) return formatCurrency(parseFloat(raw) * 52);
          return formatCurrency(raw);
        })(),
        bond: formatCurrency(initialValues?.bond),
        available_date: initialValues?.available_date || "",
        rental_application_url: initialValues?.rental_application_url || "",
        furnished: !!initialValues?.furnished,
        beds: initialValues?.beds || "",
        baths: initialValues?.baths || "",
        status: initialValues?.status?.name || initialValues?.status || "On Market Listing",
        images: initialValues?.images?.map((img) => img?.file || img) || [],
        description: initialValues?.description || "",
        features: initialValues?.features || "",
        featuresList: initialValues?.features
          ? initialValues.features.split(',').map(f => f.trim()).filter(f => f)
          : [],
        internalFeatures: initialValues?.internal_features || "",
        internalFeaturesList: initialValues?.internal_features
          ? initialValues.internal_features.split(',').map(f => f.trim()).filter(f => f)
          : [],
        externalFeatures: initialValues?.external_features || "",
        externalFeaturesList: initialValues?.external_features
          ? initialValues.external_features.split(',').map(f => f.trim()).filter(f => f)
          : [],
        agent: initialValues?.owner?.name || "",
        owner_preferred_name:
          initialValues?.owner?.preferred_name || initialValues?.owner_preferred_name || "",
        // Normalise the seeded owner phone to E.164 the same way the prefill path
        // does (line ~923). Legacy/edit records hold local AU form ("0405590684");
        // without this the Save check and the field flag it "Invalid phone number"
        // on load even though the widget displays a clean +61 number, and the agent
        // has to click in and press space to clear it (CRM-2026-00086).
        contact: toE164Value(initialValues?.owner?.contact || ""),
        email: initialValues?.owner?.email || "",
        contact_secondary: initialValues?.owner?.contact_secondary || "",
        email_secondary: initialValues?.owner?.email_secondary || "",
        // Map backend property_type to frontend propertyTypes format
        // Try property_class first (more specific), then property_type
        propertyTypes: (() => {
          const propertyClass = initialValues?.extra_info?.property_class || initialValues?.property_class;
          // Check if property_class matches one of our propertyTypeOptions
          if (propertyClass) {
            const matchingOption = propertyTypeOptions.find(opt =>
              opt.id.toLowerCase() === propertyClass.toLowerCase() ||
              opt.label.toLowerCase() === propertyClass.toLowerCase()
            );
            if (matchingOption) {
              return matchingOption.id;
            }
          }
          // Fall back to mapping property_type
          return mapBackendPropertyTypeToFrontend(initialValues?.property_type) || "Apartment";
        })(),
        publicShare: initialValues?.owner_visibilty || false,
        agentId: primaryAgentIdStr,
        secondaryAgent: secondaryAgentIdStr,
        makeSecondaryAgentOnListing: !!secondaryAgentIdStr, // When loading, if co_agent is set then tick "Make Secondary"
        tertiaryAgent: (initialValues?.tertiary_agent?.id != null ? String(initialValues.tertiary_agent.id) : (initialValues?.tertiaryAgent || "")) || "",
        status: initialValues?.status?.name || initialValues?.status || (listingType === 'offmarket' ? "Off Market Listing" : listingType === 'appraisal' ? "Appraisal" : "On Market Listing"),
        // Additional fields
        transaction_type: initialValues?.transaction_type || "sale",
        land_size: initialValues?.land_size || "",
        building_size: initialValues?.building_size || "",
        year_built: initialValues?.year_built || "",
        price_display: initialValues?.price_display || "exact",
        price_to: formatCurrency(initialValues?.price_to),
        price_display_text: initialValues?.price_display_text || "",
        // initialValues.auction_date is the stored UTC ISO string - a
        // datetime-local input needs a bare "YYYY-MM-DDTHH:MM" in Melbourne
        // wall-clock time, not the raw UTC value passed straight through.
        auction_date: initialValues?.auction_date
          ? `${formatMelbourneDatePart(initialValues.auction_date)}T${formatMelbourneTimePart(initialValues.auction_date)}`
          : "",
        auction_venue: initialValues?.auction_venue || "",
        commission_type: initialValues?.commission_type || initialValues?.extra_info?.commission_type || (initialValues?.commission_amount != null && initialValues?.commission_amount !== "" ? "fixed" : "percentage"),
        commission_rate: initialValues?.commission_rate ?? initialValues?.extra_info?.commission_rate ?? "",
        commission_amount: formatCurrency(initialValues?.commission_amount ?? initialValues?.extra_info?.commission_amount),
        commission_gst: initialValues?.commission_gst || initialValues?.extra_info?.commission_gst || "inclusive",
        letting_fee_type: initialValues?.letting_fee_type || initialValues?.extra_info?.letting_fee_type || (initialValues?.letting_fee_amount != null && initialValues?.letting_fee_amount !== "" ? "fixed" : "percentage"),
        letting_fee_rate: initialValues?.letting_fee_rate ?? initialValues?.extra_info?.letting_fee_rate ?? "",
        letting_fee_amount: formatCurrency(initialValues?.letting_fee_amount ?? initialValues?.extra_info?.letting_fee_amount),
        letting_fee_gst: initialValues?.letting_fee_gst || initialValues?.extra_info?.letting_fee_gst || "inclusive",
        virtual_tour_link: initialValues?.virtual_tour_link || "",
        video_url: initialValues?.video_url || "",
        published_on_rea: initialValues?.published_on_rea || false,
        published_on_realcommercial: initialValues?.published_on_realcommercial || false,
        published_on_domain: initialValues?.published_on_domain || false,
        published_on_website: initialValues?.published_on_website !== undefined ? initialValues.published_on_website : false,  // Default false — must explicitly tick Buyer Portal
        portal_category: PORTAL_CATEGORY_VALUES.includes(initialValues?.portal_category) ? initialValues.portal_category : "",
        internal_marketing: initialValues?.internal_marketing || false,
        published_on_agentpoint: initialValues?.published_on_agentpoint || false,
        published_on_development_ready: initialValues?.published_on_development_ready || false,
        published_on_inspect_real_estate: initialValues?.published_on_inspect_real_estate || false,
        published_on_ratemyagent: initialValues?.published_on_ratemyagent || false,
        published_on_realtimeagent: initialValues?.published_on_realtimeagent || false,
        published_on_collings: initialValues?.published_on_collings || false,
        // Stored UTC ISO → bare "YYYY-MM-DDTHH:MM" in Melbourne wall-clock time
        // for the datetime-local input (mirrors auction_date prefill above).
        listing_launch_at: initialValues?.listing_launch_at
          ? `${formatMelbourneDatePart(initialValues.listing_launch_at)}T${formatMelbourneTimePart(initialValues.listing_launch_at)}`
          : "",
        appraisal_market_type: initialValues?.appraisal_market_type || "",
        appraisal_temperature: initialValues?.appraisal_temperature || "warm",
        est_gross_yield: initialValues?.est_gross_yield || "",
        // Total Estimated Outgoings: use API value or calculate from breakdown so it always shows on edit
        est_outgoings: (() => {
          const fromApi = initialValues?.est_outgoings || "";
          if (fromApi !== "" && fromApi != null) return String(fromApi);
          const bodyCorp = initialValues?.body_corporate ? (parseFloat(initialValues.body_corporate) || 0) : 0;
          const council = parseFloat(initialValues?.council_rates) || 0;
          const water = parseFloat(initialValues?.water_rates) || 0;
          const mgmt = parseFloat(initialValues?.current_annual_property_management_fee) || 0;
          const tenantPays = initialValues?.tenant_pays_opex ?? true;
          const insur = !tenantPays ? (parseFloat(initialValues?.insurance) || 0) : 0;
          const landTax = !tenantPays ? (parseFloat(initialValues?.land_tax) || 0) : 0;
          const otherOpex = !tenantPays ? (parseFloat(initialValues?.other_opex) || 0) : 0;
          const total = bodyCorp + council + water + mgmt + insur + landTax + otherOpex;
          return total > 0 ? String(total) : "";
        })(),
        est_net_yield: initialValues?.est_net_yield || "",
        body_corporate: initialValues?.body_corporate || "",
        has_body_corporate: !!initialValues?.body_corporate,
        council_rates: initialValues?.council_rates || "",
        water_rates: initialValues?.water_rates || "",
        opex: initialValues?.opex || initialValues?.extra_info?.opex || "",
        insurance: initialValues?.insurance || "",
        land_tax: initialValues?.land_tax || "",
        other_opex: initialValues?.other_opex || "",
        tenant_pays_opex: initialValues?.tenant_pays_opex ?? true,
        current_annual_property_management_fee: initialValues?.current_annual_property_management_fee || "",
        // Restore the management-fee opt-in from whether a fee was previously stored
        include_management_fee: !!(parseFloat(initialValues?.current_annual_property_management_fee) > 0),
        // Edit mode: the API returns these as `additional_owners`. Normalise both
        // shapes so extra owners (and their preferred names) survive a re-save
        // instead of being invisible on the form.
        additionalOwners: (initialValues?.additionalOwners || initialValues?.additional_owners || []).map((o) => ({
          name: o?.name || "",
          preferred_name: o?.preferred_name || "",
          contact: o?.contact || "",
          email: o?.email || "",
        })),
        // Additional Fields
        method_of_sale: initialValues?.method_of_sale || "Private Sale",
        parking_type: initialValues?.parking_type || "",
        parking_spaces: initialValues?.parking_spaces || initialValues?.parkings || "",
        parking_list: initialValues?.parking_list || (() => {
          // Seed from the 3 typed count fields if any of them are populated.
          const entries = [];
          if (Number(initialValues?.parkings) > 0) {
            entries.push({ type: "garage", spaces: initialValues.parkings });
          }
          if (Number(initialValues?.car_ports) > 0) {
            entries.push({ type: "carport", spaces: initialValues.car_ports });
          }
          if (Number(initialValues?.open_spaces) > 0) {
            entries.push({ type: "open", spaces: initialValues.open_spaces });
          }
          // Fall back to legacy parking_type/parking_spaces if no typed counts exist.
          if (entries.length === 0) {
            entries.push({
              type: initialValues?.parking_type || "",
              spaces: initialValues?.parking_spaces || ""
            });
          }
          return entries;
        })(),
        floor_area: initialValues?.building_size || initialValues?.floor_area || "",
        land_area: initialValues?.land_size || initialValues?.land_area || "",
        commercial_category: initialValues?.commercial_category
          || initialValues?.extra_info?.commercial_category || "",
        commercial_category_2: initialValues?.commercial_category_2
          || initialValues?.extra_info?.commercial_category_2 || "",
        commercial_category_3: initialValues?.commercial_category_3
          || initialValues?.extra_info?.commercial_category_3 || "",
        // property_class will be synced with propertyTypes via useEffect below
        property_class: "",
        ai_questionnaire: Array.isArray(initialValues?.ai_questionnaire)
          ? initialValues.ai_questionnaire.map((qa) => ({
              question: String(qa?.question || ""),
              answer: String(qa?.answer || ""),
            }))
          : [],
      });
      if (initialValues?.images?.length > 0) {
        setImagePreviews(
          initialValues.images.map((img) => {
            const file = img?.file || img;
            if (file instanceof File) {
              return URL.createObjectURL(file);
            }
            if (typeof file === "string") {
              return file;
            }
            return "";
          })
        );
        setFloorplanFlags(
          initialValues.images.map((img) => Boolean(img?.is_floorplan))
        );
      }
      console.log("Form state after update - agentId:", primaryAgentIdStr, "secondaryAgent:", secondaryAgentIdStr);

      // Sync property_class with propertyTypes after form is initialized
      setForm(prev => {
        const currentPropertyTypes = prev.propertyTypes;
        // If propertyTypes is set but property_class is not, sync them
        if (currentPropertyTypes && !prev.property_class) {
          return { ...prev, property_class: currentPropertyTypes };
        }
        return prev;
      });

      // Show annual outgoings section if there's existing outgoings data
      const hasOutgoingsData = !!(initialValues?.body_corporate || initialValues?.council_rates ||
        initialValues?.water_rates || initialValues?.current_annual_property_management_fee ||
        initialValues?.est_outgoings || initialValues?.insurance || initialValues?.land_tax ||
        initialValues?.other_opex);
      setShowAnnualOutgoings(hasOutgoingsData);
    } else if (!isEdit) {
      // For NEW properties, set status based on listingType prop from the initial modal selection
      setForm(prev => ({
        ...prev,
        status: listingType === 'offmarket' ? "Off Market Listing" :
          listingType === 'appraisal' ? "Appraisal" :
            "On Market Listing",
        // Seed the AI questionnaire with default real-estate questions for fresh listings
        ai_questionnaire: prev.ai_questionnaire?.length ? prev.ai_questionnaire : buildDefaultQuestionnaire(),
      }));

      // Also handle case where initialValues.status might be passed from some other flow
      if (initialValues?.status) {
        setForm(prev => ({ ...prev, status: initialValues.status }));
      }

      // A NEW property can arrive pre-filled. "Convert to appraisal" in the
      // Database sends the owner's property address (and their details) straight
      // into this form (Christian Gravias, 2026-08-03). Seeded exactly once: this
      // effect re-runs whenever the parent re-renders (initialValues is a fresh
      // object each time), and re-seeding would fight whatever the agent typed.
      if (prefill && Object.keys(prefill).length && !prefillAppliedRef.current) {
        prefillAppliedRef.current = true;
        setForm(prev => {
          const next = { ...prev };
          Object.entries(PREFILL_FIELD_MAP).forEach(([source, target]) => {
            const value = prefill[source];
            if (value === undefined || value === null || value === "") return;
            next[target] = value;
          });
          // Seller Leads and the Database hand over the phone as typed
          // ("0478415328"); the Save check wants +61 form, so the hand-off
          // stopped on "Invalid phone number" (24 Renown Street, 1 Oct 2026).
          if (next.contact) next.contact = toE164Value(next.contact);
          if (prefill.property_type) {
            const mapped = mapBackendPropertyTypeToFrontend(prefill.property_type);
            if (mapped) {
              next.type = mapped;
              next.propertyTypes = next.propertyTypes || mapped;
            }
          }
          // Second owner on the row keeps their own name and contact line.
          const extraNames = prefill.additional_owner_names || [];
          const extraPreferred = prefill.additional_owner_preferred_names || [];
          const extraContacts = prefill.additional_owner_contacts || [];
          const extraEmails = prefill.additional_owner_emails || [];
          const extraCount = Math.max(extraNames.length, extraContacts.length, extraEmails.length);
          if (extraCount && !(next.additionalOwners || []).length) {
            next.additionalOwners = Array.from({ length: extraCount }, (_, i) => ({
              name: extraNames[i] || "",
              preferred_name: extraPreferred[i] || "",
              contact: extraContacts[i] || "",
              email: extraEmails[i] || "",
            }));
          }
          return next;
        });
      }
    }
    // NOTE: 'agents' is intentionally NOT a dependency. setAgents fires twice on mount
    // (initial fetch + missing-agents fetch), and including it would re-run setForm({...})
    // which is a full replacement — wiping any field the user has already typed into
    // (e.g., suburb edits would silently revert to initialValues.suburb).
  }, [isEdit, initialValues, listingType, prefill]);

  /*
   * A NEW APPRAISAL STARTS ON THE AGENT ENTERING IT.
   *
   * Simon Abbott, 3 Sep 2026: "Automatically select the agent entering the
   * appraisal as the primary agent and they can change it if needs be."
   * One-shot and only ever fills an empty box, so a handoff that already named
   * an agent, and anything the agent picks afterwards, both stand.
   *
   * The two pipeline defaults ride along here because the form refused to save
   * without them: the Temperature dropdown READ "Warm" (`value={x || "warm"}`)
   * while the form field itself was still empty, so validation demanded a
   * choice the agent could see was already made.
   */
  const primaryAgentSeededRef = React.useRef(false);
  useEffect(() => {
    if (isEdit || primaryAgentSeededRef.current) return;
    if (effectiveListingType !== "appraisal") return;
    const meId = currentUser?.id;
    if (!meId) return;
    primaryAgentSeededRef.current = true;
    setForm((prev) => ({
      ...prev,
      agentId: prev.agentId || String(meId),
      appraisal_market_type: prev.appraisal_market_type || "off_market",
      appraisal_temperature: prev.appraisal_temperature || "warm",
    }));
    // The agent dropdown labels its value from the fetched page of agents, and
    // the current user isn't necessarily on it — add them so the box reads
    // their name rather than sitting blank.
    setAgents((prev) =>
      prev.some((a) => String(a.id) === String(meId))
        ? prev
        : [...prev, { id: meId, name: currentUser?.name || "Me" }]
    );
  }, [isEdit, effectiveListingType, currentUser]);

  // Sync property_class with propertyTypes whenever propertyTypes changes
  useEffect(() => {
    if (form.propertyTypes && form.property_class !== form.propertyTypes) {
      setForm(prev => ({ ...prev, property_class: prev.propertyTypes }));
    }
  }, [form.propertyTypes]);

  // Recalculate yields when editing an existing property to ensure accurate values
  // This runs once after form is initialized with initial values
  useEffect(() => {
    if (isEdit && initialValues && form.weekly_rent) {
      setForm(prev => {
        const yields = calculateYields(prev);
        return { ...prev, ...yields };
      });
    }
  }, [isEdit, initialValues?.id]); // Only run when editing a specific property
  // console.log("Current Form State:", form);
  // console.log(initialValues);

  const validateForm = () => {
    const newErrors = {};

    // Appraisals are an early-stage pipeline record, not a listing: price, commission,
    // description, media and owner contact details are all optional at this point.
    const isAppraisal = form.status === "Appraisal";

    // Title validation - removed since title field was removed from UI
    // Title will be auto-generated from address and property type if not provided

    // Address validation
    if (!form.address?.trim()) {
      newErrors.address = "Address is required";
    }

    // Suburb validation
    if (!form.suburb?.trim()) {
      newErrors.suburb = "Suburb is required";
    }

    // State validation
    if (!form.state?.trim()) {
      newErrors.state = "State is required";
    }

    // Postal code validation
    if (!form.postal_code?.trim()) {
      newErrors.postal_code = "Postal code is required";
    }
    // Agent is required for all users
    if (!form.agentId?.trim()) {
      newErrors.agentId = "Agent is required";
    }

    // Price validation - only for sale properties (not lease)
    const isLease = form.transaction_type === "lease";

    if (!isLease) {
      // Price validation for sale properties only
      // Price is optional for "contact" and "auction" price types (guided price) and for appraisals
      const isOptionalPrice = isAppraisal || form.price_display === "contact" || form.price_display === "auction";
      const numericPrice = form.price ? parseFloat(form.price.replace(/[$,]/g, "")) : 0;
      const minPrice = 2900;

      if (!isOptionalPrice && !form.price?.trim()) {
        newErrors.price = "Price is required";
      } else if (form.price?.trim() && !/^\$?[\d,]+(\.\d{2})?$/.test(form.price)) {
        newErrors.price = "Invalid price format";
      } else if (form.price?.trim() && numericPrice < minPrice) {
        newErrors.price = `Minimum: $2,900`;
      }

      // Price range validation - if price_display is 'range', validate price_to
      if (form.price_display === "range") {
        if (!form.price_to?.trim()) {
          if (!isAppraisal) {
            newErrors.price_to = "Price To is required for price ranges";
          }
        } else {
          const numericPriceTo = parseFloat(form.price_to.replace(/[$,]/g, ""));
          const numericPriceFrom = parseFloat(form.price.replace(/[$,]/g, ""));

          if (!/^\$?[\d,]+(\.\d{2})?$/.test(form.price_to)) {
            newErrors.price_to = "Invalid price format";
          } else if (numericPriceTo < minPrice) {
            newErrors.price_to = `Minimum: $2,900`;
          } else if (numericPriceTo <= numericPriceFrom) {
            newErrors.price_to = "Price To must be greater than Price From";
          }
        }
      }
    }

    // Weekly rent validation - required for lease properties, optional for sale
    if (isLease) {
      const numericWeeklyRent = form.weekly_rent ? parseFloat(form.weekly_rent.replace(/[$,]/g, "")) : 0;
      if (!form.weekly_rent?.trim()) {
        newErrors.weekly_rent = "Weekly rent is required for lease properties";
      } else if (!/^\$?[\d,]+(\.\d{2})?$/.test(form.weekly_rent)) {
        newErrors.weekly_rent = "Invalid weekly rent format";
      } else if (numericWeeklyRent < 100) {
        newErrors.weekly_rent = "Minimum: $100";
      }

      // Bond is mandatory for residential lease properties. Commercial leases don't
      // collect a bond (the Bond field is only shown for residential), so skip it there.
      const isCommercialLease = form.property_class === "Commercial";
      if (!isCommercialLease) {
        if (!form.bond?.trim()) {
          newErrors.bond = "Bond is required for lease properties";
        } else if (!/^\$?[\d,]+(\.\d{2})?$/.test(form.bond)) {
          newErrors.bond = "Invalid bond format";
        }
      }
    } else if (form.weekly_rent?.trim()) {
      // Optional validation for sale properties - only validate if provided
      const numericWeeklyRent = parseFloat(form.weekly_rent.replace(/[$,]/g, ""));
      if (!/^\$?[\d,]+(\.\d{2})?$/.test(form.weekly_rent)) {
        newErrors.weekly_rent = "Invalid weekly rent format";
      } else if (numericWeeklyRent < 100) {
        newErrors.weekly_rent = "Minimum: $100";
      }
    }

    // Status is set from initial pop-up (Appraisal / Off-Market / On-Market); only validate if present
    if (!form.status) {
      newErrors.status = "Status is required";
    }
    // Appraisal pipeline: require off/on-market and temperature when status is Appraisal
    if (form.status === "Appraisal") {
      if (!form.appraisal_market_type) {
        newErrors.appraisal_market_type = "Please select Off-market or On-market appraisal";
      }
      if (!form.appraisal_temperature) {
        newErrors.appraisal_temperature = "Please select Cold, Warm or Hot";
      }
      // A from-scratch appraisal (not created from a Collings lead) must declare
      // how it was generated, so its Origin is attributed (CRM-2026 Phase 2).
      if (!lead_id && !form.origin_reason) {
        newErrors.origin_reason = "Please select how this appraisal was generated";
      }
    }
    if (!form.propertyTypes) {
      newErrors.propertyTypes = "property type is required";
    } else if (!propertyTypeOptions.some(opt => opt.id === form.propertyTypes)) {
      newErrors.propertyTypes = "Invalid property type selected";
    }

    const isCommercialOrLand = form.propertyTypes === "Commercial" || form.propertyTypes === "Land";

    // Beds validation — never required on an appraisal. Simon Abbott, 3 Sep
    // 2026: "Bedrooms, Bathrooms and Car Parks (Don't make this a required
    // field)". At appraisal stage the agent may not have been inside yet.
    if (form.beds === "") {
      if (!isCommercialOrLand && !isAppraisal) newErrors.beds = "Number of beds is required";
    } else if (isNaN(form.beds) || form.beds < 0) {
      newErrors.beds = "Beds must be a positive number";
    } else if (!Number.isInteger(Number(form.beds))) {
      newErrors.beds = "Beds must be a whole number";
    } else if (Number(form.beds) > 50) {
      newErrors.beds = "Maximum: 50";
    }

    // Baths validation — optional on an appraisal, same reasoning as beds.
    if (form.baths === "") {
      if (!isCommercialOrLand && !isAppraisal) newErrors.baths = "Number of baths is required";
    } else if (isNaN(form.baths) || form.baths < 0) {
      newErrors.baths = "Baths must be a positive number";
    } else if (!Number.isInteger(Number(form.baths))) {
      newErrors.baths = "Baths must be a whole number";
    } else if (Number(form.baths) > 50) {
      newErrors.baths = "Maximum: 50";
    }

    // Council Rates and Water Rates - No longer required (optional for all properties)

    // Parking validation
    form.parking_list.forEach((item, index) => {
      if (item.spaces !== "" && item.spaces !== undefined && item.spaces !== null) {
        if (isNaN(item.spaces) || item.spaces < 0) {
          newErrors[`parking_spaces_${index}`] = "Must be a positive number";
        } else if (!Number.isInteger(Number(item.spaces))) {
          newErrors[`parking_spaces_${index}`] = "Must be a whole number";
        } else if (Number(item.spaces) > 50) {
          newErrors[`parking_spaces_${index}`] = "Max 50";
        }
      }
      if (item.spaces && !item.type) {
        newErrors[`parking_type_${index}`] = "Type required";
      }
    });

    // Description validation - not required for Appraisal
    if (!isAppraisal) {
      if (!form.description?.trim()) {
        newErrors.description = "Description is required";
      }
    }

    // Features are optional

    // Images validation - not required for Appraisal
    if (!isAppraisal && form.images?.length === 0) {
      newErrors.images = "At least one image or video is required";
    }

    // Agent and Contact conditional validation - relaxed for Appraisal
    if (!isAppraisal && form.agent?.trim() && !form.contact?.trim()) {
      newErrors.contact = "Contact is required";
    } else if (!form.agent?.trim() && form.contact?.trim()) {
      newErrors.agent = "Agent is required";
    }

    // Owner email validation - not required for Appraisal
    if (!isAppraisal && form.agent?.trim() && !form.email?.trim()) {
      newErrors.email = "Owner email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(form.email) && form.email?.trim()) {
      newErrors.email = "Invalid email format";
    }

    // Phone validation - not required for Appraisal.
    if (!form.contact) {
      if (!isAppraisal) {
        newErrors.contact = "Phone number is required";
      }
    } else if (!isCompletePhoneNumber(form.contact)) {
      // Validate the NORMALISED value, not the raw string: the widget displays the
      // number through toE164Value, so a local-form "0405590684" shows as a clean
      // +61 number while raw isValidPhoneNumber() rejected it - the "Invalid phone
      // number" that only cleared after typing a space (CRM-2026-00086).
      // Don't trap an agent editing a listing that was imported with a malformed
      // owner phone (common on legacy/commercial data). Key off whether the
      // ORIGINAL number was valid - not string equality, which the phone widget's
      // load-time normalisation would break: only block when the listing didn't
      // already have a bad number (a new listing, or one the agent has now
      // broken). A pre-existing invalid legacy number never blocks the save.
      const originalContact = initialValues?.owner?.contact || "";
      const originalWasValid = originalContact && isCompletePhoneNumber(originalContact);
      if (originalWasValid || !originalContact) {
        newErrors.contact = "Invalid phone number";
      }
    }

    // Owner name — the one owner field an appraisal insists on. Simon Abbott,
    // 3 Sep 2026: "Owner details (Don't make this a required field. Only the
    // owners name is required)". Phone and email above stay optional. Set last
    // so it wins over the generic "Agent is required" branch above, which fires
    // on the same empty field with wording that means nothing to the agent.
    if (isAppraisal && !form.agent?.trim()) {
      newErrors.agent = "Owner name is required";
    }

    // Commission validation - REQUIRED only for Sale listings (not leases, not appraisals).
    // Appraisals are pre-listing: the commission isn't agreed yet, so it stays optional.
    if (!isLease) {
      const commissionType = form.commission_type || "percentage";
      if (!commissionType) {
        if (!isAppraisal) newErrors.commission_type = "Commission type is required";
      } else if (commissionType === "percentage") {
        if (form.commission_rate === null || form.commission_rate === undefined || form.commission_rate === "") {
          if (!isAppraisal) newErrors.commission_rate = "Commission rate is required when using percentage";
        } else if (isNaN(form.commission_rate) || parseFloat(form.commission_rate) < 0) {
          newErrors.commission_rate = "Commission rate must be a positive number";
        } else if (parseFloat(form.commission_rate) > 100) {
          newErrors.commission_rate = "Commission rate cannot exceed 100%";
        }
      } else if (commissionType === "fixed") {
        if (!form.commission_amount || form.commission_amount === "") {
          if (!isAppraisal) newErrors.commission_amount = "Commission amount is required when using fixed amount";
        } else {
          const numericAmount = parseFloat(form.commission_amount.replace(/[$,]/g, ""));
          if (isNaN(numericAmount) || numericAmount <= 0) {
            newErrors.commission_amount = "Commission amount must be a positive number";
          }
        }
      }
    }

    // Video URL validation
    if (form.video_url?.trim() && !/^https?:\/\//i.test(form.video_url.trim())) {
      newErrors.video_url = "Video URL must start with http:// or https://";
    }

    // Agent validation
    if (!form.agentId) {
      newErrors.agentId = "Primary agent is required";
    }

    // Virtual Tour Link validation
    if (form.virtual_tour_link?.trim() && !/^https?:\/\//i.test(form.virtual_tour_link.trim())) {
      newErrors.virtual_tour_link = "Virtual Tour Link must start with http:// or https://";
    }

    setErrors(newErrors);
    return newErrors;
  };

  // Scroll to the first field with an error (in DOM order) and focus it
  const scrollToFirstError = (errs) => {
    const keys = Object.keys(errs || {});
    if (keys.length === 0) return null;

    let firstEl = null;
    let firstTop = Infinity;
    let firstKey = keys[0];

    for (const key of keys) {
      const el =
        document.querySelector(`[data-error-key="${key}"]`) ||
        document.querySelector(`[name="${key}"]`);
      if (el) {
        const rect = el.getBoundingClientRect();
        const top = rect.top + window.scrollY;
        if (top < firstTop) {
          firstTop = top;
          firstEl = el;
          firstKey = key;
        }
      }
    }

    if (firstEl) {
      firstEl.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => {
        try {
          const focusable =
            firstEl.tagName === "INPUT" ||
            firstEl.tagName === "TEXTAREA" ||
            firstEl.tagName === "SELECT"
              ? firstEl
              : firstEl.querySelector("input, textarea, select, button");
          if (focusable && typeof focusable.focus === "function") {
            focusable.focus({ preventScroll: true });
          }
        } catch (_) {}
      }, 350);
    }

    return errs[firstKey];
  };

  // Helper function to parse Australian address from formatted string
  const parseAddressString = (addressString) => {
    if (!addressString) return { suburb: '', state: '', postcode: '' };

    // Australian state abbreviations
    const stateAbbreviations = {
      'NSW': 'NSW', 'VIC': 'VIC', 'QLD': 'QLD', 'SA': 'SA',
      'WA': 'WA', 'TAS': 'TAS', 'NT': 'NT', 'ACT': 'ACT'
    };

    // Pattern to match: "SUBURB STATE POSTCODE" at the end of address
    // Examples: "NINGI QLD 4511", "MELBOURNE VIC 3000", "SYDNEY NSW 2000"
    const addressParts = addressString.trim().split(',').map(part => part.trim());

    // Get the last part which usually contains suburb, state, and postcode
    const lastPart = addressParts[addressParts.length - 1];

    // Match pattern: SUBURB STATE POSTCODE
    // Postcode is 4 digits, state is 2-4 letter abbreviation
    const postcodeMatch = lastPart.match(/\b(\d{4})\b/);
    const postcode = postcodeMatch ? postcodeMatch[1] : '';

    // Find state abbreviation (2-4 uppercase letters before postcode)
    let state = '';
    let suburb = '';

    if (postcode) {
      const beforePostcode = lastPart.substring(0, lastPart.indexOf(postcode)).trim();
      // Try to find state abbreviation (usually 2-4 letters, case-insensitive)
      const stateMatch = beforePostcode.match(/\b([A-Za-z]{2,4})\b$/i);

      if (stateMatch) {
        const potentialState = stateMatch[1].toUpperCase();
        // Check if it's a valid state abbreviation
        if (stateAbbreviations[potentialState]) {
          state = potentialState;
          // Suburb is everything before the state (case-insensitive match)
          const stateIndex = beforePostcode.toUpperCase().lastIndexOf(potentialState);
          suburb = beforePostcode.substring(0, stateIndex).trim();
        } else {
          // If not a recognized state, might be part of suburb name
          suburb = beforePostcode;
        }
      } else {
        suburb = beforePostcode;
      }
    } else {
      // No postcode found, try to extract suburb and state anyway
      const words = lastPart.split(/\s+/);
      if (words.length >= 2) {
        const lastWord = words[words.length - 1].toUpperCase();
        if (stateAbbreviations[lastWord]) {
          state = lastWord;
          suburb = words.slice(0, -1).join(' ');
        } else {
          suburb = lastPart;
        }
      } else {
        suburb = lastPart;
      }
    }

    return {
      suburb: suburb || '',
      state: state || '',
      postcode: postcode || ''
    };
  };

  // calculateBond (weekly rent -> Victorian calendar-month bond) now lives in
  // ../utils/rentBond so the Property Information edit panels share one formula.

  // Helper function to calculate yields based on price, rent, and expenses
  // Returns { grossYield, netYield, managementFee, estOutgoings }
  const calculateYields = (formData) => {
    const isCommercial = formData.property_class === "Commercial";
    const tenantPaysOpex = formData.tenant_pays_opex ?? true;
    const price = formData.price ? parseFloat(String(formData.price).replace(/[$,]/g, "")) : 0;
    const rentInput = formData.weekly_rent ? parseFloat(String(formData.weekly_rent).replace(/[$,]/g, "")) : 0;

    // For commercial: weekly_rent stores the annual value; for others: multiply by 52
    const annualRent = isCommercial ? rentInput : rentInput * 52;

    // Management fee: 6.6% of annual rent. For residential it is only included when the agent
    // opts in (so "rent but no outgoings" correctly stays N/A). Commercial net leases always
    // deduct the management fee (OpEx is borne by the tenant), so it is always included there.
    const includeManagementFee = isCommercial ? true : !!formData.include_management_fee;
    const managementFee = (includeManagementFee && annualRent > 0) ? Math.round(annualRent * 0.066 * 100) / 100 : 0;

    // Residential: body corporate, council rates, water rates are owner-borne
    const bodyCorporate = (!isCommercial && formData.has_body_corporate) ? (parseFloat(formData.body_corporate) || 0) : 0;
    const councilRates = !isCommercial ? (parseFloat(formData.council_rates) || 0) : 0;
    const waterRates = !isCommercial ? (parseFloat(formData.water_rates) || 0) : 0;

    // Commercial OpEx line items: excluded from owner expenses when tenant_pays_opex is true
    const commercialCouncil = (isCommercial && !tenantPaysOpex) ? (parseFloat(formData.council_rates) || 0) : 0;
    const commercialWater = (isCommercial && !tenantPaysOpex) ? (parseFloat(formData.water_rates) || 0) : 0;
    const insurance = (isCommercial && !tenantPaysOpex) ? (parseFloat(formData.insurance) || 0) : 0;
    const landTax = (isCommercial && !tenantPaysOpex) ? (parseFloat(formData.land_tax) || 0) : 0;
    const otherOpex = (isCommercial && !tenantPaysOpex) ? (parseFloat(formData.other_opex) || 0) : 0;

    const totalExpenses = bodyCorporate + councilRates + waterRates + commercialCouncil + commercialWater + insurance + landTax + otherOpex + managementFee;

    // If no rent, yields are N/A (empty string)
    if (!annualRent || annualRent <= 0) {
      return {
        est_gross_yield: "",
        est_net_yield: "",
        current_annual_property_management_fee: "",
        est_outgoings: ""
      };
    }

    // If no price or price is 0, yields show as "—" (handled by empty string, UI shows N/A)
    if (!price || price <= 0) {
      return {
        est_gross_yield: "",
        est_net_yield: "",
        current_annual_property_management_fee: managementFee > 0 ? managementFee.toFixed(2) : "",
        est_outgoings: totalExpenses > 0 ? totalExpenses.toFixed(2) : ""
      };
    }

    // Gross Yield = Annual Rent ÷ Price × 100
    const grossYield = (annualRent / price) * 100;

    // Net Yield = (Annual Rent − Owner Expenses) ÷ Price × 100
    // Only meaningful when outgoings were actually entered. With no outgoings the net yield would
    // just equal the gross yield, so we leave it N/A (empty) — same for the outgoings total.
    const netYield = ((annualRent - totalExpenses) / price) * 100;

    // A yield of 1000% or more is always a typo (price "1", or rent typed as the
    // yearly figure) and the database can't hold it, so show it as N/A instead.
    // Paul Jimoulis, CRM-2026-00101: the appraisal would not save because of it.
    const fits = (v) => Number.isFinite(v) && Math.abs(v) < 1000;

    return {
      est_gross_yield: fits(grossYield) ? grossYield.toFixed(2) : "",
      est_net_yield: totalExpenses > 0 && fits(netYield) ? netYield.toFixed(2) : "",
      current_annual_property_management_fee: managementFee > 0 ? managementFee.toFixed(2) : "",
      est_outgoings: totalExpenses > 0 ? totalExpenses.toFixed(2) : ""
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle price_display changes - clear price when switching FROM "exact" (Asking Price) TO other types
    if (name === "price_display") {
      const previousPriceDisplay = form.price_display || "exact";
      // If switching FROM "exact" (Asking Price) TO any other type, clear the price
      if (previousPriceDisplay === "exact" && value !== "exact") {
        setForm((prev) => ({
          ...prev,
          [name]: value,
          price: "",
          price_to: "",
        }));
        // Clear price errors
        setErrors((prev) => ({
          ...prev,
          price: undefined,
          price_to: undefined,
        }));
        return;
      }
      // If switching TO "exact" from other types, keep the price
      // If switching between non-exact types, keep the price
      setForm((prev) => ({ ...prev, [name]: value }));
      return;
    }

    if (name === "price") {
      // Remove all non-numeric characters
      let numericValue = value.replace(/[^\d]/g, "");
      // Format with commas
      let formatted = numericValue
        ? `$${parseInt(numericValue, 10).toLocaleString()}`
        : "";
      
      // Recalculate yields when price changes
      setForm((prev) => {
        const newForm = { ...prev, [name]: formatted };
        const yields = calculateYields(newForm);
        return { ...newForm, ...yields };
      });

      // Real-time validation for price - different minimums for sale vs lease
      const isLease = form.transaction_type === "lease";
      const minPrice = isLease ? 100 : 2900;
      const minPriceLabel = isLease ? "$100" : "$2,900";

      if (numericValue && parseInt(numericValue, 10) < minPrice) {
        setErrors((prev) => ({ ...prev, [name]: `Minimum: ${minPriceLabel}` }));
      } else {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
      return;
    }

    if (name === "price_to") {
      // Remove all non-numeric characters
      let numericValue = value.replace(/[^\d]/g, "");
      // Format with dollar sign and commas
      let formatted = numericValue
        ? `$${parseInt(numericValue, 10).toLocaleString()}`
        : "";
      setForm((prev) => ({ ...prev, [name]: formatted }));

      // Clear any errors for price_to
      if (errors.price_to) {
        setErrors((prev) => ({ ...prev, price_to: undefined }));
      }
      return;
    }

    if (name === "weekly_rent") {
      // Remove all non-numeric characters
      let numericValue = value.replace(/[^\d]/g, "");
      // Format with dollar sign and commas
      let formatted = numericValue
        ? `$${parseInt(numericValue, 10).toLocaleString()}`
        : "";
      
      // Auto-calculate management fee, yields and (for lease properties) bond
      // when weekly rent changes.
      setForm((prev) => {
        const newForm = { ...prev, [name]: formatted };
        const yields = calculateYields(newForm);
        const bond =
          prev.transaction_type === "lease"
            ? calculateBond(formatted, newForm.property_class)
            : newForm.bond;
        return { ...newForm, ...yields, bond };
      });

      // Clear any errors for weekly_rent
      if (errors.weekly_rent) {
        setErrors((prev) => ({ ...prev, weekly_rent: undefined }));
      }
      if (errors.bond) {
        setErrors((prev) => ({ ...prev, bond: undefined }));
      }
      return;
    }

    if (name === "bond") {
      // Format the rental bond as a currency value, same as weekly rent
      let numericValue = value.replace(/[^\d]/g, "");
      let formatted = numericValue
        ? `$${parseInt(numericValue, 10).toLocaleString()}`
        : "";
      setForm((prev) => ({ ...prev, bond: formatted }));
      if (errors.bond) {
        setErrors((prev) => ({ ...prev, bond: undefined }));
      }
      return;
    }

    // Auto-enable published_on_website when method_of_sale is set to a buyer portal value
    // Also auto-sync property class for Commercial and Off the Plan Apartments
    if (name === "method_of_sale") {
      const buyerPortalMethods = ["Private Sale", "Auction", "House and Land", "Commercial", "Off the Plan Apartments"];

      // Auto-sync property class when Commercial or Off the Plan is selected
      const classSync = {
        "Commercial": "Commercial",
        "Off the Plan Apartments": "Off the Plan Apartments",
      };

      const autoClass = classSync[value];

      if (buyerPortalMethods.includes(value)) {
        setForm((prev) => ({
          ...prev,
          [name]: value,
          // Do NOT auto-enable published_on_website — On-Market never goes to Buyer Portal
          // Auto-select matching property class if applicable
          ...(autoClass ? { propertyTypes: autoClass, property_class: autoClass } : {}),
        }));
        if (errors[name]) {
          setErrors((prev) => ({ ...prev, [name]: undefined }));
        }
        return;
      }
    }

    // Commission: pick one — percentage or fixed amount
    if (name === "commission_type") {
      setForm((prev) => ({
        ...prev,
        commission_type: value,
        commission_rate: value === "fixed" ? "" : prev.commission_rate,
        commission_amount: value === "percentage" ? "" : prev.commission_amount,
      }));
      return;
    }
    if (name === "commission_rate") {
      setForm((prev) => ({ ...prev, commission_rate: value, commission_amount: "" }));
      if (errors.commission_rate) setErrors((prev) => ({ ...prev, commission_rate: undefined }));
      return;
    }
    if (name === "commission_amount") {
      const numericValue = value.replace(/[^\d]/g, "");
      const formatted = numericValue ? `$${parseInt(numericValue, 10).toLocaleString()}` : "";
      setForm((prev) => ({ ...prev, commission_amount: formatted, commission_rate: "" }));
      if (errors.commission_amount) setErrors((prev) => ({ ...prev, commission_amount: undefined }));
      return;
    }

    // Letting Fee (lease listings) — mirrors commission handling above
    if (name === "letting_fee_type") {
      setForm((prev) => ({
        ...prev,
        letting_fee_type: value,
        letting_fee_rate: value === "fixed" ? "" : prev.letting_fee_rate,
        letting_fee_amount: value === "percentage" ? "" : prev.letting_fee_amount,
      }));
      return;
    }
    if (name === "letting_fee_rate") {
      setForm((prev) => ({ ...prev, letting_fee_rate: value, letting_fee_amount: "" }));
      return;
    }
    if (name === "letting_fee_amount") {
      const numericValue = value.replace(/[^\d]/g, "");
      const formatted = numericValue ? `$${parseInt(numericValue, 10).toLocaleString()}` : "";
      setForm((prev) => ({ ...prev, letting_fee_amount: formatted, letting_fee_rate: "" }));
      return;
    }


    // Auto-calculate total estimated outgoings and yields when expense fields change
    if (name === "body_corporate" || name === "council_rates" || name === "water_rates" ||
        name === "insurance" || name === "land_tax" || name === "other_opex") {
      setForm((prev) => {
        const newForm = { ...prev, [name]: value };
        const yields = calculateYields(newForm);
        return { ...newForm, ...yields };
      });
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
      return;
    }

    // Handle tenant_pays_opex checkbox — triggers yield recalculation
    if (name === "tenant_pays_opex") {
      const checked = e.target.checked;
      setForm((prev) => {
        const newForm = { ...prev, tenant_pays_opex: checked };
        const yields = calculateYields(newForm);
        return { ...newForm, ...yields };
      });
      return;
    }

    // Handle include_management_fee toggle — agent opts in/out of the standard 6.6% fee.
    // Recalculates the fee, outgoings and yields; clearing the stored fee when opted out.
    if (name === "include_management_fee") {
      const checked = e.target.checked;
      setForm((prev) => {
        const newForm = { ...prev, include_management_fee: checked };
        const yields = calculateYields(newForm);
        return { ...newForm, ...yields };
      });
      return;
    }

    // Handle opex change (legacy field) - kept for backward compat
    if (name === "opex") {
      setForm((prev) => ({ ...prev, opex: value }));
      return;
    }

    // Handle manual override of management fee - recalculate outgoings and yields.
    // Typing a fee implies opting in; clearing it opts out.
    if (name === "current_annual_property_management_fee") {
      setForm((prev) => {
        const includeManagementFee = parseFloat(value) > 0;
        const newForm = { ...prev, [name]: value, include_management_fee: includeManagementFee };
        const yields = calculateYields(newForm);
        return { ...newForm, ...yields };
      });
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
      return;
    }

    if (name === "transaction_type") {
      setForm((prev) => ({ ...prev, [name]: value }));

      const numericPrice = form.price ? parseFloat(form.price.replace(/[$,]/g, "")) : 0;
      const isLease = value === "lease";
      const minPrice = isLease ? 100 : 2900;
      const minPriceLabel = isLease ? "$100" : "$2,900";

      if (form.price && numericPrice < minPrice) {
        setErrors((prev) => ({ ...prev, price: `Minimum: ${minPriceLabel}` }));
      } else {
        setErrors((prev) => ({ ...prev, price: undefined }));
      }

      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
      return;
    }

    if (name === "has_body_corporate") {
      const checked = e.target.checked;
      setForm((prev) => {
        const newForm = {
          ...prev,
          has_body_corporate: checked,
          body_corporate: checked ? prev.body_corporate : ""
        };
        // Recalculate yields when body corporate toggle changes
        const yields = calculateYields(newForm);
        return { ...newForm, ...yields };
      });
      return;
    }

    // Parse address when address field changes and contains a postcode pattern
    if (name === "address" && value) {
      // Check if address contains a postcode pattern (4 digits)
      const hasPostcode = /\b\d{4}\b/.test(value);

      if (hasPostcode) {
        // Parse the address string to extract suburb, state, and postcode
        const parsed = parseAddressString(value);

        setForm((prev) => ({
          ...prev,
          address: value,
          suburb: parsed.suburb || prev.suburb,
          state: parsed.state || prev.state,
          postal_code: parsed.postcode || prev.postal_code
        }));

        // Clear errors for parsed fields
        if (parsed.suburb && errors.suburb) {
          setErrors((prev) => ({ ...prev, suburb: undefined }));
        }
        if (parsed.state && errors.state) {
          setErrors((prev) => ({ ...prev, state: undefined }));
        }
        if (parsed.postcode && errors.postal_code) {
          setErrors((prev) => ({ ...prev, postal_code: undefined }));
        }
        return;
      }
    }

    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleVisibilityChange = (key, checked) => {
    if (!checked) {
      setForm(prev => ({
        ...prev,
        [key]: false,
        // Clear portal_category and listing_launch_at when Buyer Portal is unchecked
        ...(key === 'published_on_website' ? { listing_launch_at: "", portal_category: "" } : {})
      }));
      return;
    }
    setPendingVisibilityChanges({ [key]: true });
    setShowVisibilityConfirm(true);
  };

  const proceedWithVisibilityChange = () => {
    if (!pendingVisibilityChanges) return;
    setForm(prev => ({ ...prev, ...pendingVisibilityChanges }));
    setPendingVisibilityChanges(null);
    setShowVisibilityConfirm(false);
  };

  const handleTickAll = (e) => {
    const checked = e.target.checked;
    // Commercial listings toggle realcommercial.com.au, not the residential REA feed.
    const primaryPortal = form.propertyTypes === "Commercial" ? "published_on_realcommercial" : "published_on_rea";
    if (!checked) {
      setForm(prev => ({
        ...prev,
        internal_marketing: false,
        // published_on_website NOT included — On-Market never goes to Buyer Portal
        [primaryPortal]: false,
        published_on_domain: false,
        published_on_collings: false,
      }));
      return;
    }
    const allChanges = {
      internal_marketing: true,
      // published_on_website NOT included — On-Market never goes to Buyer Portal
      [primaryPortal]: true,
      published_on_domain: true,
      published_on_collings: true,
    };
    setPendingVisibilityChanges(allChanges);
    setShowVisibilityConfirm(true);
  };

  const searchOwnerContacts = (value) => {
    clearTimeout(ownerSearchTimer.current);
    setOwnerSearchLoading(true);
    setShowOwnerDropdown(true);
    ownerSearchTimer.current = setTimeout(async () => {
      try {
        const params = value ? { search: value } : {};
        const res = await getContacts(params);
        const contacts = res?.data?.results || res?.data || [];
        const real = contacts.filter(c => c.id && !String(c.id).startsWith('buyer_') && !String(c.id).startsWith('enquiry_'));
        setOwnerSuggestions(real.slice(0, 20));
        setShowOwnerDropdown(real.length > 0);
      } catch {
        setOwnerSuggestions([]);
        setShowOwnerDropdown(false);
      } finally {
        setOwnerSearchLoading(false);
      }
    }, value ? 150 : 0);
  };

  const selectOwnerContact = (contact) => {
    setForm(prev => ({
      ...prev,
      agent: contact.name || "",
      // Carry across the "goes by" name already stored on the CRM contact.
      owner_preferred_name: contact.preferred_name || prev.owner_preferred_name || "",
      contact: contact.phone || "",
      email: contact.email || "",
    }));
    setShowOwnerDropdown(false);
    setOwnerSuggestions([]);
  };

  const addOwner = () => {
    setForm(prev => ({
      ...prev,
      additionalOwners: [...prev.additionalOwners, { name: "", preferred_name: "", contact: "", email: "" }]
    }));
  };

  const removeOwner = (index) => {
    setForm(prev => ({
      ...prev,
      additionalOwners: prev.additionalOwners.filter((_, i) => i !== index)
    }));
  };

  const handleAdditionalOwnerChange = (index, field, value) => {
    setForm(prev => {
      const updatedOwners = [...prev.additionalOwners];
      updatedOwners[index] = { ...updatedOwners[index], [field]: value };
      return { ...prev, additionalOwners: updatedOwners };
    });
  };

  // AI Assistant Questionnaire handlers — agent-curated Q&A pairs fed to the Buyer Portal AI assistant.
  const handleQuestionnaireChange = (index, field, value) => {
    setForm(prev => {
      const updated = [...(prev.ai_questionnaire || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, ai_questionnaire: updated };
    });
  };

  const addQuestionnaireItem = () => {
    setForm(prev => ({
      ...prev,
      ai_questionnaire: [...(prev.ai_questionnaire || []), { question: "", answer: "" }],
    }));
  };

  const removeQuestionnaireItem = (index) => {
    setForm(prev => ({
      ...prev,
      ai_questionnaire: (prev.ai_questionnaire || []).filter((_, i) => i !== index),
    }));
  };

  const addParking = () => {
    setForm(prev => ({
      ...prev,
      parking_list: [...prev.parking_list, { type: "", spaces: "" }]
    }));
  };

  const removeParking = (index) => {
    setForm(prev => ({
      ...prev,
      parking_list: prev.parking_list.filter((_, i) => i !== index)
    }));
  };

  const handleParkingChange = (index, field, value) => {
    setForm(prev => {
      const updatedList = [...prev.parking_list];
      updatedList[index] = { ...updatedList[index], [field]: value };
      return { ...prev, parking_list: updatedList };
    });
  };

  // Feature list handlers - DEPRECATED (kept for backward compatibility)
  const addFeature = () => {
    if (form.featureInput?.trim()) {
      setForm(prev => {
        const currentFeatures = prev.featuresList || [];
        const newFeatures = [...currentFeatures, prev.featureInput.trim()];
        return {
          ...prev,
          featuresList: newFeatures,
          featureInput: "",
          features: newFeatures.join(', ')
        };
      });
    }
  };

  const removeFeature = (index) => {
    setForm(prev => {
      const currentFeatures = prev.featuresList || [];
      const updatedFeatures = currentFeatures.filter((_, i) => i !== index);
      return {
        ...prev,
        featuresList: updatedFeatures,
        features: updatedFeatures.join(', ')
      };
    });
  };

  // Internal Features handlers
  const addInternalFeature = () => {
    if (form.internalFeatureInput?.trim()) {
      setForm(prev => {
        const currentFeatures = prev.internalFeaturesList || [];
        const newFeatures = [...currentFeatures, prev.internalFeatureInput.trim()];
        return {
          ...prev,
          internalFeaturesList: newFeatures,
          internalFeatureInput: "",
          internalFeatures: newFeatures.join(', ')
        };
      });
    }
  };

  const removeInternalFeature = (index) => {
    setForm(prev => {
      const currentFeatures = prev.internalFeaturesList || [];
      const updatedFeatures = currentFeatures.filter((_, i) => i !== index);
      return {
        ...prev,
        internalFeaturesList: updatedFeatures,
        internalFeatures: updatedFeatures.join(', ')
      };
    });
  };

  // External Features handlers
  const addExternalFeature = () => {
    if (form.externalFeatureInput?.trim()) {
      setForm(prev => {
        const currentFeatures = prev.externalFeaturesList || [];
        const newFeatures = [...currentFeatures, prev.externalFeatureInput.trim()];
        return {
          ...prev,
          externalFeaturesList: newFeatures,
          externalFeatureInput: "",
          externalFeatures: newFeatures.join(', ')
        };
      });
    }
  };

  const removeExternalFeature = (index) => {
    setForm(prev => {
      const currentFeatures = prev.externalFeaturesList || [];
      const updatedFeatures = currentFeatures.filter((_, i) => i !== index);
      return {
        ...prev,
        externalFeaturesList: updatedFeatures,
        externalFeatures: updatedFeatures.join(', ')
      };
    });
  };

  const handleInternalFeatureInputChange = (value) => {
    setForm(prev => ({
      ...prev,
      internalFeatureInput: value
    }));
  };

  const handleExternalFeatureInputChange = (value) => {
    setForm(prev => ({
      ...prev,
      externalFeatureInput: value
    }));
  };

  const handleInternalFeatureInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addInternalFeature();
    }
  };

  const handleExternalFeatureInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addExternalFeature();
    }
  };

  const handleFeatureInputChange = (value) => {
    setForm(prev => ({
      ...prev,
      featureInput: value
    }));
  };

  const handleFeatureInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addFeature();
    }
  };

  // Unified file input handler for images and videos
  const handleImageChange = (e) => {
    // Name any file the portals can't take at pick time - the backend
    // refuses them with the same wording (Darebin webp incident, 1 Oct 2026).
    const { ok: files, rejected } = splitSupportedUploads(Array.from(e.target.files));
    if (rejected.length) toast.error(rejectedUploadsMessage(rejected));
    if (files?.length > 0) {
      const MAX_IMAGES = 35; // REA's per-listing maximum; matches the backend cap
      const currentImageCount = imagePreviews?.length || form?.images?.length || 0;
      const totalAfterAdd = currentImageCount + files.length;

      if (totalAfterAdd > MAX_IMAGES) {
        const allowedCount = MAX_IMAGES - currentImageCount;
        if (allowedCount > 0) {
          // Only add the allowed number of images
          const filesToAdd = files.slice(0, allowedCount);
          if (imagePreviews?.length > 0) {
            setForm((prev) => ({ ...prev, images: [...prev?.images, ...filesToAdd] }));
          } else {
            setForm((prev) => ({ ...prev, images: [...filesToAdd] }));
          }
          const previews = filesToAdd.map((file) => URL.createObjectURL(file));
          setImagePreviews((prev) => [...prev, ...previews]);
          setFloorplanFlags((prev) => [...prev, ...filesToAdd.map(() => false)]);
          toast.error(`Maximum ${MAX_IMAGES} images allowed. Only ${allowedCount} image(s) added.`, {
            duration: 4000
          });
        } else {
          toast.error(`Maximum ${MAX_IMAGES} images allowed. Please remove some images before adding more.`, {
            duration: 4000
          });
        }
        // Clear the input
        e.target.value = '';
        return;
      }

      if (imagePreviews?.length > 0) {
        setForm((prev) => ({ ...prev, images: [...prev?.images, ...files] }));
      } else {
        setForm((prev) => ({ ...prev, images: [...files] }));
      }
      const previews = files.map((file) => URL.createObjectURL(file));
      setImagePreviews((prev) => [...prev, ...previews]);
      setFloorplanFlags((prev) => [...prev, ...files.map(() => false)]);
      if (errors.images) {
        setErrors((prev) => ({ ...prev, images: undefined }));
      }
    }
  };

  // Drag and drop handlers for image reordering
  const handleDragStart = (e, index) => {
    setDraggedImageIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedImageIndex === null || draggedImageIndex === dropIndex) {
      return;
    }

    // Reorder images array
    const newImages = [...form.images];
    const draggedImage = newImages[draggedImageIndex];
    newImages.splice(draggedImageIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);

    // Reorder preview array
    const newPreviews = [...imagePreviews];
    const draggedPreview = newPreviews[draggedImageIndex];
    newPreviews.splice(draggedImageIndex, 1);
    newPreviews.splice(dropIndex, 0, draggedPreview);

    // Reorder floorplan flags array
    const newFlags = [...floorplanFlags];
    const draggedFlag = newFlags[draggedImageIndex];
    newFlags.splice(draggedImageIndex, 1);
    newFlags.splice(dropIndex, 0, draggedFlag);

    setForm((prev) => ({ ...prev, images: newImages }));
    setImagePreviews(newPreviews);
    setFloorplanFlags(newFlags);
    setDraggedImageIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedImageIndex(null);
  };

  // Document name handlers
  const getDocumentKey = (file, type, index) => {
    return `${type}-${index}-${file.name}`;
  };

  const getDocumentName = (file, type, index) => {
    const key = getDocumentKey(file, type, index);
    return documentNames[key] || file.name.replace(/\.[^/.]+$/, ""); // Remove extension for display
  };

  const handleDocumentNameChange = (file, type, index, newName) => {
    const key = getDocumentKey(file, type, index);
    setDocumentNames(prev => ({
      ...prev,
      [key]: newName
    }));
  };

  // REMOVE handleVideoChange

  // Verify the browser can actually read each newly-added image off disk before we
  // build the upload. When a selected file has been moved/renamed/deleted, or lives in
  // a cloud "online-only" folder (OneDrive/iCloud/Google Drive), the multipart POST
  // dies with net::ERR_FILE_NOT_FOUND — which axios surfaces as a generic "Network
  // Error". Reading one byte here lets us catch it and name the offending file instead.
  const findUnreadableImages = async (images = []) => {
    const unreadable = [];
    for (const item of images) {
      // Strings are already-uploaded existing images (URLs) — nothing to read.
      if (!(item instanceof File)) continue;
      try {
        await item.slice(0, 1).arrayBuffer();
      } catch {
        unreadable.push(item.name || "image");
      }
    }
    return unreadable;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Guard against duplicate creates: if a submit is already in flight, ignore
    // repeat invocations (e.g. a rapid second click) so the same listing can't
    // be POSTed twice. The primary guard is that this handler is now wired only
    // to the form's onSubmit — the button's redundant onClick was removed, so a
    // single click dispatches this once instead of twice.
    if (isSubmitting) return;
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      const firstMessage = scrollToFirstError(validationErrors);
      toast.error(firstMessage || "Please fix the highlighted fields before continuing.");
      return;
    }

    // Guard: make sure every newly-added photo is still readable from disk before we
    // fire the request. If not, stop here with a clear message rather than letting the
    // upload fail with a cryptic net::ERR_FILE_NOT_FOUND / "Network Error".
    const unreadableImages = await findUnreadableImages(form.images);
    if (unreadableImages.length > 0) {
      const shown = unreadableImages.slice(0, 3).join(", ");
      const more =
        unreadableImages.length > 3 ? ` and ${unreadableImages.length - 3} more` : "";
      const isOne = unreadableImages.length === 1;
      const msg =
        `Couldn't read ${isOne ? "this photo" : "these photos"}: ${shown}${more}. ` +
        `The file may have been moved, renamed, or is stored online-only ` +
        `(e.g. OneDrive/iCloud). Please remove and re-add your photos from a local ` +
        `folder on this computer, then try again.`;
      setErrors((prev) => ({ ...prev, images: msg }));
      scrollToFirstError({ images: msg });
      toast.error(msg, { duration: 9000 });
      return;
    }

    // Live profile already at this address? Ask once before making another.
    // A fresh lookup, not the on-screen one, so a quick type-and-save is caught.
    const currentTwinKey = twinKey(form.address, form.suburb);
    const twinsAccepted = !isEdit && twinsAcceptedForRef.current === currentTwinKey;
    if (!isEdit && !twinsAccepted) {
      const twins = await lookupAddressTwins(form.address, form.suburb);
      if (twins.length > 0) {
        setAddressTwins(twins);
        setShowTwinConfirm(true);
        return;
      }
      // No twin, but the same building without (or with) a unit number:
      // ask once whether it is the same property.
      if (buildingTwinsRef.current.length > 0 && buildingAcceptedForRef.current !== currentTwinKey) {
        setShowBuildingConfirm(true);
        return;
      }
    }

    const isLease = form.transaction_type === "lease";
    // Appraisals are saved before price and commission are agreed, so both stay optional here.
    const isAppraisal = form.status === "Appraisal";

    // Map frontend property types to backend broad categories
    const mapPropertyTypeToCategory = (propertyType) => {
      const mapping = {
        'Apartment': 'apartment',
        'Unit': 'apartment',
        'House': 'house',
        'Townhouse': 'townhouse',
        'Villa': 'house',
        'House and Land': 'house',
        'Land': 'land',
        'Block of Units': 'apartment'
      };
      return mapping[propertyType] || 'house';
    };

    const formData = new FormData();
    // Auto-generate title if not provided (since title field was removed from UI)
    const title = form.title?.trim() || `${form.propertyTypes || 'Property'} at ${form.address || 'Address'}`;
    formData.append("title", title);
    formData.append("address", form.address);
    formData.append("street", form.street || "");
    formData.append("suburb", form.suburb);
    formData.append("city", form.city || form.suburb);
    formData.append("state", form.state);
    formData.append("postal_code", form.postal_code);
    formData.append("address_publish_mode", form.address_publish_mode || "full");

    // Price - only for sale properties, not for lease
    if (!isLease) {
      // For contact agent and auction, if no price is set, send null/empty
      // Price should be cleared when switching from exact to contact/auction
      if ((isAppraisal || form.price_display === 'contact' || form.price_display === 'auction') && !form.price?.trim()) {
        formData.append("price", "");
      } else {
        // Extract numeric value from formatted price string
        const priceValue = form.price ? form.price.replace(/[$,]/g, "") : "";
        // Ensure it's a valid number, default to 0 if invalid
        const numericPrice = priceValue ? parseFloat(priceValue) : 0;
        formData.append("price", numericPrice.toString());
      }
    } else {
      // For lease properties, send a nominal price of 0 or skip it entirely
      // Backend should handle lease properties differently
      formData.append("price", "0");
    }

    formData.append("beds", form.beds !== "" ? form.beds : 0);
    formData.append("baths", form.baths !== "" ? form.baths : 0);

    // Distribute parking entries into the 3 backend fields by type.
    // Multiple entries of the same type are summed.
    const parkingTotals = form.parking_list.reduce(
      (acc, item) => {
        const count = parseInt(item.spaces) || 0;
        if (item.type === "garage") acc.garage += count;
        else if (item.type === "carport") acc.carport += count;
        else if (item.type === "open") acc.open += count;
        return acc;
      },
      { garage: 0, carport: 0, open: 0 }
    );
    formData.append("parkings", parkingTotals.garage);
    formData.append("car_ports", parkingTotals.carport);
    formData.append("open_spaces", parkingTotals.open);

    // Clear the legacy single-slot fields so the card sum doesn't double-count.
    formData.append("parking_type", "");
    formData.append("parking_spaces", 0);
    formData.append("description", form.description);
    formData.append("features", form.features);  // Keep for backward compatibility
    formData.append("internal_features", form.internalFeatures || "");
    formData.append("external_features", form.externalFeatures || "");
    formData.append("status", form.status);
    // Appraisals can be saved without images — tell the backend to skip its image-required check.
    if (form.status === "Appraisal") {
      formData.append("skip_image_validation", "true");
    }
    formData.append("owner_name", form?.agent || "");
    // Always sent so clearing the field also clears it on the property.
    formData.append("owner_preferred_name", form?.owner_preferred_name || "");
    formData.append("contact", form?.contact || "");
    // Optional second mobile / email for the same owner (CRM-2026-00044). Always
    // sent so clearing the field also clears it on the property.
    formData.append("contact_secondary", form?.contact_secondary || "");
    formData.append("email_secondary", form?.email_secondary || "");
    // Map specific property type to backend category
    formData.append("property_type", mapPropertyTypeToCategory(form.propertyTypes));
    formData.append("owner_visibilty", form.publicShare);
    formData.append("email", form.email);
    if (form.additionalOwners && form.additionalOwners.length > 0) {
      formData.append("additional_owners", JSON.stringify(form.additionalOwners));
    }

    // Method of Sale (CRITICAL for Buyer Portal filtering)
    formData.append("method_of_sale", form.method_of_sale || "Private Sale");

    // Transaction type - always send explicitly to ensure updates work
    const transactionType = form.transaction_type || "sale";
    formData.append("transaction_type", transactionType);

    // Weekly rent for lease and sale properties (optional for sale). Always send so clearing the field removes it on the backend.
    // For commercial: the form stores annual rent — convert to weekly (÷ 52) before sending
    const isCommercial = form.property_class === "Commercial";
    if (form.weekly_rent && form.weekly_rent.trim()) {
      const rentValue = form.weekly_rent.replace(/[$,]/g, "");
      const numericRent = rentValue ? parseFloat(rentValue) : 0;
      const weeklyRentToSend = isCommercial ? numericRent / 52 : numericRent;
      formData.append("weekly_rent", weeklyRentToSend.toFixed(2));
    } else {
      formData.append("weekly_rent", "");
    }

    // Bond (lease properties). Always send so clearing the field removes it on the backend.
    if (form.bond && form.bond.trim()) {
      const bondValue = form.bond.replace(/[$,]/g, "");
      const numericBond = bondValue ? parseFloat(bondValue) : 0;
      formData.append("bond", numericBond.toFixed(2));
    } else {
      formData.append("bond", "");
    }

    // Available-from date (lease properties). Only send when set so an empty date clears it.
    formData.append("available_date", form.available_date || "");

    // Ignite application link (lease properties). This is what the rental
    // enquiry auto-reply puts behind its "Apply for this property" button;
    // leaving it blank drops the button and asks the renter to reply instead.
    formData.append("rental_application_url", (form.rental_application_url || "").trim());

    // Furnished (residential lease properties). Sent to realestate.com.au as
    // the listing's furnished flag; the backend only uses it on rentals.
    formData.append("furnished", form.furnished ? "true" : "false");

    // Price display mode
    formData.append("price_display", form.price_display || "exact");

    // Price to (for price ranges) - always send when range is selected, send empty string to clear if not range
    if (form.price_display === "range") {
      if (form.price_to) {
        // Extract numeric value from formatted price_to string
        const priceToValue = form.price_to.replace(/[$,]/g, "");
        const numericPriceTo = priceToValue ? parseFloat(priceToValue) : 0;
        formData.append("price_to", numericPriceTo.toString());
      } else {
        // Send empty string to clear price_to in backend when switching to range without value
        formData.append("price_to", "");
      }
    } else {
      // Clear price_to when not using range
      formData.append("price_to", "");
    }

    // Price display text (freeform) — always send so clearing the field removes it on the backend.
    // When set, the Buyer Portal uses this verbatim instead of the price range / asking price.
    formData.append("price_display_text", (form.price_display_text || "").trim());

    // Auction details
    if (form.auction_date) {
      // form.auction_date is a native <input type="datetime-local"> value -
      // a bare "YYYY-MM-DDTHH:MM" with no timezone. Django's TIME_ZONE is UTC,
      // so sending that string as-is gets a Melbourne wall-clock time (e.g.
      // "12:30pm") stored as 12:30pm UTC - 10-11 hours off from the real
      // instant (confirmed live, 2026-08-14: 8/118 Miller Street's auction
      // showed a different time on the CRM than on the live REA listing for
      // this exact reason). Convert it to the correct UTC instant first.
      const auctionUtc = datetimeLocalToUtcISOString(form.auction_date);
      if (auctionUtc) formData.append("auction_date", auctionUtc);
    }
    if (form.auction_venue) {
      formData.append("auction_venue", form.auction_venue);
    }

    // Publishing status - On-market visibility only (Agent Point, Development Ready, Inspect Real Estate, RateMyAgent, RealTimeAgent removed; Collings.com.au added)
    formData.append("published_on_website", form.published_on_website ? "true" : "false");
    // Always send portal_category (even if empty) so it can be cleared on update
    formData.append("portal_category", form.portal_category || "");
    formData.append("published_on_rea", form.published_on_rea ? "true" : "false");
    formData.append("published_on_realcommercial", form.published_on_realcommercial ? "true" : "false");
    formData.append("published_on_domain", form.published_on_domain ? "true" : "false");
    formData.append("published_on_collings", form.published_on_collings ? "true" : "false");
    formData.append("internal_marketing", form.internal_marketing ? "true" : "false");
    // Scheduled launch date/time — applies to all platforms (Buyer Portal filters by it; future Celery task will push to REA/Domain on this date)
    if (form.listing_launch_at) {
      formData.append("listing_launch_at", datetimeLocalToUtcISOString(form.listing_launch_at));
    } else {
      formData.append("listing_launch_at", "");  // Clear the field if removed
    }
    if (form.appraisal_market_type) {
      formData.append("appraisal_market_type", form.appraisal_market_type);
    }
    // Manual appraisal origin (CRM-2026 Phase 2): only sent for a from-scratch
    // appraisal. Lead-based appraisals inherit origin server-side, so it's omitted.
    if (form.status === "Appraisal" && !lead_id && form.origin_reason) {
      formData.append("origin_reason", form.origin_reason);
    }
    if (form.appraisal_temperature) {
      formData.append("appraisal_temperature", form.appraisal_temperature);
    }
    if (form.appraisal_reminder_date) {
      formData.append("appraisal_reminder_date", new Date(form.appraisal_reminder_date).toISOString());
    }
    if (form.appraisal_reminder_reason) {
      formData.append("appraisal_reminder_reason", form.appraisal_reminder_reason);
    }
    // The whole call-back series, one reminder per entry on the backend.
    if (Array.isArray(form.appraisal_reminders) && form.appraisal_reminders.length > 0) {
      formData.append(
        "appraisal_reminders",
        JSON.stringify(
          form.appraisal_reminders.map((entry) => ({
            date: new Date(entry.date).toISOString(),
            reason: entry.reason,
          }))
        )
      );
    }
    // First note on the appraisal. Saved as a PropertyNote, so it shows on the
    // pipeline card and in the property's Notes tab.
    if (form.appraisal_note && form.appraisal_note.trim()) {
      formData.append("appraisal_note", form.appraisal_note.trim());
    }

    // AI Assistant Questionnaire — JSON-stringified list of {question, answer} pairs.
    // Backend strips entries with empty question or answer; always send so updates can clear cleared rows.
    const questionnairePayload = (form.ai_questionnaire || [])
      .map((qa) => ({
        question: String(qa?.question || "").trim(),
        answer: String(qa?.answer || "").trim(),
      }))
      .filter((qa) => qa.question && qa.answer);
    formData.append("ai_questionnaire", JSON.stringify(questionnairePayload));

    // When creating a listing for other agents (the creator isn't one of the
    // assigned agents), the questionnaire wasn't shown/filled here — tell the
    // backend to email the questions to the primary agent to complete.
    if (!isEdit && !isCreatorAssignedAgent && effectiveListingType !== "appraisal") {
      const questionStrings = (form.ai_questionnaire || [])
        .map((qa) => String(qa?.question || "").trim())
        .filter(Boolean);
      formData.append("notify_agents_questionnaire", "true");
      formData.append(
        "questionnaire_questions",
        JSON.stringify(questionStrings.length ? questionStrings : DEFAULT_AI_QUESTIONS)
      );
    }

    // Additional property details
    // IMPORTANT: "area" is required by backend - use floor_area as the primary display area
    const areaValue = form.floor_area || form.building_size || form.land_area || form.land_size || "0";
    formData.append("area", areaValue);

    if (form.land_area || form.land_size) {
      formData.append("land_size", form.land_area || form.land_size);
    }
    if (form.floor_area || form.building_size) {
      formData.append("building_size", form.floor_area || form.building_size);
    }
    if (form.year_built) {
      formData.append("year_built", form.year_built);
    }
    if (form.video_url) {
      formData.append("video_url", form.video_url);
    }
    if (form.virtual_tour_link || form.virtual_tour) {
      formData.append("virtual_tour_link", form.virtual_tour_link || form.virtual_tour);
    }
    // Commission fields
    const commissionType = form.commission_type || "percentage";
    formData.append("commission_type", commissionType);

    if (commissionType === "fixed") {
      // For fixed commission, commission_amount is required for SALE (not appraisals)
      if (!isLease && !isAppraisal && (!form.commission_amount || form.commission_amount === "")) {
        throw new Error("Commission amount is required when commission type is 'fixed'");
      }
      if (form.commission_amount) {
        formData.append("commission_amount", form.commission_amount.replace(/[$,]/g, ""));
      }
      // Clear commission_rate for fixed type
      if (form.commission_rate) {
        formData.append("commission_rate", ""); // Explicitly clear it
      }
    } else {
      // For percentage commission, commission_rate is required for SALE (not appraisals)
      if (!isLease && !isAppraisal && (!form.commission_rate || form.commission_rate === "")) {
        throw new Error("Commission rate is required when commission type is 'percentage'");
      }
      if (form.commission_rate) {
        formData.append("commission_rate", form.commission_rate);
      }
      // Clear commission_amount for percentage type
      if (form.commission_amount) {
        formData.append("commission_amount", ""); // Explicitly clear it
      }
    }
    formData.append("commission_gst", form.commission_gst || "inclusive");

    // Letting Fee fields (lease listings only) — mirrors commission handling
    if (isLease) {
      const lettingFeeType = form.letting_fee_type || "percentage";
      formData.append("letting_fee_type", lettingFeeType);
      if (lettingFeeType === "fixed") {
        if (form.letting_fee_amount) {
          formData.append("letting_fee_amount", form.letting_fee_amount.replace(/[$,]/g, ""));
        }
        if (form.letting_fee_rate) {
          formData.append("letting_fee_rate", ""); // Explicitly clear it
        }
      } else {
        if (form.letting_fee_rate) {
          formData.append("letting_fee_rate", form.letting_fee_rate);
        }
        if (form.letting_fee_amount) {
          formData.append("letting_fee_amount", ""); // Explicitly clear it
        }
      }
      formData.append("letting_fee_gst", form.letting_fee_gst || "inclusive");
    }

    // Yield & Financial Details
    if (form.est_gross_yield) {
      formData.append("est_gross_yield", form.est_gross_yield);
    }
    if (form.est_outgoings) {
      formData.append("est_outgoings", form.est_outgoings);
    }
    if (form.est_net_yield) {
      formData.append("est_net_yield", form.est_net_yield);
    }
    // Annual Outgoings Breakdown
    if (isCommercial) {
      // Commercial: no body corporate; council/water/insurance/land_tax/other are OpEx line items
      formData.append("body_corporate", "0");
      // Send commercial OpEx line items (council_rates and water_rates also used for commercial)
      formData.append("council_rates", form.council_rates || "0");
      formData.append("water_rates", form.water_rates || "0");
      formData.append("insurance", form.insurance || "0");
      formData.append("land_tax", form.land_tax || "0");
      formData.append("other_opex", form.other_opex || "0");
      formData.append("tenant_pays_opex", form.tenant_pays_opex ? "true" : "false");
    } else {
      if (form.has_body_corporate && form.body_corporate) {
        formData.append("body_corporate", form.body_corporate);
      } else {
        formData.append("body_corporate", "0");
      }
      if (form.council_rates) {
        formData.append("council_rates", form.council_rates);
      }
      if (form.water_rates) {
        formData.append("water_rates", form.water_rates);
      }
    }
    if (form.current_annual_property_management_fee) {
      formData.append("current_annual_property_management_fee", form.current_annual_property_management_fee);
    }
    // Management-fee opt-in (residential only). Sent every time so the backend can clear a
    // previously-stored fee when the agent unticks it. Commercial always carries its own fee
    // (sent above), so the flag is omitted there to avoid wiping it.
    if (!isCommercial) {
      formData.append("include_management_fee", form.include_management_fee ? "true" : "false");
    }
    // Always send property_class - sync with propertyTypes if not set
    const propertyClassToSend = form.property_class || form.propertyTypes || "";
    if (propertyClassToSend) {
      formData.append("property_class", propertyClassToSend);
    }
    // realcommercial.com.au category for commercial listings. Sent even when
    // blank so clearing it on an edit is honoured by the backend.
    if (isCommercial) {
      formData.append("commercial_category", form.commercial_category || "");
      formData.append("commercial_category_2", form.commercial_category_2 || "");
      formData.append("commercial_category_3", form.commercial_category_3 || "");
    }

    if (lead_id) {
      formData.append("lead_id", lead_id);
    }
    // Agent assignment - always send agent field to prevent backend default-to-current-user behavior
    // For new properties: if no agent selected, backend will use current user
    // For edits: if agent is set, send it; if cleared, send empty to preserve existing (backend handles this)
    formData.append("agent", form.agentId || "");
    formData.append("co_agent", form.secondaryAgent || "");
    formData.append("tertiary_agent", form.tertiaryAgent || "");

    // Add delete_images for edit mode
    if (isEdit && deleteImages.length > 0) {
      formData.append("delete_images", JSON.stringify(deleteImages));
    }

    // Build floorplan payload. form.images is a parallel array to floorplanFlags:
    //   - existing items are URL strings — collect their URLs if flagged
    //   - new items are File objects — append the file and its flag in order
    const newFloorplanFlags = [];
    const existingFloorplanUrls = [];
    // image_order: parallel to the appended "images" entries — string URL for an existing
    // image (matched by base URL), null for a new uploaded file (consumed in upload order).
    const imageOrder = [];
    form.images?.forEach((file, idx) => {
      formData.append("images", file);
      const flag = !!floorplanFlags[idx];
      if (file instanceof File) {
        newFloorplanFlags.push(flag);
        imageOrder.push(null);
      } else if (typeof file === "string") {
        if (flag) existingFloorplanUrls.push(file);
        imageOrder.push(file.split("?")[0]);
      }
    });
    formData.append("floorplan_flags", JSON.stringify(newFloorplanFlags));
    if (isEdit) {
      formData.append("floorplan_existing", JSON.stringify(existingFloorplanUrls));
      formData.append("image_order", JSON.stringify(imageOrder));
    }
    // The agent already chose "Save anyway" on the twin warning, so the
    // backend's publish guard must not ask the same question again.
    if (twinsAccepted) {
      formData.append("allow_duplicate", "true");
    }

    const submissionData = {
      ...form,
      title: title,
      deleteImages: deleteImages
    };

    // Appraisal: never goes to Buyer Portal. Skip confirmation.
    if (effectiveListingType === "appraisal") {
      await proceedWithSubmission(false, {
        formData,
        submissionData,
        isBuyerPortalChecked: false,
      });
      return;
    }

    // On-market: respect the published_on_website checkbox the user selected.
    if (effectiveListingType === "onmarket") {
      await proceedWithSubmission(null, {
        formData,
        submissionData,
        isBuyerPortalChecked: form.published_on_website,
      });
      return;
    }

    // Off-Market: respect the published_on_website checkbox the user selected.
    // No confirmation dialog needed — buyer portal choice is explicit on the form.
    if (effectiveListingType === "offmarket") {
      await proceedWithSubmission(null, {
        formData,
        submissionData,
        isBuyerPortalChecked: form.published_on_website,
      });
      return;
    }
  };

  const proceedWithSubmission = async (publishToBuyerPortal, overridePending) => {
    const payload = overridePending;
    if (!payload) return;
    const { formData, submissionData } = payload;


    // Update buyer portal flag in formData based on user's confirmation choice
    if (publishToBuyerPortal === true && !submissionData.published_on_website) {
      formData.set("published_on_website", "true");
      submissionData.published_on_website = true;
    } else if (publishToBuyerPortal === false) {
      formData.set("published_on_website", "false");
      submissionData.published_on_website = false;
    }

    setIsSubmitting(true);

    // "Add to this profile": merge into the live profile already at this
    // address instead of creating a second one (CRM-2026-00081).
    if (mergeTargetRef.current && !isEdit) {
      const targetId = mergeTargetRef.current;
      if (sellerLeadId) formData.set("seller_lead_id", sellerLeadId);
      try {
        const res = await mergeIntoProperty(targetId, formData);
        setErrors({});
        toast.success(res?.data?.detail || "Added to the existing profile.", { duration: 7000 });
        if (isSuccess) isSuccess(targetId, { merged: true });
        if (isModal && onClose) onClose();
      } catch (error) {
        toast.error(error?.response?.data?.detail || "Couldn't add this to the existing profile.");
      } finally {
        mergeTargetRef.current = null;
        setIsSubmitting(false);
      }
      return;
    }

    // If custom onSubmit is provided (edit mode / Collings), use it
    if (onSubmit) {
      try {
        await onSubmit(submissionData, formData);
        setErrors({});
        if (isSuccess) isSuccess();
      } catch (error) {
        const isNetworkFailure =
          !error?.response &&
          (error?.code === "ERR_NETWORK" ||
            error?.code === "ERR_FILE_NOT_FOUND" ||
            error?.message === "Network Error");
        if (isNetworkFailure) {
          const msg =
            "Upload failed before reaching the server. This is usually a photo that " +
            "couldn't be read — it may have been moved, renamed, or stored online-only " +
            "(OneDrive/iCloud). Please re-add your photos from a local folder and try " +
            "again. If your photos are very large, try fewer or smaller images.";
          setErrors((prev) => ({ ...prev, images: msg }));
          scrollToFirstError({ images: msg });
          toast.error(msg, { duration: 9000 });
        } else {
          toast.error(
            error?.response?.data?.detail || "Failed to update property."
          );
        }
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    try {
      let res;
      try {
        res = await registerProperty(formData);
      } catch (error) {
        // One profile per property: the backend refuses a second live profile
        // of the same kind. Show the same choice the twin check offers.
        if (error?.response?.status === 409 && error?.response?.data?.code === "live_profile_exists") {
          twinsAcceptedForRef.current = null;
          setAddressTwins(error.response.data.matches || []);
          setShowTwinConfirm(true);
          return;
        }
        // Duplicate-address guard: the backend answers 409 when this address
        // already has a PUBLISHED listing (portal or collings.com.au), so the
        // portal doesn't end up showing the same house twice. Let the agent
        // confirm an intentional duplicate and retry with the override flag.
        if (error?.response?.status === 409 && error?.response?.data?.code === "duplicate_address") {
          const dup = error.response.data;
          const confirmed = window.confirm(
            `A published property already exists at this address:\n` +
            `"${dup.duplicate_address}" (ID ${dup.duplicate_property_id}).\n\n` +
            `Creating this one will show the listing twice on the portal/website.\n` +
            `Create it anyway?`
          );
          if (!confirmed) {
            toast.error("Property not created — a published listing already exists at this address.", { duration: 7000 });
            return;
          }
          formData.append("allow_duplicate", "true");
          res = await registerProperty(formData);
        } else {
          throw error;
        }
      }

      console.log(res);
      const newId = extractCreatedPropertyId(res);
      setErrors({});
      if (newId) {
        setCreatedPropertyId(newId);
        // Fire the "claim your property" invite(s) if the agent opted in. Wizard
        // mode drops the owner into the pre-filled Add-Property portfolio wizard.
        // Best-effort: a failed send must never block the save from finishing.
        if (sendClaimInvite) {
          const owners = [
            { email: form.email, name: form.agent, contact: form.contact },
            ...(form.additionalOwners || []).map((o) => ({
              email: o.email, name: o.name, contact: o.contact,
            })),
          ];
          const seen = new Set();
          const recipients = owners.filter((o) => {
            const em = (o.email || "").trim().toLowerCase();
            if (!em || !em.includes("@") || seen.has(em)) return false;
            seen.add(em);
            return true;
          });
          const results = await Promise.allSettled(
            recipients.map((o) => {
              const [first, ...rest] = (o.name || "").trim().split(/\s+/);
              return sendPropertyClaimInvite(newId, {
                email: (o.email || "").trim(),
                first_name: first || "",
                last_name: rest.join(" "),
                mobile_number: o.contact || "",
                completion_mode: "wizard",
              });
            })
          );
          const sent = results.filter((r) => r.status === "fulfilled").length;
          if (sent > 0) {
            toast.success(
              sent === 1
                ? "Invitation to claim the property has been sent to the owner."
                : `Claim invitations sent to ${sent} owners.`
            );
          } else if (recipients.length === 0) {
            toast.error("Add an owner email to send a claim invitation.");
          } else {
            toast.error("Couldn't send the claim invitation. You can re-send it from the property page.");
          }
        }
        if (effectiveListingType === "appraisal") {
          // Appraisals don't require documents — finish without the documents step.
          toast.success("Appraisal added successfully!");
          // Pass the new id so callers can link it back to whatever started the
          // flow (e.g. the Database record being converted to an appraisal).
          if (isSuccess) isSuccess(newId);
          if (onClose) onClose();
        } else {
          toast.success("Property registered successfully!");
          setStep(2);
        }
      } else {
        toast.error(
          "Could not determine created property ID. Please try again."
        );
      }
    } catch (error) {
      // Enhanced error handling to show detailed validation errors
      let errorMessage = "Failed to register property.";

      if (error?.response?.data) {
        const errorData = error.response.data;

        // Check for detail message
        if (errorData.detail) {
          errorMessage = errorData.detail;
        }
        // Check for field-specific validation errors
        else if (typeof errorData === 'object') {
          const fieldErrors = [];
          Object.keys(errorData).forEach(field => {
            const fieldError = Array.isArray(errorData[field])
              ? errorData[field][0]
              : errorData[field];
            const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            fieldErrors.push(`${fieldName}: ${fieldError}`);
          });

          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join('; ');
            // Also set field-specific errors in form
            const fieldErrorMap = {};
            Object.keys(errorData).forEach(field => {
              const fieldError = Array.isArray(errorData[field])
                ? errorData[field][0]
                : errorData[field];
              fieldErrorMap[field] = fieldError;
            });
            setErrors(fieldErrorMap);
          }
        }
      } else if (
        !error?.response &&
        (error?.code === "ERR_NETWORK" ||
          error?.code === "ERR_FILE_NOT_FOUND" ||
          error?.message === "Network Error")
      ) {
        // No HTTP response at all — the request never reached the server. The usual
        // cause is a photo that couldn't be read at send time (moved/renamed or stored
        // online-only), which the browser reports as net::ERR_FILE_NOT_FOUND.
        errorMessage =
          "Upload failed before reaching the server. This is usually a photo that " +
          "couldn't be read — it may have been moved, renamed, or stored online-only " +
          "(OneDrive/iCloud). Please re-add your photos from a local folder and try " +
          "again. If your photos are very large, try fewer or smaller images.";
        setErrors((prev) => ({ ...prev, images: errorMessage }));
        scrollToFirstError({ images: errorMessage });
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, { duration: 9000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateAI = async () => {
    // Basic validation to ensure we have some data to work with
    if (!form.address) {
      toast.error("Please provide the property address to generate description.");
      return;
    }

    setIsGeneratingAI(true);
    try {
      const response = await generateAIDescription(form);
      if (response.data && response.data.description) {
        setForm(prev => ({
          ...prev,
          description: response.data.description
        }));
        toast.success("AI description generated!");
      } else {
        toast.error("AI service returned empty response.");
      }
    } catch (error) {
      console.error("AI Generation Error:", error);
      toast.error(error?.response?.data?.detail || "Failed to generate AI description.");
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const handleCancel = () => {
    setForm(initialState);
    setImagePreviews([]);
    setFloorplanFlags([]);
    // REMOVE setVideoPreviews([])
    setErrors({});
    if (onClose) onClose();
    isCrossIconPress && isCrossIconPress();
  };

  const Stepper = ({ step }) => {
    // Appraisals are a single step — no documents to upload.
    if (effectiveListingType === "appraisal") {
      return (
        <div className="w-full mx-auto mb-6">
          <div className="flex justify-center items-center">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm bg-[#55C5D0]">
              1
            </div>
          </div>
          <div className="flex justify-center text-xs sm:text-sm mt-3 text-gray-600">
            <span className="text-center">Add Appraisal</span>
          </div>
        </div>
      );
    }
    return (
    <div className="w-full mx-auto mb-6">
      <div className="relative flex justify-between items-center">
        {[1, 2].map((s, idx) => (
          <div key={s} className="relative flex flex-col items-center flex-1">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm z-10
              ${step >= s ? "bg-[#55C5D0]" : "bg-gray-300"}`}
            >
              {s}
            </div>
            {idx < 1 && (
              <div
                className={`absolute top-1/2 left-1/2 w-full h-1 -translate-y-1/2
                ${step > s ? "bg-[#55C5D0]" : "bg-gray-200"}`}
              ></div>
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-between text-xs sm:text-sm mt-3 text-gray-600">
        <span className="text-center w-1/2">Add Property</span>
        <span className="text-center w-1/2">Documents</span>
      </div>
    </div>
    );
  };

  const content = (
    <div
      className={
        isModal
          ? "w-full max-w-5xl mx-auto bg-white rounded-2xl h-[90%] shadow overflow-y-auto"
          : "max-w-5xl mx-auto bg-white rounded-2xl shadow p-3 sm:p-4 md:p-8 mt-4 sm:mt-6 md:mt-8"
      }
    >
      <div className={isModal ? "sticky top-0 bg-white z-10 p-4 sm:p-6 md:p-8 pb-3 sm:pb-4" : ""}>
        <Stepper step={step} />
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          {/* <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Home className="w-6 h-6 text-blue-500" />{" "}
            {step===1 ? (isEdit && !lead_id ? "Update Property" : "Add New Property") : step===2 ? "Property Price Statement" : "Contract"}
          </h2> */}
          {/* <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600"
            title="Clear Form"
          >
            <X size={24} />
          </button> */}
        </div>
      </div>
      <div className={isModal ? "px-4 sm:px-6 md:px-8 pb-6 sm:pb-8" : ""}>
        {step === 1 && (
          <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8 md:space-y-10">
            {/* Market Status Badge - shows which type of listing is being created/edited */}

            {(() => {
              const lt = effectiveListingType;
              if (lt === 'onmarket') {
                return (
                  <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-50 border border-green-200 w-fit">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500"></span>
                    <span className="text-sm font-semibold text-green-700">On Market Listing</span>
                  </div>
                );
              }
              if (lt === 'offmarket') {
                return (
                  <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-purple-50 border border-purple-200 w-fit">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-purple-500"></span>
                    <span className="text-sm font-semibold text-purple-700">Off Market Listing</span>
                  </div>
                );
              }
              if (lt === 'appraisal') {
                return (
                  <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200 w-fit">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500"></span>
                    <span className="text-sm font-semibold text-amber-700">Appraisal</span>
                  </div>
                );
              }
              return null;
            })()}
            {/* Property Information Section */}
            <div className="bg-[#f9fafb] rounded-xl p-4 sm:p-6 shadow-sm">
              <div className="flex items-center mb-4 sm:mb-6">
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                  className="w-5 h-5 text-[#55C5D0] mr-2"
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">
                  Property Information
                </h3>
              </div>
              <div className="space-y-4 sm:space-y-6">
                {/* Listing status is pre-selected based on initial choice and hidden from UI */}

                {/* Transaction Type - MOVED TO TOP */}
                {showExtra && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Type (For Sale / For Lease) <span className="text-red-500">*</span>
                  </label>
                  <div
                    style={{ border: '0.85px solid #BDBDBD' }}
                    className="flex rounded-[10px] overflow-hidden divide-x-[0.85px] divide-[#BDBDBD] w-full"
                  >
                    {transactionTypes.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => handleChange({ target: { name: 'transaction_type', value: type.id } })}
                        className={`flex-1 py-3 px-4 text-sm font-semibold transition-all ${form.transaction_type === type.id
                          ? "bg-[#55C5D0] text-white shadow-sm"
                          : "bg-[#ECECEC] text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                          }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>
                )}

                {/* Status - change listing status (excludes current status from options) */}
                {showExtra && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Status
                  </label>
                  {(() => {
                    const currentStatus =
                      initialValues?.status?.name ||
                      initialValues?.status ||
                      form.status ||
                      "";
                    const allStatuses = [
                      "Prospect / Not Currently Listed",
                      "Appraisal",
                      "On Market Listing",
                      "Off Market Listing",
                    ];
                    return (
                      <select
                        name="status"
                        value={form.status === currentStatus ? "" : form.status}
                        onChange={(e) =>
                          handleChange({
                            target: { name: "status", value: e.target.value },
                          })
                        }
                        className="w-full px-4 py-3 text-sm rounded-[10px] bg-white text-gray-700 outline-none focus:border-[#55C5D0]"
                        style={{ border: "0.85px solid #BDBDBD" }}
                      >
                        <option value="" disabled>
                          {currentStatus
                            ? `${currentStatus} (current)`
                            : "Select status"}
                        </option>
                        {allStatuses
                          .filter((s) => s !== currentStatus)
                          .map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                      </select>
                    );
                  })()}
                </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <AddressAutocomplete
                    value={form.address}
                    onChange={handleChange}
                    onSelect={(suggestion) => {
                      const { raw } = suggestion;
                      const addressString = suggestion.description || '';

                      // Debug: Log the raw response to see the actual structure (remove after confirming structure)
                      console.log('Addresser API Response:', raw);
                      console.log('Address String:', addressString);

                      // Check if address components are nested
                      const components = raw?.components || raw?.address_components || {};

                      // Extract suburb from various possible property names (check both root and nested)
                      let suburb = raw?.suburb || raw?.locality || raw?.suburb_name || raw?.locality_name ||
                        raw?.suburb_name_long || raw?.suburb_long ||
                        components?.suburb || components?.locality || components?.suburb_name || '';

                      // Extract state from various possible property names (check both root and nested)
                      let state = raw?.state || raw?.state_code || raw?.state_name || raw?.state_abbreviation ||
                        raw?.state_short || components?.state || components?.state_code ||
                        components?.state_name || '';

                      // Extract postal code from various possible property names (check both root and nested)
                      let postcode = raw?.postcode || raw?.postal_code || raw?.postcode_number || raw?.postal ||
                        raw?.postcode_long || components?.postcode || components?.postal_code ||
                        components?.postcode_number || '';

                      // If API didn't provide structured data, parse from address string
                      if ((!suburb || !state || !postcode) && addressString) {
                        const parsed = parseAddressString(addressString);
                        suburb = suburb || parsed.suburb;
                        state = state || parsed.state;
                        postcode = postcode || parsed.postcode;
                      }

                      setForm(prev => {
                        // When the agent typed a unit/flat ("1/32 Porter Street"),
                        // Mapbox's street-level suggestion drops the sub-dwelling
                        // (and sometimes the street number too), so replacing the
                        // field with it would clobber "1/32" down to "Porter
                        // Street". Keep the typed street text and just borrow the
                        // geocoded suburb/state/postcode, appending them when the
                        // agent didn't already type the suburb (CRM-2026-00038).
                        const typed = (prev.address || '').trim();
                        const hasUnit = /^\s*(?:unit|apartment|apt|u)?\s*\d+[a-z]?\s*\/\s*\d/i.test(typed);
                        let finalAddress = addressString;
                        // GNAF suggestions carry the full authoritative address
                        // (unit included), so use them as-is. The typed-unit
                        // reconstruction below is only for Mapbox, which drops
                        // the sub-dwelling (CRM-2026-00038).
                        if (!raw?.gnaf && hasUnit) {
                          finalAddress = typed;
                          const tail = [suburb, state, postcode].filter(Boolean).join(' ');
                          if (suburb && !typed.toLowerCase().includes(suburb.toLowerCase())) {
                            finalAddress = `${typed}, ${tail}`;
                          }
                        }
                        return {
                          ...prev,
                          address: finalAddress,
                          suburb: suburb || prev.suburb,
                          state: state || prev.state,
                          postal_code: postcode || prev.postal_code
                        };
                      });
                    }}
                    placeholder="Start typing address... (e.g. 123 Collins Street, Melbourne)"
                    className={errors.address ? "border-red-500" : "border-gray-300"}
                  />
                  <p className="text-xs text-gray-400 mt-1">Address will auto-populate once you begin typing</p>
                  {errors.address && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.address}
                    </p>
                  )}
                  {!isEdit && (
                    <DuplicateAddressNotice matches={addressTwins} onOpen={openAddressTwin} transactionType={form.transaction_type} />
                  )}
                  {!isEdit && addressTwins.length === 0 && (
                    <SameBuildingNotice matches={buildingTwins} onOpen={openAddressTwin} />
                  )}
                </div>

                {/* Street — used for the "Show Street and Suburb" publish mode on internal marketing (e.g. EDMs) */}
                {showExtra && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street
                  </label>
                  <input
                    type="text"
                    name="street"
                    value={form.street}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                    placeholder="e.g. Collins Street"
                  />
                  <p className="text-xs text-gray-400 mt-1">Used when Publish is set to &quot;Show Street and Suburb&quot;</p>
                </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Suburb <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="suburb"
                      value={form.suburb}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border ${errors.suburb ? "border-red-500" : "border-gray-300"
                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                      placeholder="e.g. East Geelong"
                    />
                    {errors.suburb && (
                      <p className="text-red-500 text-xs mt-1">{errors.suburb}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={form.state}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border ${errors.state ? "border-red-500" : "border-gray-300"
                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                      placeholder="e.g. VIC"
                    />
                    {errors.state && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.state}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Postal Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="postal_code"
                      value={form.postal_code}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border ${errors.postal_code
                        ? "border-red-500"
                        : "border-gray-300"
                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                      placeholder="e.g. 3000"
                    />
                    {errors.postal_code && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.postal_code}
                      </p>
                    )}
                  </div>
                </div>

                {/* Publish — controls how the address is shown on internal marketing (e.g. EDMs) */}
                {showExtra && (
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                  <label className="text-sm font-medium text-gray-700 md:w-24" htmlFor="address_publish_mode">
                    Publish:
                  </label>
                  <select
                    id="address_publish_mode"
                    name="address_publish_mode"
                    value={form.address_publish_mode || "full"}
                    onChange={handleChange}
                    className="md:w-72 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                  >
                    <option value="full">Show Full Address</option>
                    <option value="suburb">Show Suburb Only</option>
                    <option value="street_suburb">Show Street and Suburb</option>
                  </select>
                  <span className="text-xs text-gray-500">
                    (&quot;show street and suburb&quot; is only applicable to &quot;Internal Marketing&quot; such as EDMs)
                  </span>
                </div>
                )}

                {/* Property Class */}
                <div data-error-key="propertyTypes">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Property Class <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {propertyTypeOptions.map((type) => {
                      const isSelected = form.propertyTypes === type.id;
                      return (
                        <div
                          key={type.id}
                          className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${isSelected
                            ? "border-[#55C5D0] bg-[#55C5D0]/15 shadow-md ring-2 ring-[#55C5D0]/30"
                            : "border-gray-200 bg-white hover:border-gray-300"
                            }`}
                          onClick={() => {
                            handleChange({ target: { name: 'propertyTypes', value: type.id } });
                            handleChange({ target: { name: 'property_class', value: type.id } });
                          }}
                        >
                          <div className={`p-2 rounded-full flex-shrink-0 ${isSelected ? "bg-[#55C5D0]/25" : "bg-gray-100"}`}>
                            <type.icon className={`w-5 h-5 ${isSelected ? "text-[#55C5D0]" : "text-gray-500"}`} />
                          </div>
                          <span className={`text-sm font-semibold truncate ${isSelected ? "text-[#0d9488]" : "text-gray-600"}`}>
                            {type.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {errors.propertyTypes && (
                    <p className="text-red-500 text-xs mt-1">{errors.propertyTypes}</p>
                  )}
                  {form.propertyTypes === "Commercial" && (
                    <div className="mt-4 space-y-3">
                      {[
                        { name: "commercial_category", label: "Commercial category", placeholder: "Select a commercial category (defaults to Other)" },
                        { name: "commercial_category_2", label: "2nd category (optional)", placeholder: "None" },
                        { name: "commercial_category_3", label: "3rd category (optional)", placeholder: "None" },
                      ].map((slot) => (
                        <div key={slot.name}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{slot.label}</label>
                          <select
                            name={slot.name}
                            value={commercialCategoryOptions.includes(form[slot.name]) ? form[slot.name] : ""}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                          >
                            <option value="">{slot.placeholder}</option>
                            {commercialCategoryOptions.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                      <p className="text-xs text-gray-500">
                        How the listing is categorised on realcommercial.com.au (REA allows up to 3).
                      </p>
                    </div>
                  )}
                </div>

                {/* Price & Commission */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {form.transaction_type === "lease" ? (
                    <>
                      {/* Rent for Lease - Weekly for residential, Annual for commercial */}
                      {form.property_class === "Commercial" ? (
                        <>
                          {/* ANNUAL RENT section header */}
                          <div className="col-span-full">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Annual Rent</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Annual rent <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <input
                                type="text"
                                name="weekly_rent"
                                value={form.weekly_rent}
                                onChange={handleChange}
                                className={`w-full pl-7 pr-4 py-2 border ${errors.weekly_rent ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                                placeholder="85,000"
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Advertised as {form.weekly_rent ? `$${parseFloat(String(form.weekly_rent).replace(/[$,]/g, "")).toLocaleString()}` : "$XX,XXX"} p.a.                            </p>
                            {errors.weekly_rent && <p className="text-red-500 text-xs mt-1">{errors.weekly_rent}</p>}
                          </div>
                          {/* Ignite application link - what the rental enquiry auto-reply
                              puts behind its "Apply for this property" button. */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Application link (Ignite)
                            </label>
                            <input
                              type="url"
                              name="rental_application_url"
                              value={form.rental_application_url}
                              onChange={handleChange}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                              placeholder="https://..."
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Renters get this as the &quot;Apply for this property&quot; button on the enquiry auto-reply. Leave blank and they are asked to reply instead.
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Weekly Rent <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="weekly_rent"
                              value={form.weekly_rent}
                              onChange={handleChange}
                              className={`w-full px-4 py-2 border ${errors.weekly_rent ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                              placeholder="$500"
                            />
                            {errors.weekly_rent && <p className="text-red-500 text-xs mt-1">{errors.weekly_rent}</p>}
                          </div>
                          {/* Bond — mandatory for lease properties */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Bond <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="bond"
                              value={form.bond}
                              onChange={handleChange}
                              className={`w-full px-4 py-2 border ${errors.bond ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                              placeholder="$2,000"
                            />
                            {errors.bond && <p className="text-red-500 text-xs mt-1">{errors.bond}</p>}
                          </div>
                          {/* Available date — when the property can be rented from */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Available
                            </label>
                            <input
                              type="date"
                              name="available_date"
                              value={form.available_date}
                              onChange={handleChange}
                              className={`w-full px-4 py-2 border ${errors.available_date ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                            />
                            {errors.available_date && <p className="text-red-500 text-xs mt-1">{errors.available_date}</p>}
                          </div>
                          {/* Ignite application link - what the rental enquiry auto-reply
                              puts behind its "Apply for this property" button. */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Application link (Ignite)
                            </label>
                            <input
                              type="url"
                              name="rental_application_url"
                              value={form.rental_application_url}
                              onChange={handleChange}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                              placeholder="https://..."
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Renters get this as the &quot;Apply for this property&quot; button on the enquiry auto-reply. Leave blank and they are asked to reply instead.
                            </p>
                          </div>
                          {/* Furnished - REA's "Furnished" rental search filter. Only
                              CRM listings store it; the Collings/Public pages pass
                              hideFurnished because they save to a different model. */}
                          {!hideFurnished && (
                          <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                name="furnished"
                                checked={!!form.furnished}
                                onChange={(e) => setForm((prev) => ({ ...prev, furnished: e.target.checked }))}
                                className="h-4 w-4 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0]"
                              />
                              Furnished
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              Shows the listing as furnished on realestate.com.au.
                            </p>
                          </div>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {/* NO PRICE YET. Simon Abbott, 3 Sep 2026: "Price (But
                          have a 'No Price Yet' button)". Plenty of appraisals
                          are logged before a number is discussed; this says so
                          out loud instead of leaving an empty box that looks
                          unfinished. Ticking it clears whatever was typed. */}
                      {form.status === "Appraisal" && (
                        <div className="col-span-full">
                          <button
                            type="button"
                            aria-pressed={noPriceYet}
                            onClick={() => {
                              const next = !noPriceYet;
                              setNoPriceYet(next);
                              if (next) {
                                // Recalculate so a yield worked out from a half-typed price goes too.
                                setForm((prev) => {
                                  const cleared = { ...prev, price: "", price_to: "" };
                                  return { ...cleared, ...calculateYields(cleared) };
                                });
                                setErrors((prev) => ({ ...prev, price: undefined, price_to: undefined }));
                              }
                            }}
                            className={`px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${noPriceYet
                              ? "border-[#55C5D0] bg-[#55C5D0]/10 text-[#55C5D0]"
                              : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                              }`}
                          >
                            {noPriceYet ? "✓ No Price Yet" : "No Price Yet"}
                          </button>
                          {noPriceYet && (
                            <p className="text-xs text-gray-500 mt-1">
                              No price will be saved. Add one later from the property or the pipeline card.
                            </p>
                          )}
                        </div>
                      )}

                      {/* Price Type Selector */}
                      {!noPriceYet && (
                      <div className="col-span-full">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Price Type{" "}
                          {form.status === "Appraisal"
                            ? <span className="text-gray-400 text-xs">(Optional)</span>
                            : <span className="text-red-500">*</span>}
                        </label>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {[
                            { value: "exact", label: "Asking Price" },
                            { value: "range", label: "Price Range" },
                            { value: "contact", label: "Contact Agent" },
                            // { value: "auction", label: "Auction" },
                          ].map((opt) => {
                            const isSelected = (form.price_display || "exact") === opt.value;
                            return (
                              <label
                                key={opt.value}
                                className={`flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg border-2 transition-all ${isSelected
                                  ? "border-[#55C5D0] bg-[#55C5D0]/10 text-[#55C5D0] font-semibold"
                                  : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                                  }`}
                              >
                                <input
                                  type="radio"
                                  name="price_display"
                                  value={opt.value}
                                  checked={isSelected}
                                  onChange={handleChange}
                                  className="sr-only"
                                />
                                <span className="text-sm font-medium">{opt.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      )}

                      {/* ASKING PRICE section header for commercial */}
                      {form.property_class === "Commercial" && (
                        <div className="col-span-full">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Asking Price</p>
                        </div>
                      )}

                      {/* Price for Sale - Single or Range based on price_display */}
                      {!noPriceYet && (form.price_display === "range" ? (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {form.property_class === "Commercial"
                                ? <>Price From</>
                                : "Price From"
                              }
                              {form.status !== "Appraisal" && <span className="text-red-500">*</span>}
                            </label>
                            <input
                              type="text"
                              name="price"
                              value={form.price}
                              onChange={handleChange}
                              className={`w-full px-4 py-2 border ${errors.price ? "border-red-500" : "border-gray-300"
                                } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                              placeholder="$1,200,000"
                            />
                            {form.property_class === "Commercial" && form.price && (
                              <p className="text-xs text-gray-500 mt-1">
                                Advertised as {`$${parseFloat(String(form.price).replace(/[$,]/g, "")).toLocaleString()}`}                              </p>
                            )}
                            {errors.price && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.price}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {form.property_class === "Commercial"
                                ? <>Price To</>
                                : "Price To"
                              }
                              {form.status !== "Appraisal" && <span className="text-red-500">*</span>}
                            </label>
                            <input
                              type="text"
                              name="price_to"
                              value={form.price_to || ""}
                              onChange={handleChange}
                              className={`w-full px-4 py-2 border ${errors.price_to ? "border-red-500" : "border-gray-300"
                                } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                              placeholder="$1,350,000"
                            />
                            {form.property_class === "Commercial" && form.price_to && (
                              <p className="text-xs text-gray-500 mt-1">
                                Advertised as {`$${parseFloat(String(form.price_to).replace(/[$,]/g, "")).toLocaleString()}`}                              </p>
                            )}
                            {errors.price_to && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.price_to}
                              </p>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {form.property_class === "Commercial"
                                ? <>Asking price</>
                                : (form.price_display === "auction" || form.price_display === "contact") ? "Guided Price" : "Asking Price"
                              }
                              {form.status !== "Appraisal" && form.price_display !== "contact" && form.price_display !== "auction" && <span className="text-red-500">*</span>}
                              {(form.status === "Appraisal" || form.price_display === "contact" || form.price_display === "auction") && <span className="text-gray-400 text-xs"> (Optional)</span>}
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <input
                                type="text"
                                name="price"
                                value={form.price}
                                onChange={handleChange}
                                className={`w-full pl-7 pr-4 py-2 border ${errors.price ? "border-red-500" : "border-gray-300"
                                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                                placeholder={form.price_display === "auction" || form.price_display === "contact" ? "1,250,000 (Guided Price)" : "1,250,000"}
                              />
                            </div>
                            {form.property_class === "Commercial" && form.price && (
                              <p className="text-xs text-gray-500 mt-1">
                                Advertised as {form.price ? `$${parseFloat(String(form.price).replace(/[$,]/g, "")).toLocaleString()}` : "$X,XXX,XXX"}                              </p>
                            )}
                            {errors.price && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.price}
                              </p>
                            )}
                          </div>
                        </>
                      ))}

                      {/* Price Display Text (Freeform) - overrides price range/value on Buyer Portal when set */}
                      {showExtra && (
                      <div className="col-span-full">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Price Display Text <span className="text-gray-400 text-xs">(Optional — overrides price display on Collings Portal when set)</span>
                        </label>
                        <input
                          type="text"
                          name="price_display_text"
                          value={form.price_display_text || ""}
                          onChange={handleChange}
                          maxLength={255}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                          placeholder='e.g. "Offers above $1,250,000" or "Expressions of Interest"'
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          When set, this freeform text replaces the price range / asking price wherever the price is shown on the Collings Portal.
                        </p>
                      </div>
                      )}

                      {/* ANNUAL RENT section header for commercial */}
                      {form.property_class === "Commercial" && (
                        <div className="col-span-full">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Annual Rent</p>
                        </div>
                      )}

                      {/* Rent for Sale Properties (if rented) */}
                      {form.property_class === "Commercial" ? (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Annual rent <span className="text-gray-400 text-xs">(Optional)</span>
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <input
                                type="text"
                                name="weekly_rent"
                                value={form.weekly_rent}
                                onChange={handleChange}
                                className={`w-full pl-7 pr-4 py-2 border ${errors.weekly_rent ? "border-red-500" : "border-gray-300"
                                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                                placeholder="85,000"
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Advertised as {form.weekly_rent ? `$${parseFloat(String(form.weekly_rent).replace(/[$,]/g, "")).toLocaleString()}` : "$XX,XXX"} p.a.                            </p>
                            {errors.weekly_rent && <p className="text-red-500 text-xs mt-1">{errors.weekly_rent}</p>}
                          </div>
                        </>
                      ) : (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Weekly Rent <span className="text-gray-400 text-xs">(Optional)</span>
                          </label>
                          <input
                            type="text"
                            name="weekly_rent"
                            value={form.weekly_rent}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 border ${errors.weekly_rent ? "border-red-500" : "border-gray-300"
                              } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                            placeholder="$500"
                          />
                          <p className="text-xs text-gray-500 mt-1">Add weekly rent if property is currently rented</p>
                          {errors.weekly_rent && <p className="text-red-500 text-xs mt-1">{errors.weekly_rent}</p>}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Expected commission on the short appraisal form, marked
                    optional. Agents can enter either a percentage rate or a
                    fixed dollar amount (CRM-2026-00088) - some appraisals are
                    quoted as a flat fee, not a percentage. The GST treatment is
                    not settled at appraisal stage, so it waits behind Add Extra
                    Information with the full block below. */}
                {!showExtra && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expected Commission{" "}
                      <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-2">Pick one, percentage or fixed amount</p>
                    <div className="flex gap-4 mb-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="commission_type"
                          value="percentage"
                          checked={(form.commission_type || "percentage") === "percentage"}
                          onChange={handleChange}
                          className="text-[#55C5D0] focus:ring-[#55C5D0]"
                        />
                        <span className="text-sm font-medium text-gray-700">Percentage</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="commission_type"
                          value="fixed"
                          checked={form.commission_type === "fixed"}
                          onChange={handleChange}
                          className="text-[#55C5D0] focus:ring-[#55C5D0]"
                        />
                        <span className="text-sm font-medium text-gray-700">Fixed Amount</span>
                      </label>
                    </div>
                    {(form.commission_type || "percentage") === "percentage" ? (
                      <>
                        <span className="block text-xs text-gray-500 mb-1">Rate (%)</span>
                        <input
                          type="number"
                          name="commission_rate"
                          value={form.commission_rate}
                          onChange={handleChange}
                          min="0"
                          step="0.01"
                          className={`w-full px-4 py-2 border ${errors.commission_rate ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                          placeholder="e.g. 2.5"
                        />
                        {errors.commission_rate && (
                          <p className="text-red-500 text-xs mt-1">{errors.commission_rate}</p>
                        )}
                      </>
                    ) : (
                      <>
                        <span className="block text-xs text-gray-500 mb-1">Fixed amount ($)</span>
                        <input
                          type="text"
                          name="commission_amount"
                          value={form.commission_amount}
                          onChange={handleChange}
                          className={`w-full px-4 py-2 border ${errors.commission_amount ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                          placeholder="e.g. 15,000"
                        />
                        {errors.commission_amount && (
                          <p className="text-red-500 text-xs mt-1">{errors.commission_amount}</p>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Commission Section - Always visible */}
                {showExtra && (
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {form.transaction_type === "lease" ? "Management Fee" : "Commission"}{" "}
                      {form.transaction_type !== "lease" && form.status !== "Appraisal" && <span className="text-red-500">*</span>}
                      {form.status === "Appraisal" && <span className="text-gray-400 text-xs">(Optional)</span>}
                    </label>
                    <p className="text-xs text-gray-500 mb-2">Pick one, percentage or fixed amount</p>
                    <div className="flex gap-4 mb-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="commission_type"
                          value="percentage"
                          checked={(form.commission_type || "percentage") === "percentage"}
                          onChange={handleChange}
                          className="text-[#55C5D0] focus:ring-[#55C5D0]"
                        />
                        <span className="text-sm font-medium text-gray-700">Percentage</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="commission_type"
                          value="fixed"
                          checked={form.commission_type === "fixed"}
                          onChange={handleChange}
                          className="text-[#55C5D0] focus:ring-[#55C5D0]"
                        />
                        <span className="text-sm font-medium text-gray-700">Fixed Amount</span>
                      </label>
                    </div>
                    {(form.commission_type || "percentage") === "percentage" ? (
                      <>
                        <span className="block text-xs text-gray-500 mb-1">Rate (%) {form.transaction_type !== "lease" && form.status !== "Appraisal" && <span className="text-red-500">*</span>}</span>
                        <input
                          type="number"
                          name="commission_rate"
                          value={form.commission_rate}
                          onChange={handleChange}
                          min="0"
                          step="0.01"
                          className={`w-full px-4 py-2 border ${errors.commission_rate ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                          placeholder="e.g. 2.5"
                        />
                        {errors.commission_rate && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.commission_rate}
                          </p>
                        )}
                        <div className="flex gap-4 mt-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="commission_gst"
                              value="inclusive"
                              checked={(form.commission_gst || "inclusive") === "inclusive"}
                              onChange={handleChange}
                              className="text-[#55C5D0] focus:ring-[#55C5D0]"
                            />
                            <span className="text-xs text-gray-600">Inclusive of GST</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="commission_gst"
                              value="plus"
                              checked={form.commission_gst === "plus"}
                              onChange={handleChange}
                              className="text-[#55C5D0] focus:ring-[#55C5D0]"
                            />
                            <span className="text-xs text-gray-600">Plus GST</span>
                          </label>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="block text-xs text-gray-500 mb-1">Fixed amount ($) {form.transaction_type !== "lease" && form.status !== "Appraisal" && <span className="text-red-500">*</span>}</span>
                        <input
                          type="text"
                          name="commission_amount"
                          value={form.commission_amount}
                          onChange={handleChange}
                          className={`w-full px-4 py-2 border ${errors.commission_amount ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                          placeholder="e.g. 15,000"
                        />
                        {errors.commission_amount && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.commission_amount}
                          </p>
                        )}
                        <div className="flex gap-4 mt-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="commission_gst"
                              value="inclusive"
                              checked={(form.commission_gst || "inclusive") === "inclusive"}
                              onChange={handleChange}
                              className="text-[#55C5D0] focus:ring-[#55C5D0]"
                            />
                            <span className="text-xs text-gray-600">Inclusive of GST</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="commission_gst"
                              value="plus"
                              checked={form.commission_gst === "plus"}
                              onChange={handleChange}
                              className="text-[#55C5D0] focus:ring-[#55C5D0]"
                            />
                            <span className="text-xs text-gray-600">Plus GST</span>
                          </label>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                )}

                {/* Letting Fee Section - lease listings only */}
                {form.transaction_type === "lease" && (
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Letting Fee
                      </label>
                      <p className="text-xs text-gray-500 mb-2">Pick one, percentage or fixed amount</p>
                      <div className="flex gap-4 mb-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="letting_fee_type"
                            value="percentage"
                            checked={(form.letting_fee_type || "percentage") === "percentage"}
                            onChange={handleChange}
                            className="text-[#55C5D0] focus:ring-[#55C5D0]"
                          />
                          <span className="text-sm font-medium text-gray-700">Percentage</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="letting_fee_type"
                            value="fixed"
                            checked={form.letting_fee_type === "fixed"}
                            onChange={handleChange}
                            className="text-[#55C5D0] focus:ring-[#55C5D0]"
                          />
                          <span className="text-sm font-medium text-gray-700">Fixed Amount</span>
                        </label>
                      </div>
                      {(form.letting_fee_type || "percentage") === "percentage" ? (
                        <>
                          <span className="block text-xs text-gray-500 mb-1">Rate (%)</span>
                          <input
                            type="number"
                            name="letting_fee_rate"
                            value={form.letting_fee_rate}
                            onChange={handleChange}
                            min="0"
                            step="0.01"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                            placeholder="e.g. 2.5"
                          />
                          <div className="flex gap-4 mt-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="letting_fee_gst"
                                value="inclusive"
                                checked={(form.letting_fee_gst || "inclusive") === "inclusive"}
                                onChange={handleChange}
                                className="text-[#55C5D0] focus:ring-[#55C5D0]"
                              />
                              <span className="text-xs text-gray-600">Inclusive of GST</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="letting_fee_gst"
                                value="plus"
                                checked={form.letting_fee_gst === "plus"}
                                onChange={handleChange}
                                className="text-[#55C5D0] focus:ring-[#55C5D0]"
                              />
                              <span className="text-xs text-gray-600">Plus GST</span>
                            </label>
                          </div>
                        </>
                      ) : (
                        <>
                          <span className="block text-xs text-gray-500 mb-1">Fixed amount ($)</span>
                          <input
                            type="text"
                            name="letting_fee_amount"
                            value={form.letting_fee_amount}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                            placeholder="e.g. 1,500"
                          />
                          <div className="flex gap-4 mt-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="letting_fee_gst"
                                value="inclusive"
                                checked={(form.letting_fee_gst || "inclusive") === "inclusive"}
                                onChange={handleChange}
                                className="text-[#55C5D0] focus:ring-[#55C5D0]"
                              />
                              <span className="text-xs text-gray-600">Inclusive of GST</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="letting_fee_gst"
                                value="plus"
                                checked={form.letting_fee_gst === "plus"}
                                onChange={handleChange}
                                className="text-[#55C5D0] focus:ring-[#55C5D0]"
                              />
                              <span className="text-xs text-gray-600">Plus GST</span>
                            </label>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Yield & Financial Details - Hidden for non-commercial leases; shown for commercial regardless of transaction type */}
                {showExtra && (form.transaction_type !== "lease" || form.property_class === "Commercial") && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                          <Percent className="w-4 h-4" /> Est. Gross Yield (%)
                          <span className="text-xs text-gray-400 ml-1">(Auto-calculated)</span>
                        </label>
                        <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 font-medium">
                          {form.est_gross_yield ? `${form.est_gross_yield}%` : "—"}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {form.property_class === "Commercial" ? "Annual Rent ÷ Price × 100" : "(Weekly Rent × 52) ÷ Price × 100"}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                          <Percent className="w-4 h-4" /> Est. Net Yield (%)
                          <span className="text-xs text-gray-400 ml-1">(Auto-calculated)</span>
                        </label>
                        <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 font-medium">
                          {form.est_net_yield ? `${form.est_net_yield}%` : "—"}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {form.property_class === "Commercial"
                            ? "After management fee (OpEx excluded — paid by tenant)"
                            : "After all rates and fees deducted"
                          }
                        </p>
                      </div>
                    </div>

                    {/* Annual Outgoings Breakdown - Collapsible */}
                    <div className="space-y-4">
                      {!showAnnualOutgoings ? (
                        <button
                          type="button"
                          onClick={() => setShowAnnualOutgoings(true)}
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#55C5D0] border border-[#55C5D0] rounded-lg hover:bg-[#55C5D0] hover:text-white transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          {form.property_class === "Commercial" ? "Add OpEx / Outgoings" : "Add Annual Outgoings"}
                        </button>
                      ) : (
                        <div className="space-y-4 border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-900">
                              {form.property_class === "Commercial" ? "OPEX (OUTGOINGS)" : "Annual Outgoings Breakdown"}
                            </h3>
                            <button
                              type="button"
                              onClick={() => setShowAnnualOutgoings(false)}
                              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                            >
                              {showAnnualOutgoings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              Hide
                            </button>
                          </div>

                          {form.property_class === "Commercial" ? (
                            /* Commercial: OPEX (OUTGOINGS) breakdown with checkbox and 6-item grid */
                            <>
                              <div className="flex items-center gap-2 mb-2">
                                <input
                                  type="checkbox"
                                  id="tenant_pays_opex"
                                  name="tenant_pays_opex"
                                  checked={form.tenant_pays_opex}
                                  onChange={handleChange}
                                  className="w-4 h-4 text-[#55C5D0] border-gray-300 rounded focus:ring-[#55C5D0]"
                                />
                                <label htmlFor="tenant_pays_opex" className="text-sm font-medium text-gray-700">
                                  Tenant pays OpEx (typical for commercial leases)
                                </label>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                    <DollarSign className="w-4 h-4" /> Council rates ($/yr)
                                  </label>
                                  <input
                                    type="number"
                                    name="council_rates"
                                    value={form.council_rates}
                                    onChange={handleChange}
                                    min="0"
                                    step="1"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                                    placeholder="0"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                    <DollarSign className="w-4 h-4" /> Water rates ($/yr)
                                  </label>
                                  <input
                                    type="number"
                                    name="water_rates"
                                    value={form.water_rates}
                                    onChange={handleChange}
                                    min="0"
                                    step="1"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                                    placeholder="0"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                    <DollarSign className="w-4 h-4" /> Body Corporate ($/yr)
                                  </label>
                                  <input
                                    type="number"
                                    name="insurance"
                                    value={form.insurance}
                                    onChange={handleChange}
                                    min="0"
                                    step="1"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                                    placeholder="0"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                    <DollarSign className="w-4 h-4" /> Management fee ($/yr)
                                    <span className="text-xs text-teal-600">(Auto-calculated: 6.6%)</span>
                                  </label>
                                  <input
                                    type="number"
                                    name="current_annual_property_management_fee"
                                    value={form.current_annual_property_management_fee}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                    className="w-full px-4 py-2 border border-teal-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent bg-teal-50/30"
                                    placeholder={form.weekly_rent ? "Auto-calculated" : "Enter annual rent first"}
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    Annual Rent × 6.6% = ${form.current_annual_property_management_fee ? parseFloat(form.current_annual_property_management_fee).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '0.00'}/yr
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                    <DollarSign className="w-4 h-4" /> Other OpEx ($/yr)
                                  </label>
                                  <input
                                    type="number"
                                    name="other_opex"
                                    value={form.other_opex}
                                    onChange={handleChange}
                                    min="0"
                                    step="1"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                            </>
                          ) : (
                            /* Residential / other: Body Corporate, Council Rates, Water Rates */
                            <>
                              <div className="flex items-center gap-2 mb-2">
                                <input
                                  type="checkbox"
                                  id="has_body_corporate"
                                  name="has_body_corporate"
                                  checked={form.has_body_corporate}
                                  onChange={handleChange}
                                  className="w-4 h-4 text-[#55C5D0] border-gray-300 rounded focus:ring-[#55C5D0]"
                                />
                                <label htmlFor="has_body_corporate" className="text-sm font-medium text-gray-700">
                                  Property has Body Corporate
                                </label>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className={!form.has_body_corporate ? "opacity-50 pointer-events-none" : ""}>
                                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                    <DollarSign className="w-4 h-4" /> Body Corporate ($/year)
                                    {!form.has_body_corporate && <span className="text-gray-400 text-xs">(Optional)</span>}
                                  </label>
                                  <input
                                    type="number"
                                    name="body_corporate"
                                    value={form.body_corporate}
                                    onChange={handleChange}
                                    min="0"
                                    step="1"
                                    disabled={!form.has_body_corporate}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                                    placeholder={form.has_body_corporate ? "e.g. 2000" : "N/A"}
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                    <DollarSign className="w-4 h-4" /> Council Rates ($/year)
                                  </label>
                                  <input
                                    type="number"
                                    name="council_rates"
                                    value={form.council_rates}
                                    onChange={handleChange}
                                    min="0"
                                    step="1"
                                    className={`w-full px-4 py-2 border ${errors.council_rates ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                                    placeholder="e.g. 1500"
                                  />
                                  {errors.council_rates && <p className="text-red-500 text-xs mt-1">{errors.council_rates}</p>}
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                    <DollarSign className="w-4 h-4" /> Water Rates ($/year)
                                  </label>
                                  <input
                                    type="number"
                                    name="water_rates"
                                    value={form.water_rates}
                                    onChange={handleChange}
                                    min="0"
                                    step="1"
                                    className={`w-full px-4 py-2 border ${errors.water_rates ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                                    placeholder="e.g. 1000"
                                  />
                                  {errors.water_rates && <p className="text-red-500 text-xs mt-1">{errors.water_rates}</p>}
                                </div>
                              </div>
                            </>
                          )}

                          {/* Management fee + savings: residential only (commercial shows it in the OPEX grid above) */}
                          {form.property_class !== "Commercial" && (
                            <>
                              {/* Opt-in: the standard 6.6% management fee is only added to outgoings /
                                  net yield when the agent ticks this box. Off by default so a property
                                  with rent but no outgoings shows N/A (not a management-fee-only figure). */}
                              <label className="flex items-start gap-2 mb-3 cursor-pointer">
                                <input
                                  type="checkbox"
                                  name="include_management_fee"
                                  checked={form.include_management_fee}
                                  onChange={handleChange}
                                  className="w-4 h-4 mt-0.5 text-[#55C5D0] border-gray-300 rounded focus:ring-[#55C5D0]"
                                />
                                <span className="text-sm font-medium text-gray-700">
                                  Add standard property management fee (6.6% of annual rent)
                                  <span className="block text-xs font-normal text-gray-500">
                                    Leave unticked to keep outgoings and net yield as N/A when no outgoings are entered.
                                  </span>
                                </span>
                              </label>

                              {form.include_management_fee && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                      <DollarSign className="w-4 h-4" /> Property Management Fee ($/year)
                                      <span className="text-xs text-teal-600 ml-1">(Auto-calculated: 6.6%)</span>
                                    </label>
                                    <div className="relative">
                                      <input
                                        type="number"
                                        name="current_annual_property_management_fee"
                                        value={form.current_annual_property_management_fee}
                                        onChange={handleChange}
                                        min="0"
                                        step="0.01"
                                        className="w-full px-4 py-2 border border-teal-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent bg-teal-50/30"
                                        placeholder={form.weekly_rent ? "Auto-calculated" : "Enter weekly rent first"}
                                      />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {`Weekly Rent × 52 × 6.6% = $${form.current_annual_property_management_fee ? parseFloat(form.current_annual_property_management_fee).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '0.00'}/year. You can override manually.`}
                                    </p>
                                  </div>
                                  {form.current_annual_property_management_fee && parseFloat(form.current_annual_property_management_fee) > 0 && (
                                    <div className="bg-green-50 p-4 rounded-lg border border-green-200 flex flex-col justify-center">
                                      <label className="block text-sm font-semibold text-green-800 mb-1">
                                        Potential Savings if you Switch to Collings
                                      </label>
                                      <div className="text-2xl font-bold text-green-600">
                                        ${Math.round(parseFloat(form.current_annual_property_management_fee) * 0.25).toLocaleString()}/year
                                      </div>
                                      <p className="text-xs text-green-700 mt-1">25% of current annual management fee</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}

                          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <label className="block text-sm font-semibold text-gray-900 mb-1">
                              {form.property_class === "Commercial" ? "Owner Outgoings ($/year)" : "Total Estimated Outgoings ($/year)"}
                            </label>
                            <div className="text-2xl font-bold text-[#55C5D0]">
                              {form.est_outgoings ? `$${parseFloat(form.est_outgoings).toLocaleString()}` : 'N/A'}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {form.property_class === "Commercial"
                                ? form.tenant_pays_opex
                                  ? "Management fee only — OpEx is paid by the tenant and excluded from yield."
                                  : "All OpEx items included — owner bears operating costs."
                                : "Auto-calculated from breakdown above"
                              }
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Method of Sale - shown for both On-Market and Off-Market sale listings so agents can pick Auction vs Private Sale and set auction date/time here. Hidden for lease and appraisal. */}
                {form.transaction_type !== "lease" && effectiveListingType !== "appraisal" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Method of Sale
                      </label>
                      <select
                        name="method_of_sale"
                        value={form.method_of_sale}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent appearance-none bg-white"
                      >
                        {methodOfSaleOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {/* Buyer Portal controls moved to Visibility section below */}
                    </div>


                    {form.method_of_sale?.toLowerCase().includes('auction') && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                            <Calendar className="w-4 h-4" /> Auction Date & Time
                          </label>
                          <input
                            type="datetime-local"
                            name="auction_date"
                            value={form.auction_date}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                            <MapPin className="w-4 h-4" /> Auction Venue
                          </label>
                          <input
                            type="text"
                            name="auction_venue"
                            value={form.auction_venue}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                            placeholder="e.g. On Site"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Specs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <Bed className="w-4 h-4" /> Bedrooms{" "}
                      {form.propertyTypes !== "Commercial" && form.propertyTypes !== "Land" && form.status !== "Appraisal" && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="number"
                      name="beds"
                      value={form.beds}
                      onChange={handleChange}
                      min="0"
                      step="1"
                      className={`w-full px-4 py-2 border ${errors.beds ? "border-red-500" : "border-gray-300"
                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                    />
                    {errors.beds && (
                      <p className="text-red-500 text-xs mt-1">{errors.beds}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <Bath className="w-4 h-4" /> Bathrooms{" "}
                      {form.propertyTypes !== "Commercial" && form.propertyTypes !== "Land" && form.status !== "Appraisal" && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="number"
                      name="baths"
                      value={form.baths}
                      onChange={handleChange}
                      min="0"
                      step="1"
                      className={`w-full px-4 py-2 border ${errors.baths ? "border-red-500" : "border-gray-300"
                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                    />
                    {errors.baths && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.baths}
                      </p>
                    )}
                  </div>

                  {/* CAR PARKS on the short appraisal form. The full Parking
                      Details panel (type per bay, multiple bays) is behind Add
                      Extra Information; at appraisal stage a single count is
                      all anyone has, so this writes it as garage spaces - the
                      figure the pipeline card and the listing both read. */}
                  {!showExtra && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        <Car className="w-4 h-4" /> Car Parks
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={form.parking_list?.[0]?.spaces ?? ""}
                        onChange={(e) => {
                          const spaces = e.target.value;
                          setForm((prev) => {
                            const list = [...(prev.parking_list || [])];
                            list[0] = { type: spaces ? "garage" : "", spaces };
                            return { ...prev, parking_list: list };
                          });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                      />
                    </div>
                  )}
                </div>

                {showExtra && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <Square className="w-4 h-4" /> Floor Area (m²)
                    </label>
                    <input
                      type="number"
                      name="floor_area"
                      value={form.floor_area}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                      placeholder="e.g. 120.5"
                    />
                    <p className="text-xs text-gray-500 mt-1">Internal floor area</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <Square className="w-4 h-4" /> Land Area (m²)
                    </label>
                    <input
                      type="number"
                      name="land_area"
                      value={form.land_area}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                      placeholder="e.g. 450.0"
                    />
                    <p className="text-xs text-gray-500 mt-1">Total land size</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <Calendar className="w-4 h-4" /> Year Built
                    </label>
                    <input
                      type="number"
                      name="year_built"
                      value={form.year_built}
                      onChange={handleChange}
                      min="1800"
                      max={new Date().getFullYear()}
                      step="1"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                      placeholder="e.g. 2020"
                    />
                  </div>
                </div>
                )}

                {showExtra && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <Car className="w-5 h-5 text-blue-600" />
                      Parking Details
                    </h4>
                    <button
                      type="button"
                      onClick={addParking}
                      className="text-xs font-semibold text-[#55C5D0] hover:text-[#45a4ad] flex items-center gap-1"
                    >
                      Add More Parking +
                    </button>
                  </div>

                  <div className="space-y-4">
                    {form.parking_list.map((parking, index) => (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 relative">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Parking Type
                          </label>
                          <select
                            value={parking.type}
                            onChange={(e) => handleParkingChange(index, "type", e.target.value)}
                            className={`w-full px-4 py-2 border ${errors[`parking_type_${index}`] ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent appearance-none bg-white`}
                          >
                            <option value="">Select type</option>
                            <option value="garage">Garage</option>
                            <option value="carport">Carports</option>
                            <option value="open">Open parking</option>
                          </select>
                          {errors[`parking_type_${index}`] && <p className="text-red-500 text-xs mt-1">{errors[`parking_type_${index}`]}</p>}
                        </div>
                        <div className="flex gap-2 items-end">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Number of Spaces
                            </label>
                            <input
                              type="number"
                              value={parking.spaces}
                              onChange={(e) => handleParkingChange(index, "spaces", e.target.value)}
                              min="0"
                              step="1"
                              className={`w-full px-4 py-2 border ${errors[`parking_spaces_${index}`] ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                              placeholder="e.g. 2"
                            />
                            {errors[`parking_spaces_${index}`] && <p className="text-red-500 text-xs mt-1">{errors[`parking_spaces_${index}`]}</p>}
                          </div>
                          {form.parking_list.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeParking(index)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Display Total Parking Count */}
                  <div className="mt-4 pt-4 border-t border-blue-100 flex justify-end">
                    <div className="text-sm font-medium text-gray-700">
                      Total Parking Count: <span className="text-blue-600 font-bold">
                        {form.parking_list.reduce((sum, item) => sum + (parseInt(item.spaces) || 0), 0)}
                      </span>
                    </div>
                  </div>
                </div>
                )}

                {/* Virtual Tour & Video */}
                {showExtra && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <Video className="w-4 h-4" /> Video URL
                    </label>
                    <input
                      type="text"
                      name="video_url"
                      value={form.video_url}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border ${errors.video_url ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                      placeholder="https://vimeo.com/..."
                    />
                    {errors.video_url && (
                      <p className="text-red-500 text-xs mt-1">{errors.video_url}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <Link className="w-4 h-4" /> Virtual Tour Link
                    </label>
                    <input
                      type="text"
                      name="virtual_tour_link"
                      value={form.virtual_tour_link}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border ${errors.virtual_tour_link ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                      placeholder="https://matterport.com/..."
                    />
                    {errors.virtual_tour_link && (
                      <p className="text-red-500 text-xs mt-1">{errors.virtual_tour_link}</p>
                    )}
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Property Description Section */}
            {showExtra && (
            <div className="bg-[#f9fafb] rounded-xl p-4 sm:p-6 shadow-sm">
              <div className="flex items-center mb-4 sm:mb-6">
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                  className="w-5 h-5 text-[#55C5D0] mr-2"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">
                  Property Description
                </h3>
              </div>
              <div className="space-y-4 sm:space-y-6">
                {/* Property Title/Headline */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <FileText className="w-4 h-4" /> Property Title / Headline
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Add a catchy headline for your property listing (e.g., "Stunning Family Home with Pool" or "Modern Apartment in Prime Location")
                  </p>
                  <input
                    type="text"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border ${errors.title ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                    placeholder="E.g., Charming 3-Bedroom Home with Modern Upgrades"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-xs mt-1">{errors.title}</p>
                  )}
                </div>

                {/* Property Description with AI Generation */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <FileText className="w-4 h-4" /> Property Description{" "}
                    {form.status !== "Appraisal" && <span className="text-red-500">*</span>}
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    💡 Tip: Type key details or notes about the property, then click "Generate with AI" to create a professional description. Or leave blank to generate from property details only.
                  </p>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    rows={6}
                    className={`w-full px-4 py-2 border ${errors.description ? "border-red-500" : "border-gray-300"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent resize-none`}
                    placeholder="E.g., Recently renovated kitchen, walking distance to shops, quiet street, north-facing backyard..."
                  />
                  {errors.description && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.description}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={handleGenerateAI}
                    disabled={isGeneratingAI}
                    className={`mt-3 px-4 py-2 bg-[#55C5D0] text-white text-sm font-bold rounded-lg hover:bg-[#3bb3be] transition-colors flex items-center gap-2 ${isGeneratingAI ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    <span className="text-lg">✨</span>
                    {isGeneratingAI ? "Generating..." : form.description ? "Rewrite with AI" : "Generate with AI"}
                  </button>
                </div>

                {/* Internal Features - hidden for Land */}
                {form.propertyTypes !== "Land" && (
                <div className="mt-4" data-error-key="internal_features">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <ListIcon className="w-4 h-4" /> Internal Features
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Add internal features (e.g., Air Conditioning, Built-in Wardrobes, Dishwasher). Press Enter or click "Add Feature" to add each one.
                  </p>

                  {/* Display added internal features as pills */}
                  {form.internalFeaturesList && form.internalFeaturesList.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3 p-3 bg-white rounded-lg border border-gray-200">
                      {form.internalFeaturesList.map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 px-3 py-1.5 bg-[#55C5D0] text-white rounded-full text-sm"
                        >
                          <span>{feature}</span>
                          <button
                            type="button"
                            onClick={() => removeInternalFeature(index)}
                            className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                            title="Remove feature"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Input field for adding new internal features */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={form.internalFeatureInput || ""}
                      onChange={(e) => handleInternalFeatureInputChange(e.target.value)}
                      onKeyPress={handleInternalFeatureInputKeyPress}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                      placeholder="e.g., Air Conditioning"
                    />
                    <button
                      type="button"
                      onClick={addInternalFeature}
                      disabled={!form.internalFeatureInput?.trim()}
                      className={`p-2 bg-[#55C5D0] text-white rounded-lg hover:bg-[#3bb3be] transition-colors ${!form.internalFeatureInput?.trim() ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      title="Add feature"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  {errors.internal_features && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.internal_features}
                    </p>
                  )}
                </div>
                )}

                {/* External Features */}
                <div className="mt-4" data-error-key="external_features">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <ListIcon className="w-4 h-4" /> External Features
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Add external features (e.g., Pool, Garden, Garage, Balcony). Press Enter or click "Add Feature" to add each one.
                  </p>

                  {/* Display added external features as pills */}
                  {form.externalFeaturesList && form.externalFeaturesList.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3 p-3 bg-white rounded-lg border border-gray-200">
                      {form.externalFeaturesList.map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 px-3 py-1.5 bg-[#55C5D0] text-white rounded-full text-sm"
                        >
                          <span>{feature}</span>
                          <button
                            type="button"
                            onClick={() => removeExternalFeature(index)}
                            className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                            title="Remove feature"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Input field for adding new external features */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={form.externalFeatureInput || ""}
                      onChange={(e) => handleExternalFeatureInputChange(e.target.value)}
                      onKeyPress={handleExternalFeatureInputKeyPress}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                      placeholder="e.g., Pool"
                    />
                    <button
                      type="button"
                      onClick={addExternalFeature}
                      disabled={!form.externalFeatureInput?.trim()}
                      className={`p-2 bg-[#55C5D0] text-white rounded-lg hover:bg-[#3bb3be] transition-colors ${!form.externalFeatureInput?.trim() ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      title="Add feature"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  {errors.external_features && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.external_features}
                    </p>
                  )}
                </div>
              </div>
            </div>
            )}

            {/* Property Media */}
            {showExtra && (
            <div className="bg-[#f9fafb] rounded-xl p-6 shadow-sm">
              <div className="flex flex-col mb-6">
                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2 mb-4">
                  <Building2 className="w-5 h-5 text-[#55C5D0]" />
                  Property Media
                </h3>
                <div className="border-b border-gray-200 w-full" />
              </div>

              <div data-error-key="images">
                <label className="text-sm font-medium text-gray-600 mb-3 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" />
                  Property Images & Videos (multiple) {form.status !== "Appraisal" && <span className="text-red-500">*</span>}
                </label>
                <div
                  style={{ border: '0.85px solid #BDBDBD' }}
                  className="mt-1 flex justify-center px-6 pt-5 pb-8 rounded-[15px] bg-white hover:bg-gray-50 transition-colors cursor-pointer relative"
                >
                  <input
                    type="file"
                    name="images"
                    accept={SUPPORTED_UPLOAD_ACCEPT}
                    multiple
                    onChange={handleImageChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div className="space-y-4 text-center">
                    <div className="flex justify-center">
                      <div className="relative w-16 h-12">
                        <svg viewBox="0 0 64 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-[#55C5D0]">
                          <path d="M48 16C46.5 16 45.1 16.3 43.8 16.9C42.2 10.6 36.6 6 30 6C23.4 6 17.8 10.6 16.2 16.9C14.9 16.3 13.5 16 12 16C5.4 16 0 21.4 0 28C0 34.6 5.4 40 12 40H48C54.6 40 60 34.6 60 28C60 21.4 54.6 16 48 16Z" fill="currentColor" />
                          <path d="M30 18L22 26H28V34H32V26H38L30 18Z" fill="white" />
                        </svg>
                      </div>
                    </div>
                    <div className="text-sm text-gray-700">
                      <span>Click or <strong>Drag</strong> your photos here</span>
                    </div>
                  </div>
                </div>
                {errors.images && (
                  <p className="text-red-500 text-xs mt-1">{errors.images}</p>
                )}

                {imagePreviews?.length > 0 && (
                  <div className="flex flex-wrap gap-3 bg-white p-4 rounded-lg border border-gray-200 mt-3">
                    {imagePreviews.map((src, idx) => {
                      const file = form.images[idx];
                      let isVideo = false;
                      if (file instanceof File) {
                        isVideo = file.type && file.type.startsWith("video");
                      } else if (typeof file === "string") {
                        isVideo = /\.(mp4|mov|webm|ogg)(\?.*)?$/i.test(
                          file.split("?")[0]
                        );
                      }
                      const isFloorplan = !!floorplanFlags[idx];
                      // The uploader's filename, so the agent can see/confirm the
                      // order they named the photos in (e.g. "01 Front.jpg").
                      const imageName =
                        file instanceof File
                          ? file.name
                          : (typeof file === "string"
                              ? existingImageNames[file.split("?")[0]]
                              : "") || "";
                      return (
                        <div
                          key={idx}
                          className={`relative w-32 group cursor-move ${draggedImageIndex === idx ? 'opacity-50' : ''
                            }`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, idx)}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, idx)}
                          onDragEnd={handleDragEnd}
                        >
                          <div
                            className={`relative w-full h-20 flex items-center justify-center border rounded overflow-hidden bg-gray-50 cursor-pointer ${isFloorplan ? 'ring-2 ring-[#55C5D0]' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setLightboxImage({ src, isVideo });
                            }}
                          >
                            {isVideo ? (
                              <video
                                src={src}
                                className="object-cover w-full h-full pointer-events-none"
                              />
                            ) : (
                              <img
                                src={src}
                                alt={`Preview ${idx + 1}`}
                                className="object-cover w-full h-full pointer-events-none"
                              />
                            )}
                            {/* Order badge */}
                            <div className="absolute top-1 left-1 bg-[#55C5D0] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center pointer-events-none">
                              {idx + 1}
                            </div>
                            {isFloorplan && (
                              <div className="absolute bottom-1 left-1 bg-[#55C5D0] text-white text-[10px] font-semibold px-1.5 py-0.5 rounded pointer-events-none">
                                Floorplan
                              </div>
                            )}
                          </div>
                          {/* Original filename, so the agent can confirm the order
                              they named the photos in. Truncated with a tooltip; a
                              fourth parallel array is avoided by deriving it above. */}
                          {imageName && (
                            <div
                              className="mt-1 text-[10px] text-gray-500 truncate"
                              title={imageName}
                            >
                              {imageName}
                            </div>
                          )}
                          {/* Floorplan toggle (hidden for videos). The label reads as an
                              action until ticked - a bare "Floorplan" under every photo
                              looked like every photo was already a floorplan. */}
                          {!isVideo && (
                            <label
                              className={`mt-1 flex items-center justify-between text-[11px] cursor-pointer select-none ${isFloorplan ? "text-[#55C5D0] font-semibold" : "text-gray-500"}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span>{isFloorplan ? "Floorplan \u2713" : "Mark as floorplan"}</span>
                              <input
                                type="checkbox"
                                checked={isFloorplan}
                                onChange={(e) =>
                                  setFloorplanFlags((prev) => {
                                    const next = [...prev];
                                    next[idx] = e.target.checked;
                                    return next;
                                  })
                                }
                                className="h-3.5 w-3.5 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0]"
                              />
                            </label>
                          )}
                          <button
                            onClick={() => {
                              if (isEdit && src.includes("https")) {
                                setDeleteImages((prev) => [...prev, src]);
                              }
                              setImagePreviews((prev) =>
                                prev.filter((_, i) => i !== idx)
                              );
                              setForm((prev) => ({
                                ...prev,
                                images: prev.images.filter((_, i) => i !== idx),
                              }));
                              setFloorplanFlags((prev) =>
                                prev.filter((_, i) => i !== idx)
                              );
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity z-20"
                            type="button"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Agent Assignment Section */}
            <div className="bg-[#f9fafb] rounded-xl p-6 shadow-sm">
              <div className="flex items-center mb-6">
                <User className="w-5 h-5 text-[#55C5D0] mr-2" />
                <h3 className="text-xl font-semibold text-gray-800">
                  Agent Assignment
                </h3>
              </div>

              <div className="space-y-6">
                <div data-error-key="agentId">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Primary Agent <span className="text-red-500">*</span>
                  </label>
                  <InfiniteScrollSelect
                    required
                    value={form.agentId || ""}
                    onChange={(val) => handleChange({ target: { name: "agentId", value: val } })}
                    placeholder="Select Agent"
                    fetchOptions={fetchAgentOptions}
                    pageSize={12}
                    initialOptions={agents.filter(a => String(a.id) === String(form.agentId))}
                    getLabel={(d) => d.name || `${d.first_name || ''} ${d.last_name || ''}`.trim() || `Agent ${d.id}`}
                    getValue={(d) => String(d.id)}
                  />
                  <p className="text-xs text-gray-400 mt-1">Select the main agent responsible for this listing</p>
                  {errors.agentId && (
                    <p className="text-red-500 text-xs mt-1">{errors.agentId}</p>
                  )}
                </div>

                {/* Secondary agent - Optional. When selected, automatically appears on the listing. */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Secondary Agent (Optional)
                  </label>
                  <InfiniteScrollSelect
                    value={form.secondaryAgent || ""}
                    onChange={(val) => setForm((prev) => ({ ...prev, secondaryAgent: val, makeSecondaryAgentOnListing: !!val }))}
                    placeholder="Select Agent (Optional)"
                    clearable
                    clearLabel="No secondary agent"
                    fetchOptions={fetchAgentOptions}
                    pageSize={12}
                    initialOptions={agents.filter(a => String(a.id) === String(form.secondaryAgent))}
                    getLabel={(d) => d.name || `${d.first_name || ''} ${d.last_name || ''}`.trim() || `Agent ${d.id}`}
                    getValue={(d) => String(d.id)}
                  />
                  <p className="text-xs text-gray-400 mt-1">The secondary agent is shown on marketing alongside the primary agent.</p>
                </div>
                {/* Third agent - access only, not on marketing. Also shown on the
                    short appraisal form so a third agent can be added when adding an
                    appraisal (Monday 13065093914). */}
                {(showExtra || isShortAppraisalForm) && (
                <div className="md:col-span-2">
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
                    This agent will have access to the listing but will not be shown on marketing.
                  </p>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Third Agent (Optional)
                  </label>
                  <InfiniteScrollSelect
                    value={form.tertiaryAgent || ""}
                    onChange={(val) => handleChange({ target: { name: "tertiaryAgent", value: val } })}
                    placeholder="None"
                    clearable
                    clearLabel="No third agent"
                    fetchOptions={fetchAgentOptions}
                    pageSize={12}
                    initialOptions={agents.filter(a => String(a.id) === String(form.tertiaryAgent))}
                    getLabel={(d) => d.name || `${d.first_name || ''} ${d.last_name || ''}`.trim() || `Agent ${d.id}`}
                    getValue={(d) => String(d.id)}
                  />
                </div>
                )}
              </div>
            </div>

            {/* Owner Information Section */}
            <div className="bg-[#f9fafb] rounded-xl p-6 shadow-sm">
              <div className="flex items-center mb-6">
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                  className="w-5 h-5 text-[#55C5D0] mr-2"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <h3 className="text-xl font-semibold text-gray-800">
                  Owner Information
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative" ref={ownerDropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <User className="w-4 h-4" /> Owner Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="agent"
                    value={form.agent}
                    onChange={(e) => {
                      handleChange(e);
                      searchOwnerContacts(e.target.value);
                    }}
                    onBlur={() => setTimeout(() => { setShowOwnerDropdown(false); setOwnerSearchLoading(false); }, 150)}
                    onFocus={() => searchOwnerContacts(form.agent)}
                    autoComplete="off"
                    className={`w-full px-4 py-2 border ${errors.agent ? "border-red-500" : "border-gray-300"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                    placeholder="e.g. Sarah Johnson"
                  />
                  {showOwnerDropdown && (ownerSearchLoading || ownerSuggestions.length > 0) && (
                    <ul className="absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                      {ownerSearchLoading ? (
                        <li className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4 text-[#55C5D0]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                          Searching contacts...
                        </li>
                      ) : ownerSuggestions.map((c) => (
                        <li
                          key={c.id}
                          onMouseDown={() => selectOwnerContact(c)}
                          className="px-4 py-2 cursor-pointer hover:bg-[#f0fbfc] text-sm"
                        >
                          <span className="font-medium text-gray-800">{c.name}</span>
                          {c.phone && <span className="text-gray-500 ml-2">{c.phone}</span>}
                          {c.email && <span className="text-gray-400 ml-2 text-xs">{c.email}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                  {errors.agent && (
                    <p className="text-red-500 text-xs mt-1">{errors.agent}</p>
                  )}
                </div>

                {/* The name the owner actually goes by, when it differs from their legal
                    name (Christian Gravias, Monday 12720874749). Mirrors the Preferred
                    Name field on the Add Contact form. */}
                {showExtra && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <User className="w-4 h-4" /> Preferred Name{" "}
                    <span className="text-xs text-gray-500">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="owner_preferred_name"
                    value={form.owner_preferred_name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                    placeholder="e.g. Andrew"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The name they go by, if it differs from their legal name.
                  </p>
                </div>
                )}

                <div data-error-key="contact">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <User className="w-4 h-4" /> Contact Info{" "}
                    {form.status !== "Appraisal" && <span className="text-red-500">*</span>}
                  </label>
                  <PhoneField
                    value={form.contact}
                    onChange={(value) => {
                      handleChange({ target: { name: "contact", value } });
                      if (!value) {
                        setPhoneError(form.status === "Appraisal" ? "" : "Phone number is required");
                      } else if (!isCompletePhoneNumber(value)) {
                        setPhoneError("Invalid phone number");
                      } else {
                        setPhoneError("");
                      }
                    }}
                    className={`w-full px-4 py-2 border ${errors.contact ? "border-red-500" : "border-gray-300"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                    placeholder="412 345 678"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Defaults to Australian format (+61) - select a different country for an international vendor.
                  </p>
                  {(errors.contact || phoneError) && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.contact || phoneError}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <User className="w-4 h-4" /> Owner Email {form.status !== "Appraisal" && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border ${errors.email ? "border-red-500" : "border-gray-300"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent`}
                    placeholder="e.g. owner@email.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Additional Owners rendering */}
                {form.additionalOwners?.map((owner, index) => (
                  <div key={index} className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-gray-100 mt-4 relative">
                    <button
                      type="button"
                      onClick={() => removeOwner(index)}
                      className="absolute top-0 right-0 p-1 text-red-500 hover:text-red-700"
                      title="Remove owner"
                    >
                      <X className="w-5 h-5" />
                    </button>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        <User className="w-4 h-4" /> Additional Owner Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={owner.name}
                        onChange={(e) => handleAdditionalOwnerChange(index, 'name', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                        placeholder="e.g. Robert Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        <User className="w-4 h-4" /> Preferred Name{" "}
                        <span className="text-xs text-gray-500">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={owner.preferred_name || ""}
                        onChange={(e) => handleAdditionalOwnerChange(index, 'preferred_name', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                        placeholder="e.g. Bob"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        The name they go by, if it differs from their legal name.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        <User className="w-4 h-4" /> Contact Info
                      </label>
                      <PhoneField
                        value={owner.contact}
                        onChange={(value) => handleAdditionalOwnerChange(index, 'contact', value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                        placeholder="412 345 678"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Defaults to Australian format (+61) - select a different country for an international vendor.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        <User className="w-4 h-4" /> Owner Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={owner.email}
                        onChange={(e) => handleAdditionalOwnerChange(index, 'email', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                        placeholder="e.g. owner2@email.com"
                      />
                    </div>
                  </div>
                ))}

                {/* Always available, including on the short appraisal form: CG
                    (#12986068596) wants agents to add extra owners with a button
                    rather than cramming a second mobile/email onto one owner. */}
                <div className="md:col-span-2">
                  <button
                    type="button"
                    onClick={addOwner}
                    className="px-4 py-2 bg-[#55C5D0] text-white rounded-lg text-sm font-medium hover:bg-[#3bb3be] transition-colors"
                  >
                    + Add another owner
                  </button>
                </div>

                {/* Invite the owner to claim this property into their My Property
                    Portfolio. The email fires once the listing/appraisal is saved. */}
                {showExtra && (
                <div className="md:col-span-2">
                  <label className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 bg-[#f9fafb] cursor-pointer hover:border-[#55C5D0] transition-colors">
                    <input
                      type="checkbox"
                      checked={sendClaimInvite}
                      onChange={(e) => setSendClaimInvite(e.target.checked)}
                      className="mt-0.5 w-4 h-4 text-[#55C5D0] border-gray-300 rounded focus:ring-[#55C5D0]"
                    />
                    <span className="text-sm text-gray-700">
                      <span className="font-medium block">Send invitation to claim property</span>
                      When you save this listing, we&apos;ll email the owner
                      {form.email ? <> (<span className="font-medium">{form.email}</span>)</> : null} a
                      secure link to add this property to their My Property Portfolio and finish
                      setting up their loan, rental and goal details.
                    </span>
                  </label>
                </div>
                )}
              </div>
            </div>

            {/* AI Assistant Questionnaire — agent-curated Q&A injected into the Buyer Portal chatbot's per-property context.
                Skipped for appraisals: they never reach the Buyer Portal.
                Only shown when the creator is one of the assigned agents (or in edit mode); otherwise
                it's emailed to the primary agent — see the note rendered below. */}
            {effectiveListingType !== "appraisal" && showQuestionnaire && (
            <div className="bg-[#f9fafb] rounded-xl p-6 shadow-sm">
              <div className="flex items-center mb-2">
                <ClipboardList className="w-5 h-5 text-[#55C5D0] mr-2" />
                <h3 className="text-xl font-semibold text-gray-800">
                  AI Assistant Questionnaire
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Answer the questions below so the Buyer Portal AI assistant can give accurate, on-brand
                responses to enquiries about this property. Leave a question blank to skip it, and add
                custom Q&amp;A for anything specific.
                {!isEdit && (
                  <>
                    {" "}
                    If you leave this blank, the listing's primary agent is emailed the
                    questions to answer later.
                  </>
                )}
              </p>

              <div className="space-y-4">
                {(form.ai_questionnaire || []).map((qa, index) => (
                  <div
                    key={index}
                    className="bg-white border border-gray-200 rounded-lg p-4 relative"
                  >
                    <button
                      type="button"
                      onClick={() => removeQuestionnaireItem(index)}
                      className="absolute top-2 right-2 p-1 text-red-500 hover:text-red-700"
                      title="Remove question"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Question {index + 1}
                    </label>
                    <input
                      type="text"
                      value={qa.question}
                      onChange={(e) =>
                        handleQuestionnaireChange(index, "question", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent mb-2"
                      placeholder="e.g. Is there NBN connected?"
                    />
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Answer
                    </label>
                    <textarea
                      value={qa.answer}
                      onChange={(e) =>
                        handleQuestionnaireChange(index, "answer", e.target.value)
                      }
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent resize-y"
                      placeholder="Type the answer the AI assistant should give buyers…"
                    />
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addQuestionnaireItem}
                  className="px-4 py-2 bg-[#55C5D0] text-white rounded-lg text-sm font-medium hover:bg-[#3bb3be] transition-colors inline-flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Add question
                </button>
              </div>
            </div>
            )}

            {/* Creator isn't one of the assigned agents: hide the questionnaire and tell
                them it will be emailed to the primary agent to complete. */}
            {effectiveListingType !== "appraisal" && !showQuestionnaire && (
            <div className="bg-[#f9fafb] rounded-xl p-6 shadow-sm">
              <div className="flex items-center mb-2">
                <ClipboardList className="w-5 h-5 text-[#55C5D0] mr-2" />
                <h3 className="text-xl font-semibold text-gray-800">
                  AI Assistant Questionnaire
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                You're creating this listing for another agent, so the AI Assistant
                Questionnaire isn't shown here. When you save, the primary agent will be
                emailed the questions to answer for their own listing.
              </p>
            </div>
            )}

            {/* Visibility / Appraisal section: depends on listing type (On-Market only gets visibility; Appraisal gets pipeline options; Off-Market gets neither) */}
            {effectiveListingType === "appraisal" && (
              <div className="bg-[#f9fafb] rounded-xl p-6 shadow-sm">
                <div className="flex items-center mb-6">
                  <ClipboardList className="w-5 h-5 text-[#55C5D0] mr-2" />
                  <h3 className="text-xl font-semibold text-gray-800">Appraisal – Pipeline</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">This appraisal will save to your CRM pipeline. Choose type and temperature for sorting.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Off-market or On-market appraisal</label>
                    <select
                      name="appraisal_market_type"
                      value={form.appraisal_market_type || ""}
                      onChange={(e) => handleChange({ target: { name: "appraisal_market_type", value: e.target.value } })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                    >
                      <option value="">Select</option>
                      <option value="off_market">Off-Market</option>
                      <option value="on_market">On-Market</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Temperature (for pipeline)</label>
                    <select
                      name="appraisal_temperature"
                      value={form.appraisal_temperature || "warm"}
                      onChange={(e) => handleChange({ target: { name: "appraisal_temperature", value: e.target.value } })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                    >
                      <option value="cold">Cold</option>
                      <option value="warm">Warm</option>
                      <option value="hot">Hot</option>
                    </select>
                  </div>
                </div>

                {/* How was this generated? Only for a from-scratch appraisal
                    (an appraisal added from a Collings lead inherits its origin
                    automatically). Required — sets the property's Origin
                    attribution (CRM-2026 Phase 2). */}
                {!lead_id && (
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      How was this appraisal generated? <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="origin_reason"
                      value={form.origin_reason || ""}
                      onChange={(e) => handleChange({ target: { name: "origin_reason", value: e.target.value } })}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent ${errors.origin_reason ? "border-red-500" : "border-gray-300"}`}
                    >
                      <option value="">Select</option>
                      {APPRAISAL_ORIGIN_REASONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    {errors.origin_reason && (
                      <p className="text-red-500 text-xs mt-1">{errors.origin_reason}</p>
                    )}
                  </div>
                )}

                {/* First note on the appraisal. Shows on the pipeline card as
                    the most recent note, and lands in the property's Notes tab
                    (Simon Abbott, Monday 12953887002). */}
                <div className="mt-6 border-t pt-6 border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note
                  </label>
                  <textarea
                    name="appraisal_note"
                    rows={3}
                    value={form.appraisal_note || ""}
                    onChange={(e) => handleChange({ target: { name: "appraisal_note", value: e.target.value } })}
                    placeholder="What came out of the appraisal? This shows on the pipeline card as the most recent note."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                  />
                </div>

                {/* Remind Me To Call Again (for Appraisals) - a SERIES, not one
                    call-back: "we need to be able to add a reminder series below
                    the note inside appraisals" (Simon Abbott, Monday
                    12985995388, 7 Sep 2026). Every entry is saved with the
                    appraisal as its own reminder. */}
                <div className="mt-6 border-t pt-6 border-gray-100">
                  <AppraisalReminderSeries
                    propertyId={isEdit ? initialValues?.id : undefined}
                    value={form.appraisal_reminders || []}
                    onChange={(list) =>
                      handleChange({ target: { name: "appraisal_reminders", value: list } })
                    }
                  />
                </div>
              </div>
            )}

            {effectiveListingType === "offmarket" && (
              <div className="bg-[#f9fafb] rounded-xl p-6 shadow-sm">
                <div className="flex items-center mb-4">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-[#55C5D0] mr-2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <h3 className="text-xl font-semibold text-gray-800">Visibility (Off-Market)</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Off-market listings are <strong>private</strong>. Tick the checkbox below to make this property visible on the Collings Portal. It will <em>not</em> appear on REA, Domain or any public sites.
                </p>

                {/* Collings Portal checkbox */}
                <div className={`flex items-center justify-between px-3 py-2 rounded-lg border-2 transition-all ${form.published_on_website ? "border-[#55C5D0] bg-[#55C5D0]/10" : "border-gray-200 bg-white"}`}>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="published_on_website"
                      id="off_market_published_on_website"
                      checked={form.published_on_website}
                      onChange={(e) => handleVisibilityChange("published_on_website", e.target.checked)}
                      className="w-4 h-4 text-[#55C5D0] rounded border-gray-300 focus:ring-[#55C5D0] cursor-pointer"
                    />
                    <label htmlFor="off_market_published_on_website" className="text-sm font-medium text-gray-700 cursor-pointer">
                      Collings Portal
                    </label>
                  </div>
                  <span className="text-xs text-gray-500">Off-Market Portal only</span>
                </div>

                {/* Portal Category Dropdown & Launch Date — only shown when Collings Portal is ticked */}
                {form.published_on_website && (
                  <>
                    {/* Portal Category Dropdown */}
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Portal Category
                      </label>
                      <select
                        name="portal_category"
                        value={form.portal_category}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                      >
                        <option value="">Select Portal Tab</option>
                        <option value="apartment_unit_villa">Apartment/Unit/Villa</option>
                        <option value="house">House</option>
                        <option value="townhouse">Townhouse</option>
                        <option value="blocks_of_units">Blocks of Units</option>
                        <option value="land">Land</option>
                        <option value="house_and_land">House & Land Package</option>
                        <option value="off_the_plan">Off-the-Plan Apartment</option>
                        <option value="off_the_plan_townhouse">Off-the-Plan Townhouse</option>
                        <option value="commercial">Commercial Property</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Select which tab this property appears under in the Collings Portal
                      </p>
                    </div>

                    {/* Launch Date & Time */}
                    <div className="mt-3 px-3 py-3 rounded-lg bg-[#55C5D0]/5 border border-[#55C5D0]/30">
                      <label htmlFor="off_market_listing_launch_at" className="block text-xs font-medium text-gray-600 mb-1">
                        🕐 Launch Date &amp; Time
                        <span className="ml-1 font-normal text-gray-400">(optional)</span>
                      </label>
                      <input
                        type="datetime-local"
                        id="off_market_listing_launch_at"
                        name="listing_launch_at"
                        value={form.listing_launch_at || ""}
                        onChange={handleChange}
                        className="w-full text-sm px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-[#55C5D0] bg-white text-gray-700"
                      />
                      <p className="mt-1.5 text-xs text-gray-400 leading-snug">
                        {form.listing_launch_at
                          ? `⏳ Will appear on Collings Portal on ${formatMelbourneDateTime(datetimeLocalToUtcISOString(form.listing_launch_at))}.`
                          : "Leave blank to publish immediately when Collings Portal is ticked."}
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}

            {effectiveListingType === "onmarket" && (
              <div className="bg-[#f9fafb] rounded-xl p-6 shadow-sm">
                <div className="flex items-center mb-6">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-[#55C5D0] mr-2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <h3 className="text-xl font-semibold text-gray-800">Visibility (On-Market)</h3>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-[#55C5D0] rounded border-gray-300 focus:ring-[#55C5D0] cursor-pointer"
                      id="tick_all"
                      onChange={handleTickAll}
                      checked={
                        form.internal_marketing &&
                        (form.propertyTypes === "Commercial" ? form.published_on_realcommercial : form.published_on_rea) &&
                        form.published_on_domain &&
                        form.published_on_collings
                      }
                    />
                    <label htmlFor="tick_all" className="text-sm text-gray-700 cursor-pointer">(tick all)</label>
                  </div>

                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg border-2 transition-all ${form.internal_marketing ? "border-[#55C5D0] bg-[#55C5D0]/10" : "border-transparent bg-transparent"}`}>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="internal_marketing"
                        id="internal_marketing"
                        checked={form.internal_marketing}
                        onChange={(e) => handleVisibilityChange("internal_marketing", e.target.checked)}
                        className="w-4 h-4 text-[#55C5D0] rounded border-gray-300 focus:ring-[#55C5D0] cursor-pointer"
                      />
                      <label htmlFor="internal_marketing" className="text-sm text-gray-700 cursor-pointer">Internal Marketing</label>
                    </div>
                  </div>

                  {/* Collings Portal - now available for On-Market listings */}
                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg border-2 transition-all ${form.published_on_website ? "border-[#55C5D0] bg-[#55C5D0]/10" : "border-transparent bg-transparent"}`}>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="published_on_website"
                        id="on_market_published_on_website"
                        checked={form.published_on_website}
                        onChange={(e) => handleVisibilityChange("published_on_website", e.target.checked)}
                        className="w-4 h-4 text-[#55C5D0] rounded border-gray-300 focus:ring-[#55C5D0] cursor-pointer"
                      />
                      <label htmlFor="on_market_published_on_website" className="text-sm text-gray-700 cursor-pointer">Collings Portal</label>
                    </div>
                  </div>

                  {/* Portal Category Dropdown - shown when Collings Portal is enabled */}
                  {form.published_on_website && (
                    <div className="ml-6 mt-1 mb-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Portal Category
                      </label>
                      <select
                        name="portal_category"
                        value={form.portal_category}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                      >
                        <option value="">Select Portal Tab</option>
                        <option value="apartment_unit_villa">Apartment/Unit/Villa</option>
                        <option value="house">House</option>
                        <option value="townhouse">Townhouse</option>
                        <option value="blocks_of_units">Blocks of Units</option>
                        <option value="land">Land</option>
                        <option value="house_and_land">House & Land Package</option>
                        <option value="off_the_plan">Off-the-Plan Apartment</option>
                        <option value="off_the_plan_townhouse">Off-the-Plan Townhouse</option>
                        <option value="commercial">Commercial Property</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Select which tab this property appears under in the Collings Portal
                      </p>
                    </div>
                  )}

                  {/* Commercial listings publish to realcommercial.com.au (via the REA
                      feed) instead of the residential realestate.com.au feed. */}
                  {form.propertyTypes === "Commercial" ? (
                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg border-2 transition-all ${form.published_on_realcommercial ? "border-[#55C5D0] bg-[#55C5D0]/10" : "border-transparent bg-transparent"}`}>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="published_on_realcommercial"
                        id="published_on_realcommercial"
                        checked={form.published_on_realcommercial}
                        onChange={(e) => handleVisibilityChange("published_on_realcommercial", e.target.checked)}
                        className="w-4 h-4 text-[#55C5D0] rounded border-gray-300 focus:ring-[#55C5D0] cursor-pointer"
                      />
                      <label htmlFor="published_on_realcommercial" className="text-sm text-gray-700 cursor-pointer">realcommercial.com.au</label>
                    </div>
                  </div>
                  ) : (
                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg border-2 transition-all ${form.published_on_rea ? "border-[#55C5D0] bg-[#55C5D0]/10" : "border-transparent bg-transparent"}`}>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="published_on_rea"
                        id="published_on_rea"
                        checked={form.published_on_rea}
                        onChange={(e) => handleVisibilityChange("published_on_rea", e.target.checked)}
                        className="w-4 h-4 text-[#55C5D0] rounded border-gray-300 focus:ring-[#55C5D0] cursor-pointer"
                      />
                      <label htmlFor="published_on_rea" className="text-sm text-gray-700 cursor-pointer">realestate.com.au</label>
                    </div>
                  </div>
                  )}

                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg border-2 transition-all ${form.published_on_domain ? "border-[#55C5D0] bg-[#55C5D0]/10" : "border-transparent bg-transparent"}`}>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="published_on_domain"
                        id="published_on_domain"
                        checked={form.published_on_domain}
                        onChange={(e) => handleVisibilityChange("published_on_domain", e.target.checked)}
                        className="w-4 h-4 text-[#55C5D0] rounded border-gray-300 focus:ring-[#55C5D0] cursor-pointer"
                      />
                      <label htmlFor="published_on_domain" className="text-sm text-gray-700 cursor-pointer">domain.com.au</label>
                    </div>
                  </div>

                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg border-2 transition-all ${form.published_on_collings ? "border-[#55C5D0] bg-[#55C5D0]/10" : "border-transparent bg-transparent"}`}>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="published_on_collings"
                        id="published_on_collings"
                        checked={form.published_on_collings}
                        onChange={(e) => handleVisibilityChange("published_on_collings", e.target.checked)}
                        className="w-4 h-4 text-[#55C5D0] rounded border-gray-300 focus:ring-[#55C5D0] cursor-pointer"
                      />
                      <label htmlFor="published_on_collings" className="text-sm text-gray-700 cursor-pointer">Collings.com.au</label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ADD EXTRA INFORMATION. The rest of the property form - listing
                type and status, street/publish, description and features,
                photos and floorplans, yield and outgoings, full parking, third
                agent, extra owners - lives behind this one button on a new
                appraisal (Simon Abbott, 3 Sep 2026). Once opened it stays open
                for the rest of the form; nothing typed into it is lost. */}
            {isShortAppraisalForm && !showExtraInfo && (
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => setShowExtraInfo(true)}
                  className="w-full px-6 py-3 rounded-lg border-2 border-dashed border-[#55C5D0] text-[#55C5D0] font-semibold hover:bg-[#55C5D0]/5 transition-colors"
                >
                  + Add Extra Information
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Photos, description, features, marketing and the rest of the property details. Not needed to save the appraisal.
                </p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 sm:pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCancel}
                className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium tracking-wide text-sm sm:text-base"
                disabled={isSubmitting || isUpdating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-[#55C5D0] text-white rounded-lg hover:bg-[#3bb3be] transition-colors font-medium tracking-wide disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px] text-sm sm:text-base"
                disabled={isSubmitting || isUpdating}
              >
                {isUpdating ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin h-5 w-5 mr-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"
                      ></path>
                    </svg>
                    Updating...
                  </span>
                ) : isSubmitting ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin h-5 w-5 mr-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"
                      ></path>
                    </svg>
                    Adding...
                  </span>
                ) : isEdit && !lead_id ? (
                  "Update Property"
                ) : (
                  <span className="flex items-center">Next</span>
                )}
              </button>
            </div>
          </form >
        )}
        {
          step === 2 && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Documents</h3>
                <Tabs defaultValue={form.transaction_type === "lease" ? "contract" : "statement"} className="w-full">
                  <TabsList className={`grid w-full ${form.transaction_type === "lease" ? "grid-cols-2" : "grid-cols-3"}`}>
                    {form.transaction_type !== "lease" && (
                      <TabsTrigger value="statement">Property Price Statement</TabsTrigger>
                    )}
                    <TabsTrigger value="contract">
                      {form.transaction_type === "lease" ? "Lease Agreement" : "Contract"}
                    </TabsTrigger>
                    <TabsTrigger value="legal_docs">Legal Docs</TabsTrigger>
                  </TabsList>
                  <TabsContent value="statement" className="mt-6">
                    <div
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const files = Array.from(e.dataTransfer.files || []);
                        if (files.length) {
                          setStatementFiles((prev) => [...prev, ...files]);
                        }
                      }}
                      className="border-2 border-dashed border-slate-300 rounded-lg p-4 sm:p-6 md:p-8 text-center"
                    >
                      <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600 mb-2">
                        Upload Property Price Statement
                      </p>
                      <p className="text-sm text-slate-500 mb-4">
                        Drag and drop files or click to browse
                      </p>
                      <label className="inline-flex items-center justify-center px-4 py-2 border rounded-md text-sm font-medium bg-white text-slate-700 border-slate-300 hover:bg-slate-50 cursor-pointer">
                        <Upload className="w-4 h-4 mr-2" />
                        Choose Files
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length) {
                              setStatementFiles((prev) => [...prev, ...files]);
                            }
                          }}
                        />
                      </label>
                    </div>
                    {statementFiles.length > 0 && (
                      <div className="space-y-2 mt-4">
                        {statementFiles.map((f, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg gap-3"
                          >
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <FileText className="w-5 h-5 text-slate-500 flex-shrink-0" />
                              <input
                                type="text"
                                value={getDocumentName(f, 'statement', i)}
                                onChange={(e) => handleDocumentNameChange(f, 'statement', i, e.target.value)}
                                className="flex-1 text-sm text-slate-700 bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                placeholder="Document name"
                              />
                              <span className="text-xs text-slate-400 flex-shrink-0">.{f.name.split('.').pop()}</span>
                            </div>
                            <button
                              type="button"
                              className="text-slate-500 hover:text-red-600 text-sm flex-shrink-0"
                              onClick={() =>
                                setStatementFiles((prev) =>
                                  prev.filter((_, idx) => idx !== i)
                                )
                              }
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                  <TabsContent value="contract" className="mt-6">
                    <div
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const files = Array.from(e.dataTransfer.files || []);
                        if (files.length) {
                          setContractFiles((prev) => [...prev, ...files]);
                        }
                      }}
                      className="border-2 border-dashed border-slate-300 rounded-lg p-4 sm:p-6 md:p-8 text-center"
                    >
                      <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600 mb-2">
                        {form.transaction_type === "lease" ? "Upload Lease Agreement" : "Upload Contract Documents"}
                      </p>
                      <p className="text-sm text-slate-500 mb-4">
                        Drag and drop files or click to browse
                      </p>
                      <label className="inline-flex items-center justify-center px-4 py-2 border rounded-md text-sm font-medium bg-white text-slate-700 border-slate-300 hover:bg-slate-50 cursor-pointer">
                        <Upload className="w-4 h-4 mr-2" />
                        Choose Files
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length) {
                              setContractFiles((prev) => [...prev, ...files]);
                            }
                          }}
                        />
                      </label>
                    </div>
                    {contractFiles.length > 0 && (
                      <div className="space-y-2 mt-4">
                        {contractFiles.map((f, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg gap-3"
                          >
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <FileText className="w-5 h-5 text-slate-500 flex-shrink-0" />
                              <input
                                type="text"
                                value={getDocumentName(f, 'contract', i)}
                                onChange={(e) => handleDocumentNameChange(f, 'contract', i, e.target.value)}
                                className="flex-1 text-sm text-slate-700 bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                placeholder="Document name"
                              />
                              <span className="text-xs text-slate-400 flex-shrink-0">.{f.name.split('.').pop()}</span>
                            </div>
                            <button
                              type="button"
                              className="text-slate-500 hover:text-red-600 text-sm flex-shrink-0"
                              onClick={() =>
                                setContractFiles((prev) =>
                                  prev.filter((_, idx) => idx !== i)
                                )
                              }
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                  <TabsContent value="legal_docs" className="mt-6">
                    <div
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const files = Array.from(e.dataTransfer.files || []);
                        if (files.length) {
                          setLegalDocsFiles((prev) => [...prev, ...files]);
                        }
                      }}
                      className="border-2 border-dashed border-slate-300 rounded-lg p-4 sm:p-6 md:p-8 text-center"
                    >
                      <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600 mb-2">Upload Legal Documents</p>
                      <p className="text-sm text-slate-500 mb-4">
                        Drag and drop files or click to browse
                      </p>
                      <label className="inline-flex items-center justify-center px-4 py-2 border rounded-md text-sm font-medium bg-white text-slate-700 border-slate-300 hover:bg-slate-50 cursor-pointer">
                        <Upload className="w-4 h-4 mr-2" />
                        Choose Files
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length) {
                              setLegalDocsFiles((prev) => [...prev, ...files]);
                            }
                          }}
                        />
                      </label>
                    </div>
                    {legalDocsFiles.length > 0 && (
                      <div className="space-y-2 mt-4">
                        {legalDocsFiles.map((f, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg gap-3"
                          >
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <FileText className="w-5 h-5 text-slate-500 flex-shrink-0" />
                              <input
                                type="text"
                                value={getDocumentName(f, 'legal', i)}
                                onChange={(e) => handleDocumentNameChange(f, 'legal', i, e.target.value)}
                                className="flex-1 text-sm text-slate-700 bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                placeholder="Document name"
                              />
                              <span className="text-xs text-slate-400 flex-shrink-0">.{f.name.split('.').pop()}</span>
                            </div>
                            <button
                              type="button"
                              className="text-slate-500 hover:text-red-600 text-sm flex-shrink-0"
                              onClick={() =>
                                setLegalDocsFiles((prev) =>
                                  prev.filter((_, idx) => idx !== i)
                                )
                              }
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
                <div className="flex justify-between mt-4">
                  <button
                    type="button"
                    onClick={() => { isSuccess && isSuccess(createdPropertyId); onClose && onClose(); }}
                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Skip
                  </button>
                  <button
                    onClick={async () => {
                      if (!createdPropertyId) {
                        toast.error("Property ID missing");
                        return;
                      }
                      if (statementFiles.length === 0 && contractFiles.length === 0 && legalDocsFiles.length === 0) {
                        toast.error("Please add at least one file");
                        return;
                      }
                      try {
                        setIsUploadingDocs(true);
                        await uploadPropertyDocuments(
                          createdPropertyId,
                          statementFiles,
                          contractFiles,
                          legalDocsFiles,
                          documentNames
                        );
                        toast.success("Documents uploaded");
                        isSuccess && isSuccess(createdPropertyId);
                        onClose && onClose();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Upload failed");
                      } finally {
                        setIsUploadingDocs(false);
                      }
                    }}
                    disabled={isUploadingDocs}
                    className="px-6 py-2 bg-[#55C5D0] text-white rounded-lg hover:bg-[#3bb3be] disabled:opacity-50"
                  >
                    {isUploadingDocs ? "Uploading..." : "Finish & Save"}
                  </button>
                </div>
              </div>
            </div>
          )
        }
      </div >


      {/* Visibility Confirmation Modal - single popup for one or more options */}
      <ConfirmModal
        isOpen={showVisibilityConfirm}
        onClose={() => {
          setShowVisibilityConfirm(false);
          setPendingVisibilityChanges(null);
        }}
        onConfirm={proceedWithVisibilityChange}
        title={
          pendingVisibilityChanges && Object.keys(pendingVisibilityChanges).length > 1
            ? "Confirm visibility changes"
            : "Confirm visibility"
        }
        description={
          pendingVisibilityChanges ? (
            <div className="space-y-3 text-sm">
              <p className="font-medium text-gray-700">
                You are about to enable the following:
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600">
                {Object.keys(pendingVisibilityChanges)
                  .filter((k) => pendingVisibilityChanges[k])
                  .map((key) => {
                    const opt = VISIBILITY_OPTIONS.find((o) => o.key === key);
                    return opt ? (
                      <li key={key}>
                        <span className="font-medium text-gray-700">{opt.label}:</span>{" "}
                        {opt.implication}
                      </li>
                    ) : null;
                  })}
              </ul>
              <p className="text-gray-500 pt-1">Do you want to proceed?</p>
            </div>
          ) : (
            ""
          )
        }
        confirmText="Yes, Enable"
        cancelText="Cancel"
        variant="primary"
      />

      {/* Save pressed with a live profile already at this address */}
      <DuplicateAddressDialog
        isOpen={showTwinConfirm}
        matches={addressTwins}
        transactionType={form.transaction_type}
        onOpen={openAddressTwin}
        onClose={() => setShowTwinConfirm(false)}
        onSaveAnyway={() => {
          mergeTargetRef.current = null;
          twinsAcceptedForRef.current = twinKey(form.address, form.suburb);
          setShowTwinConfirm(false);
          handleSubmit({ preventDefault: () => {} });
        }}
        onMerge={(match) => {
          mergeTargetRef.current = match.id;
          twinsAcceptedForRef.current = twinKey(form.address, form.suburb);
          setShowTwinConfirm(false);
          handleSubmit({ preventDefault: () => {} });
        }}
      />

      {/* Save pressed with the same building already there, one side without a unit number */}
      <SameBuildingDialog
        isOpen={showBuildingConfirm}
        matches={buildingTwins}
        onOpen={(match) => {
          setShowBuildingConfirm(false);
          openAddressTwin(match);
        }}
        onClose={() => setShowBuildingConfirm(false)}
        onSaveAnyway={() => {
          buildingAcceptedForRef.current = twinKey(form.address, form.suburb);
          setShowBuildingConfirm(false);
          handleSubmit({ preventDefault: () => {} });
        }}
      />
    </div >
  );

  const lightboxModal = lightboxImage ? (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80"
      onClick={() => setLightboxImage(null)}
    >
      <button
        type="button"
        className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/80 transition-colors"
        onClick={() => setLightboxImage(null)}
      >
        <X className="w-6 h-6" />
      </button>
      <div
        className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {lightboxImage.isVideo ? (
          <video
            src={lightboxImage.src}
            controls
            autoPlay
            className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
          />
        ) : (
          <img
            src={lightboxImage.src}
            alt="Preview"
            className="max-w-full max-h-[85vh] rounded-lg shadow-2xl object-contain"
          />
        )}
      </div>
    </div>
  ) : null;

  if (isModal) {
    return (
      <>
        <div className="fixed -inset-6 bg-black bg-opacity-50 flex items-center justify-center z-50">
          {content}
        </div>
        {lightboxModal}
      </>
    );
  }
  return <>{content}{lightboxModal}</>;
}

export default function AddProperty({ isEdit = false }) {
  const { requestReason: requestPriceReason, reasonModal: priceReasonModal } = usePriceChangeReason();
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  // The Database's "Convert to appraisal" navigates here with the property
  // pre-filled. When it does, the listing type is already known, so the
  // "Appraisal / Off-Market / On-Market" pop-up is skipped and the agent lands
  // straight on the form with the address filled in (Christian Gravias, 2026-08-03).
  const handoff = location.state || {};
  const prefill = handoff.prefill || null;
  const handoffListingType = handoff.listingType || null;
  const prospectRecordId = handoff.prospectRecordId || null;
  // Converting out of a Database shared with the agent READ only still creates
  // the appraisal - it just leaves the source row in the owner's grid, because
  // retiring it would be a change to their Database. The profile page tells us
  // which of the two this is so the confirmation doesn't claim a row left a
  // grid it is still sitting in. Defaults to the old behaviour for every other
  // caller (the grid, the drawer), which only ever converts its own rows.
  const prospectRetiresRecord = handoff.retiresRecord !== false;
  const prospectSourceOwner = handoff.sourceOwnerName || "";
  const sellerLeadId = handoff.sellerLeadId || null;

  const [propertyData, setPropertyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(!isEdit && !handoffListingType);
  const [selectedListingType, setSelectedListingType] = useState(handoffListingType);

  // Fetch property data if in edit mode
  useEffect(() => {
    if (isEdit && id) {
      setLoading(true);
      getProperty(id)
        .then((response) => {
          setPropertyData(response.data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Failed to fetch property:", error);
          toast.error("Failed to load property data");
          setLoading(false);
          navigate("/properties");
        });
    }
  }, [isEdit, id, navigate]);

  const handleListingTypeSelect = (type) => {
    setSelectedListingType(type);
    setShowTypeModal(false);
  };

  // Retire the source Database record once its appraisal has actually been saved.
  // Best-effort: the appraisal is already created, so a failure here must not look
  // like the save failed. The record simply stays in the grid.
  const handleProspectHandoff = async (newPropertyId) => {
    if (!prospectRecordId) return;
    try {
      const res = await convertProspect(
        prospectRecordId,
        newPropertyId ? { property_id: newPropertyId } : {}
      );
      // The server is the authority on whether the row was retired: an agent
      // with read-only access to that Database gets the appraisal and the row
      // stays put.
      const retired = res?.data?.record_retired !== false && prospectRetiresRecord;
      toast.success(
        retired
          ? "Appraisal created. The contact has left the Database grid."
          : `Appraisal created. The Database row stays with ${prospectSourceOwner || "its owner"}.`
      );
    } catch (e) {
      toast.error("Appraisal saved, but the Database record couldn't be updated. Convert it again from the Database.");
    }
  };

  // Mark the source Seller Lead as actioned only once its appraisal has actually
  // been saved - not when the agent clicked "Add as Appraisal" on the Seller
  // Leads page. Marking it up front made the lead vanish from the holding area
  // even when the agent left this form without submitting, with no appraisal to
  // show for it (Christian Gravias, Monday 12887036288, 2026-08-25). Best-effort,
  // same reasoning as handleProspectHandoff: the appraisal is already created, so
  // a failure here must not look like the save failed.
  const handleSellerLeadHandoff = async (newPropertyId) => {
    if (!sellerLeadId) return;
    try {
      // The new appraisal's id lets the lead's notes follow it (CRM-2026-00090).
      await setSellerLeadAction(sellerLeadId, "appraisal", undefined, undefined, newPropertyId || undefined);
    } catch (e) {
      toast.error("Appraisal saved, but the lead couldn't be marked actioned. Mark it from Seller Leads.");
    }
  };

  const getInitialStatusForListingType = (type) => {
    if (type === 'appraisal') return 'Appraisal';
    if (type === 'offmarket') return 'Off Market Listing';
    if (type === 'onmarket') return 'On Market Listing';
    return 'On Market Listing';
  };

  // Handle update submission
  const handleUpdate = async (propertyData, formData) => {
    try {
      try {
        await updateProperty(id, formData);
      } catch (err) {
        // A live listing's price change needs a reason (compliance price history).
        if (!isPriceReasonRequired(err)) throw err;
        const extra = await requestPriceReason();
        if (!extra) return;
        await updateProperty(id, appendPriceReason(formData, extra));
      }
      toast.success("Property updated successfully!");
      invalidateCache.properties();
      navigate("/properties");
    } catch (error) {
      console.error("Failed to update property:", error);
      toast.error(error?.response?.data?.detail || "Failed to update property");
      throw error;
    }
  };

  if (isEdit && loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55C5D0] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading property data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {priceReasonModal}
      <PropertyTypeModal
        isOpen={showTypeModal}
        onClose={() => navigate('/properties')}
        onSelectType={handleListingTypeSelect}
      />

      {(!showTypeModal || isEdit) && (
        <AddPropertyForm
          isAdmin={isAdmin}
          isEdit={isEdit}
          listingType={isEdit ? (() => {
            const s = propertyData?.status?.name || propertyData?.status || '';
            if (s === 'Appraisal') return 'appraisal';
            if (s === 'Off Market Listing') return 'offmarket';
            return 'onmarket';
          })() : selectedListingType}
          prefill={isEdit ? null : prefill}
          initialValues={
            isEdit
              ? propertyData
              : selectedListingType != null
                ? { ...initialState, status: getInitialStatusForListingType(selectedListingType) }
                : initialState
          }
          onSubmit={isEdit ? handleUpdate : undefined}
          sellerLeadId={sellerLeadId}
          isSuccess={(newPropertyId, meta) => {
            handleProspectHandoff(newPropertyId);
            handleSellerLeadHandoff(newPropertyId);
            invalidateCache.properties();
            // Merged into the profile already there: open it, it is the one profile.
            navigate(meta?.merged ? `/properties/${newPropertyId}` : "/properties");
          }}
          onClose={() => {
            invalidateCache.properties();
            navigate("/properties");
          }}
        />
      )}

    </>
  );
}
